import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { caseApi, formsApi, pricingApi, tsfadApi } from '../services/api';
import { Case, CaseMedia, TSFADAnalysisRecord } from '../types';
import { getBaseUrl } from '../services/baseUrl';
import { waitForHealth } from '../services/waitForHealth';
import { getAuthToken } from '../services/auth';
import {
  TSFADExplanationModal,
  TSFADFeedbackModal,
  TSFADKnowledgeCoverageModal,
  TSFADQuoteDeleteModal,
  TSFADQuoteInactivateModal,
} from './TSFADAnalysisAuxModals';
import { TSFADQuoteModal } from './TSFADAnalysisQuoteModal';
import { TSFADSuggestionModal } from './TSFADAnalysisSuggestionModal';
import {
  TSFADMissedCausesModal,
  type MissedCauseBatchResult,
  type MissedCauseCheckResult
} from './TSFADMissedCausesModal';
import { TSFADTestModal } from './TSFADAnalysisTestModal';
import type {
  AnalysisStageId,
  DocumentationFeedback,
  ImplementedKitSelection,
  PlanAssistantMessage,
  PlanStepOption,
  PossibleCause,
  ReferenceImageBlockSection,
  ReferenceImageHit,
  QuoteCatalogPartItem,
  QuoteLineItemDraft,
  QuoteTemplateInfoBlock,
  QuoteTemplateLayout,
  SavedTSFADQuote,
  SourceSnippet,
  StageStateMap,
  StageStatus,
  StepNode,
  TestStep,
  TSFADDraftPayload,
} from './TSFADAnalysis.shared';
import {
  buildQuoteDevicePartCatalog,
  cleanQuotePartDescription,
  clearTSFADDraft,
  computeQuoteTotals,
  createFeedbackDefaults,
  createInitialStageState,
  createQuoteLineId,
  extractQuoteLookupTokens,
  formatDateTime,
  formatDuration,
  getCaseDisplayNumber,
  isLaborCatalogEntry,
  loadTSFADDraft,
  normaliseLookupToken,
  normalizeQuoteFieldText,
  normalizeQuoteTemplateLayout,
  readPathValue,
  saveTSFADDraft,
  STAGE_INDEX_MAP,
  STAGE_MAP,
  STAGE_SEQUENCE,
  TSFAD_DRAFT_VERSION,
} from './TSFADAnalysis.shared';

const formatKnowledgePathLabel = (value?: string | null): string =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const formatTsfadApiFailure = (failure: any, fallback: string): string => {
  const code = String(failure?.code || '').trim();
  if (
    code === 'TSFAD_ENRICHMENT_EVIDENCE_SELECTION_INVALID_NODE_IDS' ||
    code === 'TSFAD_ENRICHMENT_OUTPUT_INVALID_EVIDENCE_REFS' ||
    code === 'TSFAD_MANUAL_CAUSE_INVALID_NODE_IDS'
  ) {
    return 'The action-plan evidence could not be validated. This cause was not added; please retry it.';
  }
  if (typeof failure?.details === 'string' && failure.details.trim()) {
    return failure.details.trim();
  }
  if (typeof failure?.details?.message === 'string' && failure.details.message.trim()) {
    return failure.details.message.trim();
  }
  if (typeof failure?.error === 'string' && failure.error.trim()) {
    return failure.error.trim();
  }
  return fallback;
};

const resolveMediaCategory = (media: Pick<CaseMedia, 'mimeType' | 'fileType'>): 'image' | 'video' | 'audio' | 'file' => {
  const mime = String(media?.mimeType || media?.fileType || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.includes('image')) return 'image';
  if (mime.includes('video')) return 'video';
  if (mime.includes('audio')) return 'audio';
  return 'file';
};

const formatMediaFileSize = (fileSize?: number): string => {
  const size = Number(fileSize || 0);
  if (!Number.isFinite(size) || size <= 0) {
    return 'Unknown size';
  }
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(size / 1024).toFixed(1)} KB`;
};

const BRAND_TEXT = '#123F43';
const BRAND_MUTED = '#64716F';
const BRAND_SURFACE = '#F2EEE7';
const BRAND_SURFACE_SOFT = '#FBF8F2';
const BRAND_BORDER = '#DDD4C8';
const BRAND_CARD = '#FFFDF9';
const BRAND_TEXT_SOFT = 'rgba(18, 63, 67, 0.08)';
const BRAND_TEXT_SOFT_BORDER = 'rgba(18, 63, 67, 0.18)';
const BRAND_ACCENT = '#D99A33';
const BRAND_ACCENT_SOFT = '#F6E7C9';
const BRAND_ACCENT_TEXT = '#8A611E';
const BRAND_SUCCESS_SOFT = '#E7F2EC';
const BRAND_SUCCESS_BORDER = '#BDD4C7';
const BRAND_SUCCESS_TEXT = '#25624D';
const BRAND_DANGER_SOFT = '#FDEDEC';
const BRAND_DANGER_BORDER = '#E7B9B4';
const BRAND_DANGER_TEXT = '#A63B32';
const QUOTE_HEADER_SLOTS = [
  { id: 'h1c1', row: 1, col: 1 },
  { id: 'h1c2', row: 1, col: 2 },
  { id: 'h1c3', row: 1, col: 3 },
  { id: 'h2c1', row: 2, col: 1 },
  { id: 'h2c2', row: 2, col: 2 },
  { id: 'h2c3', row: 2, col: 3 }
] as const;
const QUOTE_HEADER_SLOT_IDS = QUOTE_HEADER_SLOTS.map(slot => slot.id);
const getQuoteHeaderSlotAlign = (slotId: string): 'left' | 'center' | 'right' => {
  const normalized = String(slotId || '').toLowerCase().replace(/[^a-z0-9]/g, '').trim();
  if (normalized.endsWith('c3')) return 'right';
  if (normalized.endsWith('c2')) return 'center';
  return 'left';
};
const getQuoteHeaderSlotMeta = (
  slotId: string
): { row: number; col: number } => {
  const normalizedSlotId = String(slotId || '').toLowerCase().replace(/[^a-z0-9]/g, '').trim();
  const slot = QUOTE_HEADER_SLOTS.find(item => item.id === normalizedSlotId);
  return slot ? { row: slot.row, col: slot.col } : { row: 1, col: 1 };
};

export default function TSFADAnalysis() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(true);
  const [analysisStarted, setAnalysisStarted] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [modelId, setModelId] = useState<string | null>(null);
  const [apiBase, setApiBase] = useState<string>('');
  const [problemDescription, setProblemDescription] = useState('');
  const [aiModel, setAiModel] = useState<string | null>(null);
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [possibleCauses, setPossibleCauses] = useState<PossibleCause[]>([]);
  const [testSteps, setTestSteps] = useState<TestStep[]>([]);
  const [selectedCause, setSelectedCause] = useState<PossibleCause | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testNotes, setTestNotes] = useState('');
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStatus, setAnalysisStatus] = useState<'NONE' | 'COMPLETED' | 'ACCEPTED'>('NONE');
  const [latestAnalysis, setLatestAnalysis] = useState<TSFADAnalysisRecord | null>(null);
  const [acceptingAnalysis, setAcceptingAnalysis] = useState(false);
  const [stageStates, setStageStates] = useState<StageStateMap>(() => createInitialStageState());
  const [activeStageMessage, setActiveStageMessage] = useState<string>('Preparing analysis...');
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [showAssignmentBlockedModal, setShowAssignmentBlockedModal] = useState(false);
  const [assignmentBlockedTitle, setAssignmentBlockedTitle] = useState<string>('TSFAD Run Blocked');
  const [assignmentBlockedLead, setAssignmentBlockedLead] = useState<string>('');
  const [assignmentBlockedDetails, setAssignmentBlockedDetails] = useState<string>('');
  const [showAnalyzeRetryModal, setShowAnalyzeRetryModal] = useState(false);
  const [analyzeRetryFailureCode, setAnalyzeRetryFailureCode] = useState<string | null>(null);
  const [analyzeRetryOccurredAt, setAnalyzeRetryOccurredAt] = useState<string | null>(null);
  const [analyzeRetryDetails, setAnalyzeRetryDetails] = useState<string | null>(null);
  const [citationIntegrityReview, setCitationIntegrityReview] = useState<any | null>(null);
  const [showCitationIntegrityModal, setShowCitationIntegrityModal] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestionResult, setSuggestionResult] = useState<any>(null);
  const [showMissedCausesModal, setShowMissedCausesModal] = useState(false);
  const [missedCausesLoading, setMissedCausesLoading] = useState(false);
  const [missedCausesResult, setMissedCausesResult] = useState<MissedCauseCheckResult | null>(null);
  const [missedCausesError, setMissedCausesError] = useState<string | null>(null);
  const [missedCausesWorkflowError, setMissedCausesWorkflowError] = useState<string | null>(null);
  const [missedCausesOptimizationComplete, setMissedCausesOptimizationComplete] = useState(false);
  const [selectedMissedCauseIds, setSelectedMissedCauseIds] = useState<string[]>([]);
  const [missedCausesProcessing, setMissedCausesProcessing] = useState(false);
  const [missedCausesOptimizing, setMissedCausesOptimizing] = useState(false);
  const [processingMissedCauseId, setProcessingMissedCauseId] = useState<string | null>(null);
  const [missedCauseBatchResults, setMissedCauseBatchResults] = useState<MissedCauseBatchResult[]>([]);
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [explanationLoading] = useState(false);
  const [explanationResult, setExplanationResult] = useState<any>(null);
  const [selectedCauseForExplanation, setSelectedCauseForExplanation] = useState<PossibleCause | null>(null);
  const [causeDetailLoadingId, setCauseDetailLoadingId] = useState<string | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showKnowledgeCoverageModal, setShowKnowledgeCoverageModal] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState<DocumentationFeedback>(() => createFeedbackDefaults());
  const [feedbackCause, setFeedbackCause] = useState<PossibleCause | null>(null);
  const [feedbackSuggestionDescription, setFeedbackSuggestionDescription] = useState<string | null>(null);
  const [feedbackMetadata, setFeedbackMetadata] = useState<Record<string, unknown> | null>(null);
  const [analysisFailureCode, setAnalysisFailureCode] = useState<string | null>(null);
  const [analysisFailureFeedbackContext, setAnalysisFailureFeedbackContext] = useState<Record<string, unknown> | null>(null);
  const [knowledgeCoverageRetrySubmitting, setKnowledgeCoverageRetrySubmitting] = useState(false);
  const [knowledgeCoverageActionError, setKnowledgeCoverageActionError] = useState<string | null>(null);
  const [isEditingProcedure, setIsEditingProcedure] = useState(false);
  const [procedureDraft, setProcedureDraft] = useState('');
  const [procedureEditNotes, setProcedureEditNotes] = useState('');
  const [isInactivating, setIsInactivating] = useState(false);
  const [inactivationReason, setInactivationReason] = useState('');
  const [inactivePanelOpen, setInactivePanelOpen] = useState(false);
  const [repairDraft, setRepairDraft] = useState('');
  const [toolsDraft, setToolsDraft] = useState('');
  const [partsDraft, setPartsDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [restoringCause, setRestoringCause] = useState(false);
  const [regeneratingCause, setRegeneratingCause] = useState(false);
  const [draftRestoredAt, setDraftRestoredAt] = useState<string | null>(null);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteSaving, setQuoteSaving] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoteTemplateId, setQuoteTemplateId] = useState<string | null>(null);
  const [quoteTemplateName, setQuoteTemplateName] = useState<string | null>(null);
  const [quoteTemplateLayout, setQuoteTemplateLayout] = useState<QuoteTemplateLayout | null>(null);
  const [quoteTitle, setQuoteTitle] = useState('');
  const [quoteDescription, setQuoteDescription] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteLineItems, setQuoteLineItems] = useState<QuoteLineItemDraft[]>([]);
  const [implementedKitSelections, setImplementedKitSelections] = useState<ImplementedKitSelection[]>([]);
  const [quotePartCatalog, setQuotePartCatalog] = useState<QuoteCatalogPartItem[]>([]);
  const [quoteUnmatchedParts, setQuoteUnmatchedParts] = useState<string[]>([]);
  const [quoteEditingQuoteId, setQuoteEditingQuoteId] = useState<string | null>(null);
  const [quoteRevisionOfQuoteId, setQuoteRevisionOfQuoteId] = useState<string | null>(null);
  const [selectedQuoteLineIds, setSelectedQuoteLineIds] = useState<string[]>([]);
  const [quoteCustomerName, setQuoteCustomerName] = useState('');
  const [quoteCustomerSignature, setQuoteCustomerSignature] = useState('');
  const [savedQuotes, setSavedQuotes] = useState<SavedTSFADQuote[]>([]);
  const [savedQuotesLoaded, setSavedQuotesLoaded] = useState(false);
  const [savedQuotesLoading, setSavedQuotesLoading] = useState(false);
  const [savedQuotesLoadFailed, setSavedQuotesLoadFailed] = useState(false);
  const [quoteSendingId, setQuoteSendingId] = useState<string | null>(null);
  const [quoteStateChangeId, setQuoteStateChangeId] = useState<string | null>(null);
  const [quoteDeleteId, setQuoteDeleteId] = useState<string | null>(null);
  const [quoteDeleteTarget, setQuoteDeleteTarget] = useState<SavedTSFADQuote | null>(null);
  const [quoteDeleteError, setQuoteDeleteError] = useState<string | null>(null);
  const [quoteInactivateTarget, setQuoteInactivateTarget] = useState<SavedTSFADQuote | null>(null);
  const [quoteInactivationReason, setQuoteInactivationReason] = useState('');
  const [quoteInactivationError, setQuoteInactivationError] = useState<string | null>(null);
  const [planAssistantOpen, setPlanAssistantOpen] = useState(false);
  const [planAssistantQuestion, setPlanAssistantQuestion] = useState('');
  const [planAssistantMessages, setPlanAssistantMessages] = useState<PlanAssistantMessage[]>([]);
  const [planAssistantLoading, setPlanAssistantLoading] = useState(false);
  const [planAssistantActiveAsk, setPlanAssistantActiveAsk] = useState<'quick' | 'custom' | null>(null);
  const [planAssistantError, setPlanAssistantError] = useState<string | null>(null);
  const [planAssistantConversationId, setPlanAssistantConversationId] = useState<string | null>(null);
  const [selectedPlanStepKey, setSelectedPlanStepKey] = useState('');
  const [autoStartRequested, setAutoStartRequested] = useState<boolean>(
    () => Boolean((location.state as any)?.tsfadAutoStart)
  );
  const progressEventSourceRef = useRef<EventSource | null>(null);
  const originalCausesRef = useRef<Map<string, PossibleCause>>(new Map());
  const implementedKitsHydratedAnalysisIdRef = useRef<string | null>(null);
  const caseDisplayNumber = getCaseDisplayNumber(caseData);
  const inactivationPanelRef = useRef<HTMLDivElement | null>(null);
  const inactivationTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const prevSelectedCauseRef = useRef<PossibleCause | null>(null);
  const isEditingProcedureRef = useRef(false);
  const isInactivatingRef = useRef(false);
  const draftRestoreAppliedRef = useRef(false);
  const draftSaveTimeoutRef = useRef<number | null>(null);
  const ensureAbsoluteUrl = useCallback(
    (url?: string | null) => {
      if (!url) return '';
      if (/^(?:https?:|data:|blob:)/i.test(url)) return url;
      if (!apiBase) return url;
      return `${apiBase.replace(/\/+$/, '')}${url.startsWith('/') ? url : `/${url}`}`;
    },
    [apiBase]
  );
const isAnalysisAccepted = analysisStatus === 'ACCEPTED';
const analysisLocked = false;
const acceptedDisplayTimestamp =
    isAnalysisAccepted && latestAnalysis
      ? formatDateTime(latestAnalysis.acceptedAt || latestAnalysis.updatedAt || latestAnalysis.createdAt)
      : null;
const acceptedByLabel = isAnalysisAccepted ? latestAnalysis?.acceptedBy || null : null;
const acceptedMetaDescription = isAnalysisAccepted
    ? `${acceptedDisplayTimestamp ? `Saved ${acceptedDisplayTimestamp}` : 'Analysis accepted'}${
        acceptedByLabel ? ` by ${acceptedByLabel}` : ''
      }`
    : '';
const primaryContact =
    caseData?.customer?.contacts?.find?.((entry: any) => entry?.isPrimary) ||
    caseData?.customer?.contacts?.[0] ||
    null;
const caseHeaderProblemText = String(problemDescription || '').trim();
const caseHeaderProblemSummary =
    caseHeaderProblemText.split(/\bAdditional Details:\b/i)[0].trim() ||
    caseHeaderProblemText ||
    'No problem description provided.';
  useEffect(() => {
    let cancelled = false;
    getBaseUrl()
      .then(base => {
        if (!cancelled) {
          setApiBase(base);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
const caseHeaderCustomerName =
    caseData?.customer?.companyName ||
    caseData?.customer?.company ||
    'Unknown customer';
const caseHeaderDeviceModel = caseData?.device?.modelNumber || 'Unspecified device';
const caseHeaderDeviceSerialNumber = String(caseData?.device?.serialNumber || '').trim();
const caseHeaderDeviceAssetNumber = String(caseData?.device?.assetNumber || '').trim();
const caseHeaderDeviceLocationName = String(
    caseData?.device?.location?.name ||
    (caseData?.device as any)?.locationName ||
    (caseData?.device as any)?.customerLocationName ||
    ''
  ).trim();
const caseHeaderLocationName =
    caseHeaderDeviceLocationName ||
    primaryContact?.location?.name ||
    'Unspecified location';
const caseHeaderContactName =
    primaryContact?.fullName ||
    caseData?.contactEmail ||
    caseData?.contactPhone ||
    'Not provided';
const mediaItems: CaseMedia[] = Array.isArray(caseData?.media) ? caseData?.media ?? [] : [];
const mediaBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const resolveCaseMediaUrl = (media: CaseMedia): string =>
    media.fileUrl && /^https?:\/\//i.test(media.fileUrl)
      ? media.fileUrl
      : `${mediaBaseUrl}${media.fileUrl || ''}`;
const saveAnalysisDisabled = acceptingAnalysis || !analysisId || analysisStatus === 'ACCEPTED';
const saveAnalysisMetaLabel = acceptingAnalysis
    ? 'Saving...'
    : analysisStatus === 'ACCEPTED'
    ? acceptedDisplayTimestamp
      ? `Last saved ${acceptedDisplayTimestamp}`
      : 'Saved'
    : analysisId
    ? 'Unsaved changes'
    : 'Not saved yet';
const quoteAnalysisId =
    analysisStatus === 'ACCEPTED' && latestAnalysis && !latestAnalysis.isWorkspace
      ? latestAnalysis.id
      : null;
const heroActionLabel =
    analysisStarted && !analysisComplete
      ? 'Running TSFAD...'
      : analysisComplete && !analysisStarted
      ? 'Re-run TSFAD'
      : 'Start TSFAD';
const markAnalysisDirty = useCallback(() => {
  setAnalysisStatus('COMPLETED');
  setLatestAnalysis((prev: TSFADAnalysisRecord | null) =>
    prev
      ? { ...prev, analysisStatus: 'COMPLETED', acceptedAt: null, acceptedBy: null }
      : prev
  );
}, []);

  const buildDraftPayload = useCallback((): TSFADDraftPayload | null => {
    if (!caseData?.id || !analysisId || possibleCauses.length === 0) {
      return null;
    }
    return {
      version: TSFAD_DRAFT_VERSION,
      caseId: caseData.id,
      analysisId,
      savedAt: new Date().toISOString(),
      possibleCauses,
      testSteps,
      implementedKitSelections,
      selectedCauseId: selectedCause?.id || null,
      analysisStatus
    };
  }, [analysisId, analysisStatus, caseData, implementedKitSelections, possibleCauses, selectedCause, testSteps]);

  const loadSavedQuotes = useCallback(async () => {
    if (!quoteAnalysisId) return;
    try {
      setSavedQuotesLoading(true);
      setSavedQuotesLoadFailed(false);
      const payload = await tsfadApi.listQuotes(quoteAnalysisId);
      setSavedQuotes(Array.isArray(payload?.quotes) ? payload.quotes : []);
    } catch (error) {
      console.error('Failed to load saved TSFAD quotes', error);
      setSavedQuotesLoadFailed(true);
    } finally {
      setSavedQuotesLoaded(true);
      setSavedQuotesLoading(false);
    }
  }, [quoteAnalysisId]);

  const estimatedMinutesForQuote = useCallback(() => {
    return possibleCauses.reduce((sum, cause) => {
      if (cause.status === 'INACTIVE') return sum;
      const value = Number(cause.estimatedTime);
      return sum + (Number.isFinite(value) && value > 0 ? value : 0);
    }, 0);
  }, [possibleCauses]);

  const normaliseQuoteStatusValue = useCallback((value?: string | null) => {
    return String(value || '').trim().toUpperCase();
  }, []);

  const buildDefaultQuoteTitle = useCallback(() => {
    const now = new Date();
    const dateToken = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(
      now.getDate()
    ).padStart(2, '0')}`;
    const caseTokenRaw =
      String(caseData?.caseCode || caseData?.caseNumber || caseData?.id || '')
        .trim()
        .toUpperCase() || 'CASE';
    return `Case ${caseTokenRaw} Quote ${dateToken}`;
  }, [caseData?.caseCode, caseData?.caseNumber, caseData?.id]);

  const quoteSlaCoverage = useMemo<any | null>(() => {
    const latestCoverage =
      latestAnalysis?.caseModel && typeof latestAnalysis.caseModel === 'object'
        ? (latestAnalysis.caseModel as Record<string, any>).slaCoverage
        : null;
    const fallbackCoverage =
      caseData?.tsfadAnalyses?.[0]?.caseModel &&
      typeof caseData.tsfadAnalyses[0].caseModel === 'object'
        ? (caseData.tsfadAnalyses[0].caseModel as Record<string, any>).slaCoverage
        : null;
    const candidate = latestCoverage || fallbackCoverage;
    return candidate && typeof candidate === 'object' ? candidate : null;
  }, [caseData?.tsfadAnalyses, latestAnalysis]);

  const quoteSlaPartCoverageLookup = useMemo(() => {
    const byRaw = new Map<string, any>();
    const byPartNumber = new Map<string, any>();
    const byNormalized = new Map<string, any>();
    const entries = Array.isArray(quoteSlaCoverage?.resources?.parts)
      ? quoteSlaCoverage.resources.parts
      : [];
    entries.forEach((entry: any) => {
      const rawItem = String(entry?.item || '').trim();
      if (rawItem && !byRaw.has(rawItem)) {
        byRaw.set(rawItem, entry);
      }
      const partNumber = normaliseLookupToken(String(entry?.partNumber || ''));
      if (partNumber && !byPartNumber.has(partNumber)) {
        byPartNumber.set(partNumber, entry);
      }
      const normalized = String(entry?.normalizedKey || '').trim();
      if (normalized && !byNormalized.has(normalized)) {
        byNormalized.set(normalized, entry);
      }
    });
    return { byRaw, byPartNumber, byNormalized };
  }, [quoteSlaCoverage]);

  const clearQuoteSlaFields = useCallback((line: QuoteLineItemDraft): QuoteLineItemDraft => {
    return {
      ...line,
      isFree: false,
      originalUnitValue: null,
      slaCoverageStatus: null,
      slaLabel: null,
      slaDiscountPercent: null,
      slaCoverageReason: null,
      slaMatchedListNames: []
    };
  }, []);

  const findQuoteSlaPartCoverage = useCallback(
    (rawItem: string, partNumber?: string | null) => {
      const rawKey = String(rawItem || '').trim();
      if (rawKey && quoteSlaPartCoverageLookup.byRaw.has(rawKey)) {
        return quoteSlaPartCoverageLookup.byRaw.get(rawKey) || null;
      }
      const partKey = normaliseLookupToken(String(partNumber || ''));
      if (partKey && quoteSlaPartCoverageLookup.byPartNumber.has(partKey)) {
        return quoteSlaPartCoverageLookup.byPartNumber.get(partKey) || null;
      }
      const normalizedCandidates = Array.from(
        new Set([
          ...extractQuoteLookupTokens(rawItem),
          partKey
        ].filter(Boolean))
      );
      for (const candidate of normalizedCandidates) {
        if (quoteSlaPartCoverageLookup.byNormalized.has(candidate)) {
          return quoteSlaPartCoverageLookup.byNormalized.get(candidate) || null;
        }
      }
      return null;
    },
    [quoteSlaPartCoverageLookup]
  );

  const applySlaCoverageToQuotePartLine = useCallback(
    (line: QuoteLineItemDraft, coverage: any): QuoteLineItemDraft => {
      const baseUnitValue = Number.isFinite(Number(line.originalUnitValue))
        ? Math.max(0, Number(line.originalUnitValue))
        : Math.max(0, Number(line.unitValue || 0));
      const rules =
        quoteSlaCoverage?.rules && typeof quoteSlaCoverage.rules === 'object'
          ? quoteSlaCoverage.rules
          : {};
      const fallbackDiscountPercent = Number(rules?.discountParts?.discountPercent ?? 0);
      const hasFallbackAllPartsDiscount =
        Boolean(rules?.discountParts?.all) &&
        Number.isFinite(fallbackDiscountPercent) &&
        fallbackDiscountPercent > 0;

      if (!coverage || coverage.coverageStatus === 'NOT_COVERED') {
        if (!hasFallbackAllPartsDiscount) {
          return clearQuoteSlaFields(line);
        }
        const discountedUnitValue = Number(
          (baseUnitValue * (1 - fallbackDiscountPercent / 100)).toFixed(2)
        );
        return {
          ...line,
          unitValue: discountedUnitValue,
          isFree: false,
          originalUnitValue: baseUnitValue,
          slaCoverageStatus: 'DISCOUNTED',
          slaLabel: `SLA ${fallbackDiscountPercent}% discount`,
          slaDiscountPercent: fallbackDiscountPercent,
          slaCoverageReason: 'DISCOUNT_PARTS_ALL',
          slaMatchedListNames: []
        };
      }
      if (coverage.coverageStatus === 'COVERED') {
        return {
          ...line,
          unitValue: 0,
          isFree: true,
          originalUnitValue: baseUnitValue,
          slaCoverageStatus: 'COVERED',
          slaLabel: 'SLA Covered',
          slaDiscountPercent: null,
          slaCoverageReason: typeof coverage.coverageReason === 'string' ? coverage.coverageReason : null,
          slaMatchedListNames: Array.isArray(coverage.matchedListNames) ? coverage.matchedListNames : []
        };
      }
      const discountPercent = Number(coverage.discountPercent);
      const safeDiscount =
        Number.isFinite(discountPercent) && discountPercent > 0 ? discountPercent : null;
      const discountedUnitValue =
        safeDiscount === null
          ? baseUnitValue
          : Number((baseUnitValue * (1 - safeDiscount / 100)).toFixed(2));
      return {
        ...line,
        unitValue: discountedUnitValue,
        isFree: false,
        originalUnitValue: baseUnitValue,
        slaCoverageStatus: 'DISCOUNTED',
        slaLabel: safeDiscount === null ? 'SLA Discounted' : `SLA ${safeDiscount}% discount`,
        slaDiscountPercent: safeDiscount,
        slaCoverageReason: typeof coverage.coverageReason === 'string' ? coverage.coverageReason : null,
        slaMatchedListNames: Array.isArray(coverage.matchedListNames) ? coverage.matchedListNames : []
      };
    },
    [clearQuoteSlaFields, quoteSlaCoverage]
  );

  const buildQuoteLaborLines = useCallback(
    (baseLine: QuoteLineItemDraft, totalHours: number): QuoteLineItemDraft[] => {
      const safeHours = Number.isFinite(totalHours) ? Math.max(0, Number(totalHours)) : 0;
      const baseRate = Math.max(0, Number(baseLine.unitValue || 0));
      const description = String(baseLine.description || 'Service labor').trim() || 'Service labor';
      const rules = quoteSlaCoverage?.rules && typeof quoteSlaCoverage.rules === 'object'
        ? quoteSlaCoverage.rules
        : {};
      const makeLine = (
        idSuffix: string,
        quantity: number,
        lineUnitValue: number,
        overrides: Partial<QuoteLineItemDraft> = {}
      ): QuoteLineItemDraft => ({
        ...baseLine,
        id: createQuoteLineId(idSuffix, Math.round(quantity * 10) || 1),
        description,
        quantity: Number(quantity.toFixed(2)),
        unitValue: Number(lineUnitValue.toFixed(2)),
        isFree: false,
        originalUnitValue: null,
        slaCoverageStatus: null,
        slaLabel: null,
        slaDiscountPercent: null,
        slaCoverageReason: null,
        slaMatchedListNames: [],
        ...overrides
      });

      const lines: QuoteLineItemDraft[] = [];
      let remainingHours = safeHours;

      if (rules?.labour?.unlimited && remainingHours > 0) {
        lines.push(
          makeLine('labor-sla-covered', remainingHours, 0, {
            isFree: true,
            originalUnitValue: baseRate,
            slaCoverageStatus: 'COVERED',
            slaLabel: 'SLA Labor Included',
            slaCoverageReason: 'LABOUR_INCLUDED'
          })
        );
        remainingHours = 0;
      } else {
        const coveredHours = Number(rules?.labour?.limitedHours || 0);
        if (coveredHours > 0 && remainingHours > 0) {
          const appliedCoveredHours = Math.min(remainingHours, coveredHours);
          lines.push(
            makeLine('labor-sla-covered', appliedCoveredHours, 0, {
              isFree: true,
              originalUnitValue: baseRate,
              slaCoverageStatus: 'COVERED',
              slaLabel: 'SLA Labor Included',
              slaCoverageReason: 'LABOUR_INCLUDED'
            })
          );
          remainingHours = Number((remainingHours - appliedCoveredHours).toFixed(2));
        }
      }

      const labourDiscount = Number(rules?.discountedLabour?.discountPercent || 0);
      const hasLabourDiscount = Number.isFinite(labourDiscount) && labourDiscount > 0;
      if (remainingHours > 0 && hasLabourDiscount) {
        const discountHoursLimit = Number(rules?.discountedLabour?.limitedHours || 0);
        const discountedHours =
          discountHoursLimit > 0 ? Math.min(remainingHours, discountHoursLimit) : remainingHours;
        if (discountedHours > 0) {
          lines.push(
            makeLine(
              'labor-sla-discount',
              discountedHours,
              baseRate * (1 - labourDiscount / 100),
              {
                originalUnitValue: baseRate,
                slaCoverageStatus: 'DISCOUNTED',
                slaLabel: `SLA Labor ${labourDiscount}% discount`,
                slaDiscountPercent: labourDiscount,
                slaCoverageReason: 'LABOUR_DISCOUNT'
              }
            )
          );
          remainingHours = Number((remainingHours - discountedHours).toFixed(2));
        }
      }

      if (remainingHours > 0 || lines.length === 0) {
        lines.push(
          makeLine('labor', remainingHours > 0 ? remainingHours : safeHours, baseRate)
        );
      }

      return lines.filter(line => Number(line.quantity || 0) > 0);
    },
    [quoteSlaCoverage]
  );

  const openQuoteFromTSFAD = useCallback(async () => {
    if (!quoteAnalysisId) return;
    try {
      setQuoteLoading(true);
      setQuoteError(null);

      const [formsPayload, pricingPayload, failureRatesPayload] = await Promise.all([
        formsApi.getAll(),
        pricingApi.getAll(),
        pricingApi.getFailureRates().catch(() => ({ items: [] }))
      ]);
      const templates = Array.isArray(formsPayload?.quotes) ? formsPayload.quotes : [];
      const activeTemplate =
        templates.length === 1
          ? templates[0]
          : templates.find((template: any) => template?.isPrimary) || null;

      if (!activeTemplate) {
        throw new Error(
          'No quotation template is available. An admin must configure a primary quote form template.'
        );
      }
      const normalizedTemplate = normalizeQuoteTemplateLayout(activeTemplate);

      const pricingItems = Array.isArray(pricingPayload?.items) ? pricingPayload.items : [];
      const failureRateItems = Array.isArray(failureRatesPayload?.items) ? failureRatesPayload.items : [];
      const quoteDevicePartCatalog = buildQuoteDevicePartCatalog(
        pricingItems,
        failureRateItems,
        caseData?.device?.modelNumber || ''
      );
      setQuotePartCatalog(quoteDevicePartCatalog);

      const partPricingItems = pricingItems
        .filter((item: any) => item?.isActive !== false)
        .filter((item: any) => !isLaborCatalogEntry(item));
      const pricingByPartNumber = new Map<string, any>();
      const pricingBySku = new Map<string, any>();
      partPricingItems.forEach((item: any) => {
        const partKey = normaliseLookupToken(String(item?.partNumber || ''));
        if (partKey && !pricingByPartNumber.has(partKey)) pricingByPartNumber.set(partKey, item);
        const skuKey = normaliseLookupToken(String(item?.sku || ''));
        if (skuKey && !pricingBySku.has(skuKey)) pricingBySku.set(skuKey, item);
      });
      const allowAutoFill = normalizedTemplate.allowTsfadAutoFill;
      const templateLines: QuoteLineItemDraft[] = Array.isArray(activeTemplate?.lineItems)
        ? activeTemplate.lineItems.map((line: any, index: number) => ({
            id: createQuoteLineId('template-line', index),
            lineType: String(line?.lineType || 'part').toLowerCase() === 'labor' ? 'labor' : 'part',
            description: String(line?.description || ''),
            quantity: Number(line?.quantity || 1),
            unitValue: Number(line?.unitValue || 0),
            isFree: false,
            source: String(line?.source || 'manual'),
            catalogId: typeof line?.catalogId === 'string' ? line.catalogId : null,
            sku: typeof line?.sku === 'string' && line.sku.trim() ? line.sku.trim() : null,
            partNumber:
              typeof line?.partNumber === 'string' && line.partNumber.trim()
                ? line.partNumber.trim()
                : null,
            originalUnitValue: null,
            slaCoverageStatus: null,
            slaLabel: null,
            slaDiscountPercent: null,
            slaCoverageReason: null,
            slaMatchedListNames: []
          }))
        : [];

      let lineItems: QuoteLineItemDraft[] = [];
      const unmatchedParts: string[] = [];
      const quoteKitRecommendationsCandidate =
        latestAnalysis?.caseModel && typeof latestAnalysis.caseModel === 'object'
          ? (latestAnalysis.caseModel as Record<string, any>).kitRecommendations
          : caseData?.tsfadAnalyses?.[0]?.caseModel &&
            typeof caseData.tsfadAnalyses[0].caseModel === 'object'
          ? (caseData.tsfadAnalyses[0].caseModel as Record<string, any>).kitRecommendations
          : null;
      const quoteKitRecommendationMap = new Map<string, any>();
      const quoteKitRecommendations = Array.isArray(quoteKitRecommendationsCandidate?.recommendations)
        ? quoteKitRecommendationsCandidate.recommendations
        : [];
      quoteKitRecommendations.forEach((recommendation: any) => {
        const key = String(recommendation?.kitId || '').trim();
        if (!key || quoteKitRecommendationMap.has(key)) return;
        quoteKitRecommendationMap.set(key, recommendation);
      });
      const resolveKitDisplayLabelLocal = (value: Record<string, any> | null | undefined) => {
        const kitNumber = String(value?.kitNumber || '').trim();
        if (kitNumber) return kitNumber;
        const sku = String(value?.sku || '').trim();
        if (sku) return sku;
        const name = String(value?.name || '').trim();
        if (name) return name;
        return 'Kit';
      };
      const buildImplementedKitQuoteLineLocal = (
        selection: ImplementedKitSelection,
        index: number
      ): QuoteLineItemDraft => {
        const recommendation =
          quoteKitRecommendationMap.get(String(selection?.kitId || '').trim()) || null;
        const kitLabel = resolveKitDisplayLabelLocal(recommendation || selection);
        const kitDescription =
          String(recommendation?.name || selection?.name || '').trim() || `${kitLabel} kit`;
        const rawKitPrice = Number(recommendation?.rawKitPrice);
        const customerKitPrice = Number(recommendation?.kitPrice);
        const hasRawKitPrice = Number.isFinite(rawKitPrice) && rawKitPrice >= 0;
        const hasCustomerKitPrice = Number.isFinite(customerKitPrice) && customerKitPrice >= 0;
        const resolvedUnitValue = hasCustomerKitPrice
          ? customerKitPrice
          : hasRawKitPrice
          ? rawKitPrice
          : 0;
        const kitSlaStatus = String(recommendation?.kitSlaCoverageStatus || '').trim().toUpperCase();
        const kitDiscountPercent = Number(recommendation?.kitSlaDiscountPercent);
        const isCovered = kitSlaStatus === 'COVERED';
        const isDiscounted = kitSlaStatus === 'DISCOUNTED';
        const originalUnitValue =
          hasRawKitPrice && rawKitPrice > resolvedUnitValue + 0.0001 ? rawKitPrice : null;

        return {
          id: createQuoteLineId('kit', index),
          lineType: 'part',
          description: kitDescription,
          quantity: 1,
          unitValue: isCovered ? 0 : Number(resolvedUnitValue.toFixed(2)),
          isFree: isCovered,
          source: 'kit',
          catalogId: null,
          sku: String(recommendation?.sku || selection?.sku || '').trim() || null,
          partNumber: String(
            recommendation?.kitNumber ||
              selection?.kitNumber ||
              recommendation?.sku ||
              selection?.sku ||
              ''
          ).trim() || null,
          originalUnitValue,
          slaCoverageStatus: isCovered ? 'COVERED' : isDiscounted ? 'DISCOUNTED' : null,
          slaLabel: isCovered
            ? 'SLA Covered'
            : isDiscounted && Number.isFinite(kitDiscountPercent) && kitDiscountPercent > 0
            ? `SLA ${kitDiscountPercent}% discount`
            : null,
          slaDiscountPercent:
            isDiscounted && Number.isFinite(kitDiscountPercent) && kitDiscountPercent > 0
              ? kitDiscountPercent
              : null,
          slaCoverageReason:
            recommendation?.pricingMode === 'SLA' ? 'KIT_IMPLEMENTED' : null,
          slaMatchedListNames: []
        };
      };

      if (allowAutoFill) {
        const implementedPartKeySet = new Set(
          implementedKitSelections.flatMap(selection =>
            Array.isArray(selection?.matchedPartNumbers)
              ? selection.matchedPartNumbers
                  .map(partNumber => normaliseLookupToken(String(partNumber || '')))
                  .filter(Boolean)
              : []
          )
        );
        const partCandidates = Array.from(
          new Set(
            possibleCauses
              .filter(cause => cause.status !== 'INACTIVE')
              .flatMap(cause => (Array.isArray(cause.requiredParts) ? cause.requiredParts : []))
              .map(value => String(value || '').trim())
              .filter(Boolean)
          )
        ).filter(part => {
          const lookupTokens = extractQuoteLookupTokens(part);
          return !lookupTokens.some(token => implementedPartKeySet.has(token));
        });

        const implementedKitLines: QuoteLineItemDraft[] = implementedKitSelections.map((selection, index) => {
          const recommendation = quoteKitRecommendationMap.get(String(selection?.kitId || '').trim()) || null;
          const hasAnyKitPricing =
            Number.isFinite(Number(recommendation?.kitPrice)) ||
            Number.isFinite(Number(recommendation?.rawKitPrice));
          if (!hasAnyKitPricing) {
            unmatchedParts.push(resolveKitDisplayLabelLocal(recommendation || selection));
          }
          return buildImplementedKitQuoteLineLocal(selection, index);
        });

        const partLines: QuoteLineItemDraft[] = partCandidates.map((part, index) => {
          const lookupTokens = extractQuoteLookupTokens(part);
          const matchedToken =
            lookupTokens.find(token => pricingByPartNumber.has(token) || pricingBySku.has(token)) || '';
          const matched = matchedToken
            ? pricingByPartNumber.get(matchedToken) || pricingBySku.get(matchedToken) || null
            : null;
          const matchedPartNumber = String(matched?.partNumber || '').trim() || null;
          const matchedSku = String(matched?.sku || '').trim() || null;
          const linePartRef = matchedPartNumber || matchedSku || lookupTokens[0] || null;
          const matchedDescription = normalizeQuoteFieldText(
            String(matched?.description || matched?.name || '').trim()
          );
          const manualDescription = cleanQuotePartDescription(part, linePartRef);

          if (!matched) {
            unmatchedParts.push(manualDescription || String(part || '').trim());
          }

          const baseLine: QuoteLineItemDraft = {
            id: createQuoteLineId('part', index),
            lineType: 'part',
            description:
              matchedDescription ||
              manualDescription ||
              normalizeQuoteFieldText(String(part || '').trim()),
            quantity: 1,
            unitValue: matched ? Number(matched.unitValue || 0) : 0,
            isFree: false,
            source: matched ? 'catalog' : 'manual',
            catalogId: matched?.id || null,
            sku: matchedSku,
            partNumber: matchedPartNumber || (linePartRef ? String(linePartRef) : null),
            originalUnitValue: null,
            slaCoverageStatus: null,
            slaLabel: null,
            slaDiscountPercent: null,
            slaCoverageReason: null,
            slaMatchedListNames: []
          };
          const coverage = findQuoteSlaPartCoverage(part, baseLine.partNumber || null);
          return applySlaCoverageToQuotePartLine(baseLine, coverage);
        });

        const minutes = estimatedMinutesForQuote();
        const laborHours = Number(Math.max(0.5, minutes / 60).toFixed(1));
        const laborRateFromCatalog = pricingItems.find((item: any) =>
          String(item?.lineType || '').toLowerCase().includes('labor')
        );
        const laborLinePartNumber = String(laborRateFromCatalog?.partNumber || '').trim() || null;
        const laborLineSku = String(laborRateFromCatalog?.sku || '').trim() || null;
        const templateLaborLine = templateLines.find((line: QuoteLineItemDraft) => line.lineType === 'labor');
        const laborLine: QuoteLineItemDraft = {
          id: createQuoteLineId('labor', 0),
          lineType: 'labor',
          description:
            templateLaborLine?.description || 'On-site diagnostics, testing, and repair verification labor',
          quantity: laborHours,
          unitValue: Number(
            laborRateFromCatalog?.unitValue ?? templateLaborLine?.unitValue ?? 0
          ),
          isFree: false,
          source: laborRateFromCatalog ? 'catalog' : 'manual',
          catalogId: laborRateFromCatalog?.id || null,
          sku: laborLineSku,
          partNumber: laborLinePartNumber || templateLaborLine?.partNumber || null,
          originalUnitValue: null,
          slaCoverageStatus: null,
          slaLabel: null,
          slaDiscountPercent: null,
          slaCoverageReason: null,
          slaMatchedListNames: []
        };
        lineItems = [
          ...buildQuoteLaborLines(laborLine, laborHours),
          ...implementedKitLines,
          ...partLines
        ];
      } else {
        lineItems = templateLines.length > 0 ? templateLines : [
          {
            id: createQuoteLineId('line', 0),
            lineType: 'labor',
            description: 'Service labor',
            quantity: 1,
            unitValue: 0,
            isFree: false,
            source: 'manual',
            catalogId: null,
            sku: null,
            partNumber: null,
            originalUnitValue: null,
            slaCoverageStatus: null,
            slaLabel: null,
            slaDiscountPercent: null,
            slaCoverageReason: null,
            slaMatchedListNames: []
          }
        ];
      }
      setQuoteTemplateId(String(activeTemplate.id || ''));
      setQuoteTemplateName(activeTemplate.name || 'Quote');
      setQuoteTemplateLayout(normalizedTemplate);
      setQuoteTitle(buildDefaultQuoteTitle());
      const caseProblemText = String(
        caseData?.problemDescription || caseData?.problemDesc || problemDescription || ''
      ).trim();
      const defaultDescription = caseProblemText
        ? `Problem: ${caseProblemText}\n\n${normalizedTemplate.descriptionDefault || ''}`.trim()
        : (normalizedTemplate.descriptionDefault ||
            'Quote generated from TSFAD analysis. Review parts and labor before sending to customer.');
      setQuoteDescription(defaultDescription);
      setQuoteNotes('');
      setQuoteLineItems(lineItems);
      setQuoteUnmatchedParts(unmatchedParts);
      setQuoteEditingQuoteId(null);
      setQuoteRevisionOfQuoteId(null);
      setSelectedQuoteLineIds([]);
      const primaryContact =
        caseData?.customer?.contacts?.find?.((entry: any) => entry?.isPrimary) ||
        caseData?.customer?.contacts?.[0] ||
        null;
      setQuoteCustomerName(primaryContact?.fullName || '');
      setQuoteCustomerSignature('');
      setQuoteModalOpen(true);
    } catch (error: any) {
      setQuoteError(error?.message || 'Failed to build quote from TSFAD results.');
    } finally {
      setQuoteLoading(false);
    }
  }, [
    quoteAnalysisId,
    applySlaCoverageToQuotePartLine,
    buildDefaultQuoteTitle,
    buildQuoteLaborLines,
    caseData,
    estimatedMinutesForQuote,
    findQuoteSlaPartCoverage,
    implementedKitSelections,
    possibleCauses,
    problemDescription,
    latestAnalysis
  ]);

  const saveQuoteFromTSFAD = useCallback(async () => {
    if (!quoteAnalysisId) return;
    if (!quoteTemplateId) {
      setQuoteError(
        'No quotation template is available. An admin must configure a primary quote form template.'
      );
      return;
    }

      try {
      setQuoteSaving(true);
      setQuoteError(null);
      const payload = await tsfadApi.saveQuote(quoteAnalysisId, {
        quoteId: quoteEditingQuoteId || undefined,
        revisionOfQuoteId: quoteRevisionOfQuoteId || undefined,
        templateId: quoteTemplateId,
        title: quoteTitle.trim() || buildDefaultQuoteTitle(),
        description: quoteDescription.trim(),
        notes: quoteNotes.trim(),
        customerApprovalName: quoteCustomerName.trim(),
        customerApprovalSignature: quoteCustomerSignature.trim(),
        unmatchedParts: quoteUnmatchedParts,
        totalSettings: quoteTemplateLayout?.totalSettings || null,
        responseActions: quoteTemplateLayout?.responseActions || [],
        approvalPlacement: quoteTemplateLayout?.approvalPlacement || 'footer_c3',
        approvalFooterSlots: quoteTemplateLayout?.approvalFooterSlots || [],
        requireSignature: quoteTemplateLayout?.requireSignature ?? false,
        signatureLabel: quoteTemplateLayout?.signatureLabel || 'Customer approval signature',
        requireTechnicianSignature: quoteTemplateLayout?.requireTechnicianSignature ?? false,
        technicianSignatureLabel: quoteTemplateLayout?.technicianSignatureLabel || 'Technician signature',
        lineItems: quoteLineItems.map(line => ({
          id: line.id,
          lineType: line.lineType,
          description: line.description,
          quantity: line.quantity,
          unitValue: line.unitValue,
          isFree: Boolean(line.isFree),
          source: line.source,
          catalogId: line.catalogId || null,
          sku: line.sku || null,
          partNumber: line.partNumber || null,
          originalUnitValue:
            Number.isFinite(Number(line.originalUnitValue)) && Number(line.originalUnitValue) >= 0
              ? Number(line.originalUnitValue)
              : null,
          slaCoverageStatus: line.slaCoverageStatus || null,
          slaLabel: line.slaLabel || null,
          slaDiscountPercent:
            Number.isFinite(Number(line.slaDiscountPercent)) && Number(line.slaDiscountPercent) >= 0
              ? Number(line.slaDiscountPercent)
              : null,
          slaCoverageReason: line.slaCoverageReason || null,
          slaMatchedListNames: Array.isArray(line.slaMatchedListNames) ? line.slaMatchedListNames : []
        }))
      });
      const persistedQuote = payload?.quote && typeof payload.quote === 'object' ? payload.quote : null;
      if (persistedQuote?.quoteId) {
        setQuoteEditingQuoteId(String(persistedQuote.quoteId));
      }
      setQuoteRevisionOfQuoteId(
        persistedQuote?.revisionOfQuoteId && String(persistedQuote.revisionOfQuoteId).trim()
          ? String(persistedQuote.revisionOfQuoteId).trim()
          : null
      );
      setSavedQuotes(Array.isArray(payload?.quotes) ? payload.quotes : []);
      setSavedQuotesLoaded(true);
      setQuoteModalOpen(false);
    } catch (error: any) {
      setQuoteError(error?.message || 'Failed to save quote.');
    } finally {
      setQuoteSaving(false);
    }
  }, [
    quoteAnalysisId,
    quoteDescription,
    buildDefaultQuoteTitle,
    quoteEditingQuoteId,
    quoteLineItems,
    quoteCustomerName,
    quoteCustomerSignature,
    quoteNotes,
    quoteRevisionOfQuoteId,
    quoteTemplateId,
    quoteTemplateLayout,
    quoteTitle,
    quoteUnmatchedParts
  ]);

  const openSavedQuote = useCallback(async (quote: SavedTSFADQuote) => {
    try {
      setQuoteLoading(true);
      setQuoteError(null);
      const [formsPayload, pricingPayload, failureRatesPayload] = await Promise.all([
        formsApi.getAll(),
        pricingApi.getAll(),
        pricingApi.getFailureRates().catch(() => ({ items: [] }))
      ]);
      const templates = Array.isArray(formsPayload?.quotes) ? formsPayload.quotes : [];
      const selectedTemplate =
        templates.find((template: any) => String(template?.id || '') === String(quote.templateId || '')) ||
        (templates.length === 1
          ? templates[0]
          : templates.find((template: any) => template?.isPrimary) || null);

      if (!selectedTemplate) {
        throw new Error(
          'No quotation template is available. An admin must configure a primary quote form template.'
        );
      }

      setQuoteTemplateId(String(selectedTemplate.id || quote.templateId || ''));
      setQuoteTemplateName(selectedTemplate.name || quote.templateName || 'Quote');
      setQuoteTemplateLayout(
        normalizeQuoteTemplateLayout({
          ...selectedTemplate,
          totalSettings: quote.totalSettings || quote.totals?.totalSettings || selectedTemplate.totalSettings,
          responseActions:
            Array.isArray(quote.responseActions)
              ? quote.responseActions
              : quote.totals?.approvalSettings?.responseActions || selectedTemplate.responseActions,
          approvalPlacement:
            quote.approvalPlacement ||
            quote.totals?.approvalSettings?.approvalPlacement ||
            selectedTemplate.approvalPlacement,
          approvalFooterSlots:
            quote.approvalFooterSlots ||
            quote.totals?.approvalSettings?.approvalFooterSlots ||
            selectedTemplate.approvalFooterSlots,
          requireSignature:
            typeof quote.requireSignature === 'boolean'
              ? quote.requireSignature
              : typeof quote.totals?.approvalSettings?.requireSignature === 'boolean'
                ? quote.totals.approvalSettings.requireSignature
                : selectedTemplate.requireSignature,
          signatureLabel:
            quote.signatureLabel ||
            quote.totals?.approvalSettings?.signatureLabel ||
            selectedTemplate.signatureLabel,
          requireTechnicianSignature:
            typeof quote.requireTechnicianSignature === 'boolean'
              ? quote.requireTechnicianSignature
              : typeof quote.totals?.approvalSettings?.requireTechnicianSignature === 'boolean'
                ? quote.totals.approvalSettings.requireTechnicianSignature
                : selectedTemplate.requireTechnicianSignature,
          technicianSignatureLabel:
            quote.technicianSignatureLabel ||
            quote.totals?.approvalSettings?.technicianSignatureLabel ||
            selectedTemplate.technicianSignatureLabel
        })
      );
      setQuotePartCatalog(
        buildQuoteDevicePartCatalog(
          Array.isArray(pricingPayload?.items) ? pricingPayload.items : [],
          Array.isArray(failureRatesPayload?.items) ? failureRatesPayload.items : [],
          caseData?.device?.modelNumber || ''
        )
      );
      setQuoteTitle(quote.title || 'TSFAD Proforma Quote');
      setQuoteDescription(normalizeQuoteFieldText(quote.description || ''));
      setQuoteNotes(quote.notes || '');
      setQuoteCustomerName(quote.customerApprovalName || '');
      setQuoteCustomerSignature(quote.customerApprovalSignature || '');
      setQuoteLineItems(
        Array.isArray(quote.lineItems)
          ? quote.lineItems.map((line, index) => ({
              id:
                typeof line?.id === 'string' && line.id.trim()
                  ? line.id.trim()
                  : createQuoteLineId('saved-line', index),
              lineType: String(line?.lineType || 'part').toLowerCase() === 'labor' ? 'labor' : 'part',
              description: normalizeQuoteFieldText(String(line?.description || '').trim()),
              quantity: Number(line?.quantity || 0),
              unitValue: Number(line?.unitValue || 0),
              source: String(line?.source || 'manual'),
              catalogId: typeof line?.catalogId === 'string' ? line.catalogId : null,
              sku: typeof line?.sku === 'string' && line.sku.trim() ? line.sku.trim() : null,
              partNumber:
                typeof line?.partNumber === 'string' && line.partNumber.trim()
                  ? line.partNumber.trim()
                  : null,
              originalUnitValue:
                Number.isFinite(Number(line?.originalUnitValue)) && Number(line?.originalUnitValue) >= 0
                  ? Number(line.originalUnitValue)
                  : null,
              slaCoverageStatus:
                line?.slaCoverageStatus === 'COVERED' || line?.slaCoverageStatus === 'DISCOUNTED'
                  ? line.slaCoverageStatus
                  : null,
              slaLabel:
                typeof line?.slaLabel === 'string' && line.slaLabel.trim() ? line.slaLabel.trim() : null,
              slaDiscountPercent:
                Number.isFinite(Number(line?.slaDiscountPercent)) && Number(line?.slaDiscountPercent) >= 0
                  ? Number(line.slaDiscountPercent)
                  : null,
              slaCoverageReason:
                typeof line?.slaCoverageReason === 'string' && line.slaCoverageReason.trim()
                  ? line.slaCoverageReason.trim()
                  : null,
              slaMatchedListNames: Array.isArray(line?.slaMatchedListNames)
                ? line.slaMatchedListNames.map((value: any) => String(value || '').trim()).filter(Boolean)
                : [],
              isFree: Boolean(line?.isFree)
            }))
          : []
      );
      setQuoteUnmatchedParts(Array.isArray(quote.unmatchedParts) ? quote.unmatchedParts : []);
      const quoteStatus = normaliseQuoteStatusValue(quote?.status);
      if (quoteStatus === 'DRAFT') {
        setQuoteEditingQuoteId(quote.quoteId);
        setQuoteRevisionOfQuoteId(
          quote.revisionOfQuoteId && String(quote.revisionOfQuoteId).trim()
            ? String(quote.revisionOfQuoteId).trim()
            : null
        );
      } else if (quoteStatus === 'ADJUST_REQUESTED') {
        setQuoteEditingQuoteId(null);
        setQuoteRevisionOfQuoteId(quote.quoteId);
      } else {
        setQuoteEditingQuoteId(null);
        setQuoteRevisionOfQuoteId(null);
      }
      setSelectedQuoteLineIds([]);
      setQuoteModalOpen(true);
    } catch (error: any) {
      setQuoteError(error?.message || 'Failed to open saved quote.');
    } finally {
      setQuoteLoading(false);
    }
  }, [caseData?.device?.modelNumber, normaliseQuoteStatusValue]);

  const getQuoteStatusLabel = useCallback((quote: SavedTSFADQuote) => {
    const status = String(quote?.status || '').trim().toUpperCase();
    if (status === 'DRAFT') return 'Draft';
    if (status === 'SENT' || status === 'PENDING') return 'Waiting response';
    if (status === 'INACTIVE') return 'Inactive';
    if (status === 'ACCEPTED') return 'Approved';
    if (status === 'DENIED') return 'Denied';
    if (status === 'ADJUST_REQUESTED') return 'Adjust requested';
    return 'Saved';
  }, []);

  const getQuoteDecisionLabel = useCallback((quote: SavedTSFADQuote) => {
    const decision = String(quote?.customerDecision || '').trim().toUpperCase();
    if (decision === 'ACCEPT') return 'Accepted';
    if (decision === 'DENY') return 'Denied';
    if (decision === 'ADJUST') return 'Adjust requested';
    return null;
  }, []);

  const getQuoteDecisionBadgeStyle = useCallback((quote: SavedTSFADQuote): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      borderRadius: '999px',
      padding: '0.1rem 0.45rem',
      border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
      backgroundColor: BRAND_TEXT_SOFT,
      color: BRAND_TEXT
    };

    const decision = String(quote?.customerDecision || '').trim().toUpperCase();
    if (decision === 'ACCEPT') {
      return {
        ...baseStyle,
        backgroundColor: BRAND_SUCCESS_SOFT,
        border: `1px solid ${BRAND_SUCCESS_BORDER}`,
        color: BRAND_SUCCESS_TEXT
      };
    }
    if (decision === 'DENY') {
      return {
        ...baseStyle,
        backgroundColor: BRAND_DANGER_SOFT,
        border: `1px solid ${BRAND_DANGER_BORDER}`,
        color: BRAND_DANGER_TEXT
      };
    }
    if (decision === 'ADJUST') {
      return {
        ...baseStyle,
        backgroundColor: BRAND_ACCENT_SOFT,
        border: `1px solid ${BRAND_ACCENT}`,
        color: BRAND_ACCENT_TEXT
      };
    }
    return baseStyle;
  }, []);

  const getQuoteStatusBadgeStyle = useCallback((quote: SavedTSFADQuote): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      borderRadius: '999px',
      padding: '0.1rem 0.45rem',
      backgroundColor: BRAND_TEXT_SOFT,
      color: BRAND_TEXT,
      border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`
    };
    const status = String(quote?.status || '').trim().toUpperCase();
    if (status === 'ACCEPTED') {
      return {
        ...baseStyle,
        backgroundColor: BRAND_SUCCESS_SOFT,
        border: `1px solid ${BRAND_SUCCESS_BORDER}`,
        color: BRAND_SUCCESS_TEXT
      };
    }
    if (status === 'DENIED') {
      return {
        ...baseStyle,
        backgroundColor: BRAND_DANGER_SOFT,
        border: `1px solid ${BRAND_DANGER_BORDER}`,
        color: BRAND_DANGER_TEXT
      };
    }
    if (status === 'ADJUST_REQUESTED') {
      return {
        ...baseStyle,
        backgroundColor: BRAND_ACCENT_SOFT,
        border: `1px solid ${BRAND_ACCENT}`,
        color: BRAND_ACCENT_TEXT
      };
    }
    return baseStyle;
  }, []);

  const sendSavedQuote = useCallback(async (quote: SavedTSFADQuote) => {
    if (!quoteAnalysisId || !quote?.quoteId) return;
    try {
      setQuoteError(null);
      setQuoteSendingId(quote.quoteId);
      const payload = await tsfadApi.sendQuote(quoteAnalysisId, quote.quoteId);
      setSavedQuotes(Array.isArray(payload?.quotes) ? payload.quotes : []);
      setSavedQuotesLoaded(true);
    } catch (error: any) {
      setQuoteError(error?.message || 'Failed to send quote.');
    } finally {
      setQuoteSendingId(null);
    }
  }, [quoteAnalysisId]);

  const setSavedQuoteActive = useCallback(async (quote: SavedTSFADQuote, active: boolean, reasonInput = '') => {
    if (!quoteAnalysisId || !quote?.quoteId) return;
    const status = normaliseQuoteStatusValue(quote?.status);
    const inactiveFromStatus = normaliseQuoteStatusValue(quote?.inactiveFromStatus);
    if (!active && status !== 'SENT') return;
    if (active && (status !== 'INACTIVE' || inactiveFromStatus !== 'SENT')) return;

    const reason = String(reasonInput || '').trim();
    if (!active && !reason) return;

      try {
      setQuoteError(null);
      setQuoteStateChangeId(quote.quoteId);
      const payload = await tsfadApi.setQuoteActive(quoteAnalysisId, quote.quoteId, {
        active,
        reason: reason || undefined
      });
      setSavedQuotes(Array.isArray(payload?.quotes) ? payload.quotes : []);
      setSavedQuotesLoaded(true);
      return true;
    } catch (error: any) {
      setQuoteError(error?.message || `Failed to ${active ? 'reactivate' : 'inactivate'} quote.`);
      return false;
    } finally {
      setQuoteStateChangeId(null);
    }
  }, [quoteAnalysisId, normaliseQuoteStatusValue]);

  const openSavedQuoteDeleteModal = useCallback((quote: SavedTSFADQuote) => {
    if (!quote?.quoteId) return;
    const status = normaliseQuoteStatusValue(quote?.status);
    if (status !== 'DRAFT') return;
    setQuoteDeleteTarget(quote);
    setQuoteDeleteError(null);
    setQuoteError(null);
  }, [normaliseQuoteStatusValue]);

  const closeSavedQuoteDeleteModal = useCallback(() => {
    if (quoteDeleteId) return;
    setQuoteDeleteTarget(null);
    setQuoteDeleteError(null);
  }, [quoteDeleteId]);

  const confirmSavedQuoteDelete = useCallback(async () => {
    if (!quoteAnalysisId || !quoteDeleteTarget?.quoteId) return;
    const status = normaliseQuoteStatusValue(quoteDeleteTarget?.status);
    if (status !== 'DRAFT') {
      setQuoteDeleteError('Only draft quotes can be deleted.');
      return;
    }

    try {
      setQuoteError(null);
      setQuoteDeleteError(null);
      setQuoteDeleteId(quoteDeleteTarget.quoteId);
      const payload = await tsfadApi.deleteQuote(quoteAnalysisId, quoteDeleteTarget.quoteId);
      setSavedQuotes(Array.isArray(payload?.quotes) ? payload.quotes : []);
      setSavedQuotesLoaded(true);
      if (quoteEditingQuoteId === quoteDeleteTarget.quoteId) {
        setQuoteEditingQuoteId(null);
        setQuoteRevisionOfQuoteId(null);
      }
      setQuoteDeleteTarget(null);
    } catch (error: any) {
      const message = error?.message || 'Failed to delete draft quote.';
      setQuoteError(message);
      setQuoteDeleteError(message);
    } finally {
      setQuoteDeleteId(null);
    }
  }, [quoteAnalysisId, normaliseQuoteStatusValue, quoteDeleteTarget, quoteEditingQuoteId]);

  const openSavedQuoteInactivateModal = useCallback((quote: SavedTSFADQuote) => {
    setQuoteInactivateTarget(quote);
    setQuoteInactivationReason('');
    setQuoteInactivationError(null);
    setQuoteError(null);
  }, []);

  const closeSavedQuoteInactivateModal = useCallback(() => {
    if (quoteStateChangeId) return;
    setQuoteInactivateTarget(null);
    setQuoteInactivationReason('');
    setQuoteInactivationError(null);
  }, [quoteStateChangeId]);

  const confirmSavedQuoteInactivate = useCallback(async () => {
    if (!quoteInactivateTarget) return;
    const reason = quoteInactivationReason.trim();
    if (!reason) {
      setQuoteInactivationError('Reason is required.');
      return;
    }
    setQuoteInactivationError(null);
    const ok = await setSavedQuoteActive(quoteInactivateTarget, false, reason);
    if (ok) {
      setQuoteInactivateTarget(null);
      setQuoteInactivationReason('');
      setQuoteInactivationError(null);
    }
  }, [quoteInactivateTarget, quoteInactivationReason, setSavedQuoteActive]);

  const cloneCause = (cause: PossibleCause): PossibleCause =>
    JSON.parse(JSON.stringify(cause)) as PossibleCause;

  const storeBaselineCause = (cause: PossibleCause) => {
    originalCausesRef.current.set(cause.id, cloneCause(cause));
  };

  const loadCauseDrafts = useCallback((cause: PossibleCause | null) => {
    if (!cause) {
      setProcedureDraft('');
      setRepairDraft('');
      setToolsDraft('');
      setPartsDraft('');
      setNotesDraft('');
      return;
    }
    setProcedureDraft(formatProcedureDraft(cause));
    setRepairDraft(formatRepairDraft(cause));
    setToolsDraft(joinItemsForEditing(cause.requiredTools));
    setPartsDraft(joinItemsForEditing(cause.requiredParts));
    setNotesDraft(joinItemsForEditing(cause.planNotes));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isInactivating) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    window.requestAnimationFrame(() => {
      inactivationPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      inactivationTextareaRef.current?.focus();
    });
  }, [isInactivating]);

  useEffect(() => {
    isEditingProcedureRef.current = isEditingProcedure;
  }, [isEditingProcedure]);

useEffect(() => {
  isInactivatingRef.current = isInactivating;
}, [isInactivating]);

const persistedImplementedKitSelections = useMemo<ImplementedKitSelection[]>(() => {
  const latestSelections =
    latestAnalysis?.caseModel && typeof latestAnalysis.caseModel === 'object'
      ? (latestAnalysis.caseModel as Record<string, any>).implementedKitSelections
      : null;
  const fallbackSelections =
    caseData?.tsfadAnalyses?.[0]?.caseModel &&
    typeof caseData.tsfadAnalyses[0].caseModel === 'object'
      ? (caseData.tsfadAnalyses[0].caseModel as Record<string, any>).implementedKitSelections
      : null;
  const candidate = latestSelections || fallbackSelections;
  return Array.isArray(candidate)
    ? candidate
        .map(entry => ({
          kitId: String(entry?.kitId || '').trim(),
          kitNumber: typeof entry?.kitNumber === 'string' ? entry.kitNumber.trim() : null,
          sku: typeof entry?.sku === 'string' ? entry.sku.trim() : null,
          name: typeof entry?.name === 'string' ? entry.name.trim() : null,
          matchedPartNumbers: Array.isArray(entry?.matchedPartNumbers)
            ? entry.matchedPartNumbers.map((value: any) => String(value || '').trim()).filter(Boolean)
            : [],
          implementedAt:
            typeof entry?.implementedAt === 'string' && entry.implementedAt.trim()
              ? entry.implementedAt.trim()
              : new Date().toISOString()
        }))
        .filter(entry => entry.kitId)
    : [];
}, [caseData?.tsfadAnalyses, latestAnalysis]);

useEffect(() => {
  if (!analysisId) return;
  if (implementedKitsHydratedAnalysisIdRef.current === analysisId) return;
  setImplementedKitSelections(persistedImplementedKitSelections);
  implementedKitsHydratedAnalysisIdRef.current = analysisId;
}, [analysisId, persistedImplementedKitSelections]);

useEffect(() => {
  if (draftRestoreAppliedRef.current) {
    return;
  }
  if (!caseData?.id) {
    return;
  }

  const draft = loadTSFADDraft(caseData.id);
  draftRestoreAppliedRef.current = true;
  if (!draft || !Array.isArray(draft.possibleCauses) || draft.possibleCauses.length === 0) {
    if (analysisStatus === 'ACCEPTED') {
      clearTSFADDraft(caseData.id);
      setDraftRestoredAt(null);
    }
    return;
  }
  const draftSavedAtMs = Number(new Date(draft.savedAt || '').getTime());
  const analysisSavedAtMs = Number(
    new Date(
      latestAnalysis?.updatedAt ||
        latestAnalysis?.acceptedAt ||
        latestAnalysis?.createdAt ||
        ''
    ).getTime()
  );
  if (
    Number.isFinite(draftSavedAtMs) &&
    Number.isFinite(analysisSavedAtMs) &&
    draftSavedAtMs <= analysisSavedAtMs
  ) {
    if (analysisStatus === 'ACCEPTED') {
      clearTSFADDraft(caseData.id);
      setDraftRestoredAt(null);
    }
    return;
  }

  const restoredAnalysisId =
    typeof draft.analysisId === 'string' && draft.analysisId.trim() ? draft.analysisId.trim() : null;
  const restoredSteps: TestStep[] = (draft.testSteps || []).map(step => ({
    ...step,
    timestamp: step.timestamp ? new Date(step.timestamp as any) : undefined
  }));
  if (restoredAnalysisId) {
    setAnalysisId(restoredAnalysisId);
  }
  setAnalysisStarted(false);
  setAnalysisComplete(true);
  setModelId(prev => prev || caseData?.device?.modelId || caseData?.device?.model?.id || null);
  setLatestAnalysis(prev => {
    if (prev && restoredAnalysisId && prev.id === restoredAnalysisId) {
      return prev;
    }
    return {
      id: restoredAnalysisId || prev?.id || `draft-${caseData.id}`,
      caseId: caseData.id,
      modelId:
        prev?.modelId ||
        modelId ||
        caseData?.device?.modelId ||
        caseData?.device?.model?.id ||
        '',
      analysisStatus:
        draft.analysisStatus === 'ACCEPTED'
          ? 'COMPLETED'
          : (draft.analysisStatus || 'COMPLETED'),
      isWorkspace: true,
      persistedAnalysisId:
        prev && !prev.isWorkspace
          ? prev.id
          : prev?.persistedAnalysisId && prev.persistedAnalysisId.trim()
          ? prev.persistedAnalysisId
          : null,
      caseModel:
        prev?.caseModel && typeof prev.caseModel === 'object'
          ? prev.caseModel
          : {}
    } as TSFADAnalysisRecord;
  });
  setPossibleCauses(draft.possibleCauses);
  setTestSteps(restoredSteps);
  setImplementedKitSelections(
    Array.isArray(draft.implementedKitSelections) ? draft.implementedKitSelections.filter(entry => entry?.kitId) : []
  );
  const restoredSelected =
    draft.selectedCauseId && Array.isArray(draft.possibleCauses)
      ? draft.possibleCauses.find(cause => cause.id === draft.selectedCauseId) || null
      : null;
  setSelectedCause(restoredSelected);
  setAnalysisStatus(draft.analysisStatus === 'ACCEPTED' ? 'COMPLETED' : (draft.analysisStatus || 'COMPLETED'));
  setDraftRestoredAt(draft.savedAt || new Date().toISOString());
}, [
  analysisStatus,
  caseData?.id,
  caseData?.device?.model?.id,
  caseData?.device?.modelId,
  latestAnalysis?.acceptedAt,
  latestAnalysis?.createdAt,
  latestAnalysis?.updatedAt,
  modelId
]);

useEffect(() => {
  if (
    !caseData?.id ||
    !analysisComplete ||
    !analysisId ||
    possibleCauses.length === 0 ||
    analysisStatus === 'ACCEPTED'
  ) {
    return;
  }

  const payload = buildDraftPayload();
  if (!payload) {
    return;
  }

  if (draftSaveTimeoutRef.current) {
    window.clearTimeout(draftSaveTimeoutRef.current);
  }
  draftSaveTimeoutRef.current = window.setTimeout(() => {
    saveTSFADDraft(caseData.id, payload);
  }, 400);

  return () => {
    if (draftSaveTimeoutRef.current) {
      window.clearTimeout(draftSaveTimeoutRef.current);
      draftSaveTimeoutRef.current = null;
    }
  };
}, [analysisComplete, analysisId, analysisStatus, buildDraftPayload, caseData?.id, possibleCauses.length]);

useEffect(() => {
  if (!showTestModal || !selectedCause) return;
  setPlanAssistantOpen(false);
  setPlanAssistantQuestion('');
  setPlanAssistantMessages([]);
  setPlanAssistantError(null);
  setPlanAssistantActiveAsk(null);
  setPlanAssistantConversationId(null);
  setSelectedPlanStepKey('');
}, [selectedCause, showTestModal]);

const closeProgressStream = () => {
  if (progressEventSourceRef.current) {
    progressEventSourceRef.current.close();
    progressEventSourceRef.current = null;
  }
  };

  const updateStageState = (stageId: AnalysisStageId, status: StageStatus, message?: string, durationMs?: number) => {
    const stageDef = STAGE_MAP[stageId];
    if (!stageDef) return;

    const resolvedMessage = message && message.trim().length > 0 ? message : stageDef.defaultMessage;

    setStageStates(prev => ({
      ...prev,
      [stageId]: {
        status,
        message: resolvedMessage,
        durationMs
      }
    }));

    if (status === 'active') {
      setAnalysisProgress(prev => Math.max(prev, stageDef.startProgress));
      setActiveStageMessage(resolvedMessage);
    } else if (status === 'complete') {
      setAnalysisProgress(prev => Math.max(prev, stageDef.completeProgress));
    } else if (status === 'error') {
      setActiveStageMessage(resolvedMessage);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleAnalysisFailure = (
    status: number,
    message: string,
    details?: string,
    payload?: Record<string, unknown> | null
  ) => {
    closeProgressStream();

    const lowerMessage = message ? message.toLowerCase() : '';
    const lowerDetails = details ? details.toLowerCase() : '';
    const failureCode = typeof payload?.code === 'string' ? payload.code : null;
    const failureModelId = typeof payload?.modelId === 'string' ? payload.modelId : null;
    const failureFeedbackContext =
      payload?.feedbackContext && typeof payload.feedbackContext === 'object'
        ? (payload.feedbackContext as Record<string, unknown>)
        : null;
    const failureMetadata =
      payload?.metadata && typeof payload.metadata === 'object'
        ? (payload.metadata as Record<string, unknown>)
        : null;
    const failureCitationReview =
      failureMetadata?.citationIntegrityReview && Array.isArray(failureMetadata.citationIntegrityReview)
        ? {
            code: failureCode || 'TSFAD_CITATION_INTEGRITY_REVIEW',
            message: details || message,
            issues: failureMetadata.citationIntegrityReview
          }
        : null;
    const failureOccurredAt =
      typeof payload?.occurredAt === 'string' && payload.occurredAt.trim().length > 0
        ? payload.occurredAt.trim()
        : new Date().toISOString();
    const failureSelectedPathValues = Array.isArray(failureFeedbackContext?.selectedPaths)
      ? (failureFeedbackContext?.selectedPaths as unknown[])
          .map(value => formatKnowledgePathLabel(String(value || '')))
          .filter(Boolean)
      : [];
    const taskGuardCodes = new Set([
      'TASK_EXECUTION_CASE_CLOSED',
      'TASK_EXECUTION_ACTIVE_TASK_REQUIRED',
      'TASK_EXECUTION_UNASSIGNED',
      'TASK_EXECUTION_ASSIGNED_TO_OTHER'
    ]);
    const assignmentIssue =
      Boolean(failureCode && taskGuardCodes.has(failureCode)) ||
      lowerMessage.includes('assigned technician') ||
      lowerMessage.includes('task is not assigned') ||
      lowerMessage.includes('no active task is available') ||
      lowerMessage.includes('case is closed') ||
      lowerDetails.includes('assigned to') ||
      lowerDetails.includes('assign this case task') ||
      lowerDetails.includes('assign an active task') ||
      lowerDetails.includes('review-only mode');
    const knowledgeCoverageIssue =
      failureCode === 'TSFAD_SYSTEM_PATH_NOT_IDENTIFIED' ||
      failureCode === 'TSFAD_SYSTEM_PATH_SNIPPET_GAP';
    const documentationIssue =
      lowerMessage.includes('no documentation') ||
      lowerDetails.includes('no documentation') ||
      lowerMessage.includes('device model not found') ||
      lowerDetails.includes('device model not found') ||
      lowerMessage.includes('no knowledge available') ||
      lowerDetails.includes('no knowledge available');
    const shouldShowRetryModal = !assignmentIssue && !knowledgeCoverageIssue && !documentationIssue;

    if (failureModelId) {
      setModelId(failureModelId);
    }
    setAnalysisFailureCode(failureCode);
    setAnalysisFailureFeedbackContext(failureFeedbackContext);
    setAnalyzeRetryFailureCode(failureCode);
    setAnalyzeRetryOccurredAt(failureOccurredAt);
    setAnalyzeRetryDetails(details || message || 'An unexpected error occurred during analysis.');
    setCitationIntegrityReview(failureCitationReview);

    if (assignmentIssue) {
      const assignmentGuardByCode: Record<string, { title: string; lead: string; details: string }> = {
        TASK_EXECUTION_CASE_CLOSED: {
          title: 'Case Closed',
          lead: 'This case is closed. TSFAD is review-only.',
          details: 'This case is closed. TSFAD can be reviewed, but no new TSFAD actions can be run.'
        },
        TASK_EXECUTION_ACTIVE_TASK_REQUIRED: {
          title: 'TSFAD Run Blocked',
          lead: 'No active task is available for this case.',
          details: 'Assign an active task to a technician before running TSFAD actions.'
        },
        TASK_EXECUTION_UNASSIGNED: {
          title: 'TSFAD Run Blocked',
          lead: 'The active task is not assigned.',
          details: 'Assign this case task to a technician before running TSFAD actions.'
        },
        TASK_EXECUTION_ASSIGNED_TO_OTHER: {
          title: 'TSFAD Run Blocked',
          lead: 'Only the assigned technician can run TSFAD.',
          details: 'This task is assigned to another user.'
        }
      };
      const guardPresentation =
        (failureCode && assignmentGuardByCode[failureCode]) || {
          title: 'TSFAD Run Blocked',
          lead: 'TSFAD cannot be run for this task right now.',
          details: 'Check task assignment and status, then try again.'
        };
      setShowKnowledgeCoverageModal(false);
      setKnowledgeCoverageActionError(null);
      setError(guardPresentation.title);
      setErrorDetails(details || message || guardPresentation.details);
      setAssignmentBlockedTitle(guardPresentation.title);
      setAssignmentBlockedLead(guardPresentation.lead);
      setAssignmentBlockedDetails(
        details || message || guardPresentation.details
      );
      setShowAssignmentBlockedModal(true);
      setShowAnalyzeRetryModal(false);
    } else if (knowledgeCoverageIssue) {
      setShowKnowledgeCoverageModal(true);
      setKnowledgeCoverageActionError(null);
      setError('Knowledge Coverage Issue');
      setErrorDetails(
        details ||
          (failureCode === 'TSFAD_SYSTEM_PATH_SNIPPET_GAP'
            ? failureSelectedPathValues.length > 0
              ? `TSFAD identified ${failureSelectedPathValues.join(', ')} but found no tagged knowledge nodes for those system paths. You can send feedback so the knowledge team can fix knowledge node path coverage for this model.`
              : 'TSFAD identified a system path but found no tagged knowledge nodes for it. You can send feedback so the knowledge team can fix knowledge node path coverage for this model.'
              : 'TSFAD could not identify a reliable system path from the current problem description. You can refine the description and retry, or send feedback so the knowledge team can improve path detection for this model.')
      );
      setShowAnalyzeRetryModal(false);
    } else if (documentationIssue) {
      setShowKnowledgeCoverageModal(false);
      setKnowledgeCoverageActionError(null);
      setError('No Documentation Available');
      setErrorDetails(
        details ||
          `No technical documentation has been uploaded for the ${caseData?.device?.modelNumber} model. Documentation is required for Troubleshooting From a Distance (TSFAD).`
      );
      setShowAnalyzeRetryModal(false);
    } else if (status === 503) {
      setShowKnowledgeCoverageModal(false);
      setKnowledgeCoverageActionError(null);
      setError('Analysis Unavailable');
      setErrorDetails(details || message || 'Troubleshooting From a Distance (TSFAD) requires the AI service to be available.');
      setShowAnalyzeRetryModal(true);
    } else {
      setShowKnowledgeCoverageModal(false);
      setKnowledgeCoverageActionError(null);
      setError('Analysis Failed');
      setErrorDetails(details || message || 'An unexpected error occurred during analysis.');
      setShowAnalyzeRetryModal(true);
    }

    if (!shouldShowRetryModal) {
      setShowAnalyzeRetryModal(false);
    }

    setStageStates(prev => {
      const next = { ...prev };
      STAGE_SEQUENCE.forEach(stage => {
        const current = next[stage.id];
        if (!current || current.status !== 'complete') {
          next[stage.id] = {
            status: 'error',
            message: message || current?.message || stage.defaultMessage
          };
        }
      });
      return next;
    });
    setActiveStageMessage(message || 'Analysis halted');
    setAnalysisComplete(false);
    setAnalysisStarted(false);
    setAnalysisProgress(0);
    setAnalysisId(null);
    setPossibleCauses([]);
    originalCausesRef.current = new Map();
    setAnalysisStatus('NONE');
    setLatestAnalysis(null);
  };


  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
    setFeedbackCause(null);
    setFeedbackSuggestionDescription(null);
    setFeedbackMetadata(null);
    setSuggestionLoading(false);
    setFeedbackForm(createFeedbackDefaults());
  };

  const buildKnowledgeCoverageFeedbackDraft = useCallback(() => {
    const selectedPaths = Array.isArray(analysisFailureFeedbackContext?.selectedPaths)
      ? (analysisFailureFeedbackContext?.selectedPaths as unknown[])
          .map(value => formatKnowledgePathLabel(String(value || '')))
          .filter(Boolean)
      : [];
    const title =
      analysisFailureCode === 'TSFAD_SYSTEM_PATH_SNIPPET_GAP'
        ? 'TSFAD found system paths but zero tagged knowledge nodes'
        : 'TSFAD could not identify system paths';
    const descriptionLines = [
      `Case: ${caseDisplayNumber || caseData?.id || 'Unknown case'}`,
      `Device model: ${caseData?.device?.modelNumber || 'Unknown model'}`,
      analysisFailureCode === 'TSFAD_SYSTEM_PATH_SNIPPET_GAP'
        ? `Selected system paths: ${selectedPaths.length > 0 ? selectedPaths.join(', ') : 'None returned'}`
        : 'Selected system paths: None returned by probe',
      `Problem description: ${problemDescription || '(empty)'}`,
      `Knowledge node count: ${String(analysisFailureFeedbackContext?.snippetCount ?? 0)}`,
      `Total records: ${String(analysisFailureFeedbackContext?.totalRecords ?? 0)}`,
      `Filtered with no matching path: ${String(analysisFailureFeedbackContext?.filteredNoPath ?? 0)}`
    ];

    return {
      modelId: modelId || caseData?.device?.modelId || caseData?.device?.model?.id || null,
      defaults: {
        feedbackType: 'MISSING_TAGS',
        severity: 'HIGH',
        title,
        description: descriptionLines.join('\n'),
        impact: 'TSFAD could not build a troubleshooting plan from the available knowledge coverage.'
      },
      metadata: {
        issueCode: analysisFailureCode,
        ...(analysisFailureFeedbackContext || {}),
        deviceModel: caseData?.device?.modelNumber || null,
        caseNumber: caseDisplayNumber || null
      }
    };
  }, [
    analysisFailureCode,
    analysisFailureFeedbackContext,
    caseData?.device?.model?.id,
    caseData?.device?.modelId,
    caseData?.device?.modelNumber,
    caseData?.id,
    caseDisplayNumber,
    modelId,
    problemDescription
  ]);

  const submitKnowledgeCoverageFeedback = useCallback(async () => {
    const draft = buildKnowledgeCoverageFeedbackDraft();
    if (!draft.modelId) {
      throw new Error('Model ID not available. Please ensure the case is linked to a device model.');
    }

    await tsfadApi.submitDocumentationFeedback({
      modelId: draft.modelId,
      caseId: caseData?.id,
      feedbackType: draft.defaults.feedbackType,
      severity: draft.defaults.severity,
      title: draft.defaults.title,
      description: draft.defaults.description,
      impact: draft.defaults.impact,
      metadata: draft.metadata
    });
  }, [buildKnowledgeCoverageFeedbackDraft, caseData?.id]);

const formatCauseTitle = (description?: string, shortTitle?: string) => {
  if (shortTitle && shortTitle.trim()) {
    return shortTitle.trim();
  }
  if (!description) return '';
  return description.trim();
};

const coerceStepArray = (input: unknown): unknown[] => {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  if (typeof input === 'object') return [input];
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return [];
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === 'object') return [parsed];
      } catch (_err) {
        // fall through
      }
    }
    return trimmed.split(/[\n]+/);
  }
  return [];
};

const extractNumberAndDescription = (value: string) => {
  const cleaned = value.replace(/\s+/g, ' ').trim();
  const match = cleaned.match(/^(\d+(?:\.\d+)*)\s*[).:-]?\s*(.*)$/);
  if (match) {
    return { number: match[1].trim(), description: match[2].trim() };
  }
  return { number: '', description: cleaned };
};

// eslint-disable-next-line react-hooks/exhaustive-deps
const normaliseStepNodes = (input: unknown, parentNumber = ''): StepNode[] => {
  const items = coerceStepArray(input);
  let counter = 0;
  const nodes: StepNode[] = [];

  items.forEach(item => {
    if (item === null || item === undefined) return;

    let description = '';
    let providedNumber = '';
    let children: unknown = [];
    let tags: string[] = [];
    let sourceNodeIds: string[] = [];
    let triggeredByVerificationStepNumbers: string[] = [];
    let sourceVerificationStepNumbers: string[] = [];
    let repairActionType: string | undefined;
    let correctiveMechanism: string | undefined;
    let requiresCorrectiveActionIfFailed: boolean | undefined;
    let metadata: Record<string, unknown> | undefined;
    let capture: Record<string, unknown> | undefined;

    if (typeof item === 'string') {
      const parsed = extractNumberAndDescription(item);
      description = parsed.description;
      providedNumber = parsed.number;
    } else if (typeof item === 'object') {
      const obj = item as Record<string, unknown>;
      description = String(obj.description || obj.step || obj.value || obj.text || obj.detail || '').trim();
      providedNumber = String(obj.number || obj.id || obj.stepNumber || '').trim();
      children =
        obj.subSteps ||
        obj.substeps ||
        obj.children ||
        obj.steps ||
        obj.items ||
        obj.actions ||
        [];
      tags = Array.isArray(obj.tags)
        ? obj.tags.map(value => String(value || '').trim()).filter(Boolean)
        : [];
      sourceNodeIds = Array.isArray(obj.sourceNodeIds)
        ? obj.sourceNodeIds.map(value => String(value || '').trim()).filter(Boolean)
        : [];
      triggeredByVerificationStepNumbers = Array.isArray(obj.triggeredByVerificationStepNumbers)
        ? obj.triggeredByVerificationStepNumbers
            .map(value => String(value || '').trim())
            .filter(Boolean)
        : [];
      sourceVerificationStepNumbers = Array.isArray(obj.sourceVerificationStepNumbers)
        ? obj.sourceVerificationStepNumbers
            .map(value => String(value || '').trim())
            .filter(Boolean)
        : [];
      repairActionType = String(obj.repairActionType || '').trim() || undefined;
      correctiveMechanism = String(obj.correctiveMechanism || '').trim() || undefined;
      requiresCorrectiveActionIfFailed =
        typeof obj.requiresCorrectiveActionIfFailed === 'boolean'
          ? obj.requiresCorrectiveActionIfFailed
          : undefined;
      metadata =
        obj.metadata && typeof obj.metadata === 'object'
          ? { ...(obj.metadata as Record<string, unknown>) }
          : undefined;
      capture =
        obj.capture && typeof obj.capture === 'object'
          ? { ...(obj.capture as Record<string, unknown>) }
          : undefined;
    }

    description = description.replace(/\s+/g, ' ').trim();
    if (!description) return;

    counter += 1;
    const fallbackNumber = parentNumber ? `${parentNumber}.${counter}` : `${counter}`;
    const normalisedNumber =
      providedNumber && /^[0-9]+(?:\.[0-9]+)*$/.test(providedNumber) ? providedNumber : fallbackNumber;

    const subSteps = normaliseStepNodes(children, normalisedNumber);

    nodes.push({
      number: normalisedNumber,
      description,
      sourceNodeIds: sourceNodeIds.length > 0 ? Array.from(new Set(sourceNodeIds)) : undefined,
      triggeredByVerificationStepNumbers:
        triggeredByVerificationStepNumbers.length > 0
          ? Array.from(new Set(triggeredByVerificationStepNumbers))
          : undefined,
      sourceVerificationStepNumbers:
        sourceVerificationStepNumbers.length > 0
          ? Array.from(new Set(sourceVerificationStepNumbers))
          : undefined,
      repairActionType,
      correctiveMechanism,
      requiresCorrectiveActionIfFailed,
      tags: tags.length > 0 ? tags : undefined,
      metadata,
      capture,
      subSteps: subSteps.length > 0 ? subSteps : undefined
    });
  });

  return nodes;
};

const normaliseStepNodeText = (value: string): string =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const normalizeReferenceImageBlockSection = (
  value: unknown
): ReferenceImageBlockSection | undefined => {
  const section = String(value || '').trim().toUpperCase();
  if (
    section === 'SUMMARY' ||
    section === 'VERIFICATION' ||
    section === 'REPAIR' ||
    section === 'TOOLS' ||
    section === 'PARTS' ||
    section === 'NOTES'
  ) {
    return section;
  }
  return undefined;
};

const flattenStructuredNodesForPreserve = (nodes: StepNode[] | undefined): StepNode[] => {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];
  const flattened: StepNode[] = [];
  const visit = (list: StepNode[]) => {
    list.forEach(node => {
      if (!node || typeof node !== 'object') return;
      const description = String(node.description || '').trim();
      const number = String(node.number || '').trim();
      if (!description && !number) return;
      const flattenedNode: StepNode = {
        number,
        description
      };
      if (Array.isArray(node.sourceNodeIds) && node.sourceNodeIds.length > 0) {
        flattenedNode.sourceNodeIds = [...node.sourceNodeIds];
      }
      if (Array.isArray(node.tags) && node.tags.length > 0) {
        flattenedNode.tags = [...node.tags];
      }
      if (node.metadata && typeof node.metadata === 'object') {
        flattenedNode.metadata = { ...(node.metadata as Record<string, unknown>) };
      }
      if (node.capture && typeof node.capture === 'object') {
        flattenedNode.capture = { ...(node.capture as Record<string, unknown>) };
      }
      flattened.push(flattenedNode);
      if (Array.isArray(node.subSteps) && node.subSteps.length > 0) {
        visit(node.subSteps);
      }
    });
  };
  visit(nodes);
  return flattened;
};

const flattenStepNodes = (nodes: StepNode[]): string[] => {
  const result: string[] = [];
  const visit = (list: StepNode[]) => {
    list.forEach(node => {
      result.push(node.description);
      if (node.subSteps) {
        visit(node.subSteps);
      }
    });
  };
  visit(nodes);
  return uniqueStrings(result);
};

const flattenStepNodesForSelector = (
  nodes: StepNode[],
  section: 'VERIFICATION' | 'REPAIR'
): PlanStepOption[] => {
  const result: PlanStepOption[] = [];
  const visit = (list: StepNode[]) => {
    list.forEach((node, index) => {
      const stepNumber = node.number || `${index + 1}`;
      const description = String(node.description || '').trim();
      if (description) {
        result.push({
          key: `${section}-${stepNumber}-${description.slice(0, 40)}`,
          section,
          number: stepNumber,
          description
        });
      }
      if (Array.isArray(node.subSteps) && node.subSteps.length > 0) {
        visit(node.subSteps);
      }
    });
  };
  visit(nodes || []);
  return result;
};

const formatPlanAssistantText = (value: string): string => {
  const text = String(value || '').replace(/\r\n/g, '\n').trim();
  if (!text) return '';
  const withNumberBreaks = text
    .replace(/\s+(\d+\))/g, '\n$1')
    .replace(/\s+-\s+/g, '\n- ');
  return withNumberBreaks.replace(/\n{3,}/g, '\n\n').trim();
};

const stringifyStepNodes = (nodes: StepNode[]): string[] => {
  const lines: string[] = [];
  const visit = (list: StepNode[]) => {
    list.forEach(node => {
      const prefix = node.number ? `${node.number} ` : '';
      lines.push(`${prefix}${node.description}`);
      if (node.subSteps && node.subSteps.length > 0) {
        visit(node.subSteps);
      }
    });
  };
  visit(nodes);
  return lines;
};

function formatStepsForEditing(
  structured?: StepNode[],
  legacyList?: string[],
  legacyBlock?: string
): string {
  if (structured && structured.length > 0) {
    return stringifyStepNodes(structured).join('\n');
  }
  if (Array.isArray(legacyList) && legacyList.length > 0) {
    return legacyList.join('\n');
  }
  if (typeof legacyBlock === 'string' && legacyBlock.trim()) {
    return legacyBlock
      .split(/[\n]+/)
      .map(step => step.trim())
      .filter(step => step.length > 0)
      .join('\n');
  }
  return '';
}

function formatProcedureDraft(cause: PossibleCause): string {
  return formatStepsForEditing(cause.structuredTestingSteps, cause.testingSteps, cause.testProcedure);
}

function formatRepairDraft(cause: PossibleCause): string {
  return formatStepsForEditing(cause.structuredRepairSteps, cause.repairSteps);
}

function joinItemsForEditing(items?: string[]): string {
  return Array.isArray(items) && items.length > 0 ? items.join('\n') : '';
}

function parseMultilineList(value: string): string[] {
  return value
    .split(/\n+/)
    .map(item => item.trim())
    .filter(item => item.length > 0);
}

function parseStepDraft(
  draft: string,
  existingNodes?: StepNode[]
): { structured: StepNode[]; lines: string[]; flattened: string[]; text: string } {
  const lines = parseMultilineList(draft);
  const structured = normaliseStepNodes(lines);
  const existingFlat = flattenStructuredNodesForPreserve(existingNodes);
  const existingByNumber = new Map<string, StepNode>();
  const existingByDescription = new Map<string, StepNode>();
  existingFlat.forEach(node => {
    const number = String(node.number || '').trim();
    const descriptionKey = normaliseStepNodeText(String(node.description || ''));
    if (number && !existingByNumber.has(number)) {
      existingByNumber.set(number, node);
    }
    if (descriptionKey && !existingByDescription.has(descriptionKey)) {
      existingByDescription.set(descriptionKey, node);
    }
  });
  const preserve = (nodes: StepNode[]): StepNode[] =>
    nodes.map(node => {
      const number = String(node.number || '').trim();
      const description = String(node.description || '').trim();
      const match =
        existingByNumber.get(number) ||
        existingByDescription.get(normaliseStepNodeText(description)) ||
        undefined;
      const subSteps = Array.isArray(node.subSteps) ? preserve(node.subSteps) : [];
      const preservedNode: StepNode = {
        ...node,
        ...(match && Array.isArray(match.sourceNodeIds) && match.sourceNodeIds.length > 0
          ? { sourceNodeIds: [...match.sourceNodeIds] }
          : {}),
        ...(match &&
        Array.isArray(match.triggeredByVerificationStepNumbers) &&
        match.triggeredByVerificationStepNumbers.length > 0
          ? { triggeredByVerificationStepNumbers: [...match.triggeredByVerificationStepNumbers] }
          : {}),
        ...(match &&
        Array.isArray(match.sourceVerificationStepNumbers) &&
        match.sourceVerificationStepNumbers.length > 0
          ? { sourceVerificationStepNumbers: [...match.sourceVerificationStepNumbers] }
          : {}),
        ...(match?.repairActionType ? { repairActionType: match.repairActionType } : {}),
        ...(match?.correctiveMechanism ? { correctiveMechanism: match.correctiveMechanism } : {}),
        ...(typeof match?.requiresCorrectiveActionIfFailed === 'boolean'
          ? { requiresCorrectiveActionIfFailed: match.requiresCorrectiveActionIfFailed }
          : {}),
        ...(match && Array.isArray(match.tags) && match.tags.length > 0 ? { tags: [...match.tags] } : {}),
        ...(match && match.metadata && typeof match.metadata === 'object'
          ? { metadata: { ...(match.metadata as Record<string, unknown>) } }
          : {}),
        ...(match && match.capture && typeof match.capture === 'object'
          ? { capture: { ...(match.capture as Record<string, unknown>) } }
          : {}),
        ...(subSteps.length > 0 ? { subSteps } : {})
      };
      return preservedNode;
    });
  const preservedStructured = preserve(structured);
  const flattened = flattenStepNodes(preservedStructured);
  const text = lines.join('\n');
  return { structured: preservedStructured, lines, flattened, text };
}

// eslint-disable-next-line react-hooks/exhaustive-deps
const transformCause = (cause: any, fallbackIndex: number): PossibleCause => {
  const description = typeof cause.description === 'string' ? cause.description.trim() : '';
  const shortTitle =
    typeof cause.shortTitle === 'string' && cause.shortTitle.trim()
      ? cause.shortTitle.trim()
      : description
      ? (description.match(/^[^.!?\n]+[.!?]?/) || [description])[0].trim()
      : undefined;
  const summary = typeof cause.summary === 'string' ? cause.summary.trim() : undefined;
  const testProcedure = typeof cause.testProcedure === 'string' ? cause.testProcedure.trim() : '';

  const structuredTestingSteps = normaliseStepNodes(
    (Array.isArray(cause.structuredTestingSteps) && cause.structuredTestingSteps.length > 0
      ? cause.structuredTestingSteps
      : Array.isArray(cause.structuredVerificationSteps) && cause.structuredVerificationSteps.length > 0
      ? cause.structuredVerificationSteps
      : Array.isArray(cause.testingSteps) && cause.testingSteps.length > 0
      ? cause.testingSteps
      : Array.isArray(cause.verificationSteps) && cause.verificationSteps.length > 0
      ? cause.verificationSteps
      : testProcedure
      ? testProcedure.split(/[\n]+/)
      : []) as unknown
  );
  const structuredRepairSteps = normaliseStepNodes(
    (Array.isArray(cause.structuredRepairSteps) && cause.structuredRepairSteps.length > 0
      ? cause.structuredRepairSteps
      : Array.isArray(cause.repairSteps) && cause.repairSteps.length > 0
      ? cause.repairSteps
      : []) as unknown
  );

  const testingSteps = flattenStepNodes(structuredTestingSteps);
  const repairSteps = flattenStepNodes(structuredRepairSteps);

  const requiredTools = Array.isArray(cause.requiredTools) ? uniqueStrings(cause.requiredTools) : [];
  const requiredParts = Array.isArray(cause.requiredParts) ? uniqueStrings(cause.requiredParts) : [];
  const planNotes = normalisePlanNotes(cause.planNotes || cause.notes || cause.additionalNotes);

  const estimatedTime =
    Number.isFinite(cause.estimatedTime) && cause.estimatedTime > 0
      ? Math.round(cause.estimatedTime)
      : 15;

  const id = cause.id || `cause-${fallbackIndex}`;

  const failureModes = Array.isArray(cause.failureModes)
    ? uniqueStrings(cause.failureModes)
    : typeof cause.failureMode === 'string'
    ? [cause.failureMode.trim()]
    : [];

  const details =
    Array.isArray(cause.details) && cause.details.length > 0
      ? uniqueStrings(cause.details)
      : description
      ? [description]
      : [];

  const sourceIds = Array.isArray(cause.sourceIds) ? uniqueStrings(cause.sourceIds) : [id];
  const referenceImages = Array.isArray(cause.referenceImages)
    ? cause.referenceImages.map((image: any) => ({
        ...image,
        fileUrl: ensureAbsoluteUrl(image?.fileUrl)
      }))
    : [];
  const referenceImageBlocks = Array.isArray(cause.referenceImageBlocks)
    ? cause.referenceImageBlocks.map((block: any) => ({
        section: normalizeReferenceImageBlockSection(block?.section),
        itemId: typeof block?.itemId === 'string' && block.itemId.trim() ? block.itemId.trim() : undefined,
        text: typeof block?.text === 'string' ? block.text : '',
        imageIds: Array.isArray(block?.imageIds) ? block.imageIds.map(String) : [],
        sourceNodeIds: Array.isArray(block?.sourceNodeIds)
          ? block.sourceNodeIds.map(String).filter(Boolean)
          : [],
        anchors: Array.isArray(block?.anchors)
          ? block.anchors
              .map((anchor: any) => ({
                text: typeof anchor?.text === 'string' ? anchor.text : '',
                imageIds: Array.isArray(anchor?.imageIds) ? anchor.imageIds.map(String) : []
              }))
              .filter((anchor: any) => anchor.text && anchor.imageIds.length > 0)
          : []
      }))
    : [];

  return {
    id,
    description,
    shortTitle,
    probability: cause.probability || 'MEDIUM',
    testProcedure,
    testingSteps,
    repairSteps,
    planNotes,
    structuredTestingSteps,
    structuredRepairSteps,
    requiredTools,
    requiredParts,
    estimatedTime,
    status: cause.status || 'UNTESTED',
    eliminates: Array.isArray(cause.eliminates) ? uniqueStrings(cause.eliminates) : [],
    documentReference: cause.documentReference,
    failureMode: typeof cause.failureMode === 'string' ? cause.failureMode.trim() : description,
    failureModes,
    details,
    sourceIds,
    component: cause.component,
    summary: summary || description,
    sourceSnippets: toSourceSnippets(cause.sourceSnippets || cause.snippets),
    actionPlanCitations:
      cause.actionPlanCitations && typeof cause.actionPlanCitations === 'object'
        ? cause.actionPlanCitations
        : undefined,
    referenceImages,
    referenceImageBlocks,
    diagnosticReasoning: cause.diagnosticReasoning,
    techSuggested: Boolean(cause.techSuggested),
    secondPass: Boolean(cause.secondPass || cause.causeOrigin === 'SECOND_PASS'),
    causeOrigin:
      cause.causeOrigin === 'SECOND_PASS' || cause.causeOrigin === 'TECH_SUGGESTED'
        ? cause.causeOrigin
        : undefined,
    unverifiedPlaceholder: Boolean(cause.unverifiedPlaceholder),
    manualSuggestion:
      typeof cause.manualSuggestion === 'string' ? cause.manualSuggestion.trim() : undefined,
    knowledgeGapReason:
      typeof cause.knowledgeGapReason === 'string' ? cause.knowledgeGapReason.trim() : undefined,
    planUnavailableReason:
      typeof cause.planUnavailableReason === 'string' ? cause.planUnavailableReason.trim() : undefined,
    evidenceSource:
      typeof cause.evidenceSource === 'string' ? cause.evidenceSource.trim() : undefined,
    supplementalEvidenceKey:
      typeof cause.supplementalEvidenceKey === 'string'
        ? cause.supplementalEvidenceKey.trim()
        : undefined,
    focusedRetrievalQuery:
      typeof cause.focusedRetrievalQuery === 'string'
        ? cause.focusedRetrievalQuery.trim()
        : undefined,
    domain: cause.domain,
  };
};

  const probabilityOrder = { HIGH: 1, MEDIUM: 2, LOW: 3 } as const;
  const standardToolPatterns = [
    /multimeter/i,
    /\bdmm\b/i,
    /digital\s+multimeter/i,
    /hand\s*tool/i,
    /screwdriver/i,
    /esd\s*wrist\s*strap/i,
    /esd\s*strap/i,
    /insulated\s*pliers/i,
    /test\s*lead/i
  ];

  const uniqueStrings = (list: string[]) =>
    Array.from(
      new Set(
        list
          .filter(item => typeof item === 'string')
          .map(item => item.trim())
          .filter(Boolean)
      )
    );

  const filterSpecialTools = (tools: string[]) =>
    uniqueStrings(
      tools.filter(tool => !standardToolPatterns.some(pattern => pattern.test(tool)))
    );

  const normaliseStrings = (values: any): string[] => {
    if (!values) return [];
    if (Array.isArray(values)) {
      return uniqueStrings(
        values
          .map(value => (typeof value === 'string' ? value : String(value || '')))
          .map(value => value.trim())
          .filter(Boolean)
      );
    }
    if (typeof values === 'string') {
      return uniqueStrings(
        values
          .split(/[\n;]+/)
          .map(item => item.trim())
          .filter(Boolean)
      );
    }
    return [];
  };

  const normalisePlanNotes = (values: any): string[] => {
    if (!values) return [];
    const input = Array.isArray(values) ? values : [values];
    return uniqueStrings(
      input
        .flatMap(value => String(value ?? '').split(/\r?\n+/))
        .map(line => line.replace(/^[\s•*-]+/, '').replace(/\s+/g, ' ').trim())
        .filter(Boolean)
    );
  };

  const mergeSourceSnippets = (
    primary: SourceSnippet[] = [],
    secondary: SourceSnippet[] = []
  ): SourceSnippet[] => {
    const map = new Map<string, SourceSnippet>();
    const insert = (snippet?: SourceSnippet) => {
      if (!snippet || !snippet.recordId) return;
      if (map.has(snippet.recordId)) return;
      map.set(snippet.recordId, {
        ...snippet,
        highlights: [...(snippet.highlights || [])],
        procedures: [...(snippet.procedures || [])],
        tools: [...(snippet.tools || [])],
        parts: [...(snippet.parts || [])],
        references: snippet.references ? [...snippet.references] : undefined,
        warnings: snippet.warnings ? [...snippet.warnings] : undefined,
        indicators: snippet.indicators ? [...snippet.indicators] : undefined
      });
    };
    primary.forEach(insert);
    secondary.forEach(insert);
    return Array.from(map.values());
  };

  const toSourceSnippets = (snippets: any): SourceSnippet[] => {
    if (!Array.isArray(snippets)) return [];
    const mapped = snippets
      .map((snippet: any) => {
        if (!snippet || typeof snippet !== 'object') return undefined;
        const recordId =
          typeof snippet.recordId === 'string'
            ? snippet.recordId
            : typeof snippet.id === 'string'
            ? snippet.id
            : '';
        if (!recordId) return undefined;
        return {
          recordId,
          domain: typeof snippet.domain === 'string' ? snippet.domain : undefined,
          title: typeof snippet.title === 'string' ? snippet.title : undefined,
          summary: typeof snippet.summary === 'string' ? snippet.summary : undefined,
          highlights: normaliseStrings(snippet.highlights),
          procedures: normaliseStrings(snippet.procedures),
          tools: normaliseStrings(snippet.tools),
          parts: normaliseStrings(snippet.parts),
          references: normaliseStrings(snippet.references),
          warnings: normaliseStrings(snippet.warnings),
          indicators: normaliseStrings(snippet.indicators),
          sourcePath: typeof snippet.sourcePath === 'string' ? snippet.sourcePath : undefined
        } as SourceSnippet;
      })
      .filter(Boolean) as SourceSnippet[];
    return mergeSourceSnippets(mapped, []);
  };




  useEffect(() => {
    if (caseId) {
      loadCase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  useEffect(() => {
    const loadModel = async () => {
      try {
        if (window?.demo?.getAIModel) {
          const model = await window.demo.getAIModel();
          setAiModel(model);
        } else if (window?.demo?.getAppInfo) {
          const info = await window.demo.getAppInfo();
          if (info?.aiModel) {
            setAiModel(info.aiModel);
          }
        }
      } catch (err) {
        console.error('Failed to load AI model (Electron):', err);
      }
    };
    loadModel();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (progressEventSourceRef.current) {
        progressEventSourceRef.current.close();
        progressEventSourceRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCase = async () => {
    try {
      setLoading(true);
      const data = await caseApi.getTsfadSummary(caseId!);
      if (data) {
        setCaseData(data);
        setProblemDescription(data.problemDescription);
        
        // Extract symptoms from customer inputs
        const customerSymptoms: string[] = [];
        
        // Check for symptoms in the symptoms array
        if (data.symptoms && Array.isArray(data.symptoms)) {
          data.symptoms.forEach((s: any) => {
            if (s.type !== 'CUSTOM_MODEL' && (s.description || s.answer)) {
              customerSymptoms.push(s.description || s.answer);
            }
          });
        }
        
        // Check for customer inputs
        if (data.customerInputs && Array.isArray(data.customerInputs)) {
          data.customerInputs.forEach((input: any) => {
            if (input.answer) {
              customerSymptoms.push(`${input.question}: ${input.answer}`);
            }
          });
        }
        
        setSymptoms(customerSymptoms);
        setModelId(data.device?.modelId || data.device?.model?.id || null);

        if (Array.isArray(data.tsfadAnalyses) && data.tsfadAnalyses.length > 0) {
          const latest = data.tsfadAnalyses[0];
          try {
            applyAnalysisResult({
              analysisId: latest.id,
              modelId: latest.modelId,
              possibleCauses: latest.possibleCauses || [],
              testPlan: latest.testPlan || {},
              analysisStatus: latest.analysisStatus,
              analysisRecord: latest
            });
            setLatestAnalysis(latest);
            setAnalysisStatus(latest.analysisStatus === 'ACCEPTED' ? 'ACCEPTED' : 'COMPLETED');
          } catch (err) {
            console.error('Failed to apply stored Troubleshooting From a Distance (TSFAD)', err);
          }
        } else {
          setAnalysisStatus('NONE');
          setLatestAnalysis(null);
        }
      }
    } catch (error) {
      console.error('Failed to load case:', error);
      setError('Failed to load case data');
    } finally {
      setLoading(false);
    }
  };

// eslint-disable-next-line react-hooks/exhaustive-deps
const applyAnalysisResult = (result: any) => {
  if (!result) {
    throw new Error('No analysis result received');
  }

  setAnalysisId(result.analysisId || null);
  setModelId(result.modelId || null);
  const status = (result.analysisStatus || result.analysisRecord?.analysisStatus || 'COMPLETED') as 'COMPLETED' | 'ACCEPTED';
  setAnalysisStatus(status === 'ACCEPTED' ? 'ACCEPTED' : 'COMPLETED');
  const nextAnalysisRecord = (result.analysisRecord || null) as TSFADAnalysisRecord | null;
  setLatestAnalysis(nextAnalysisRecord);
  const nextCitationReview =
    result?.citationIntegrityReview && Array.isArray(result.citationIntegrityReview.issues)
      ? result.citationIntegrityReview
      : null;
  setCitationIntegrityReview(nextCitationReview);
  setShowCitationIntegrityModal(Boolean(nextCitationReview?.issues?.length));
  if (nextAnalysisRecord && !nextAnalysisRecord.isWorkspace) {
    setCaseData(prev =>
      prev
        ? {
            ...prev,
            tsfadAnalyses: [
              nextAnalysisRecord,
              ...(prev.tsfadAnalyses?.filter(analysis => analysis.id !== nextAnalysisRecord.id) || [])
            ]
          }
        : prev
    );
  }
  setAcceptingAnalysis(false);

  const rawCauses: PossibleCause[] = (Array.isArray(result.possibleCauses) ? result.possibleCauses : []).map(
    (cause: any, index: number) => transformCause(cause, index)
  );

  const canonicalKey = (cause: PossibleCause) => {
    const basis = cause.shortTitle || cause.description || cause.id;
    return basis ? basis.trim().toLowerCase() : cause.id;
  };

  const grouped = new Map<string, PossibleCause>();
  rawCauses.forEach(cause => {
    const key = canonicalKey(cause);
    if (!grouped.has(key)) {
      grouped.set(key, { ...cause });
      return;
    }

    const existing = grouped.get(key)!;
    existing.sourceIds = uniqueStrings([...(existing.sourceIds || []), ...(cause.sourceIds || [])]);
    existing.details = uniqueStrings([...(existing.details || []), ...(cause.details || []), cause.description]);
    existing.summary = existing.details[0] || existing.summary;
    existing.requiredTools = uniqueStrings([...(existing.requiredTools || []), ...cause.requiredTools]);
    existing.requiredParts = uniqueStrings([...(existing.requiredParts || []), ...cause.requiredParts]);
    existing.planNotes = uniqueStrings([...(existing.planNotes || []), ...(cause.planNotes || [])]);
    existing.sourceSnippets = mergeSourceSnippets(existing.sourceSnippets, cause.sourceSnippets);
    existing.eliminates = uniqueStrings([...(existing.eliminates || []), ...cause.eliminates]);

    const mergedStructuredTesting = normaliseStepNodes([
      ...(existing.structuredTestingSteps || []),
      ...(cause.structuredTestingSteps || [])
    ]);
    existing.structuredTestingSteps = mergedStructuredTesting;
    const mergedTestingSteps = flattenStepNodes(mergedStructuredTesting);
    existing.testingSteps = mergedTestingSteps;
    if (!existing.testProcedure || existing.testProcedure.length === 0) {
      existing.testProcedure = cause.testProcedure;
    }
    if (mergedTestingSteps.length > 0) {
      existing.testProcedure = mergedTestingSteps.join('\n');
    }

    const mergedStructuredRepair = normaliseStepNodes([
      ...(existing.structuredRepairSteps || []),
      ...(cause.structuredRepairSteps || [])
    ]);
    existing.structuredRepairSteps = mergedStructuredRepair;
    existing.repairSteps = flattenStepNodes(mergedStructuredRepair);

    existing.failureModes = uniqueStrings([...(existing.failureModes || []), ...(cause.failureModes || [])]);
    existing.techSuggested = existing.techSuggested || cause.techSuggested;
    existing.secondPass = existing.secondPass || cause.secondPass;
    existing.causeOrigin = existing.techSuggested
      ? 'TECH_SUGGESTED'
      : existing.secondPass
      ? 'SECOND_PASS'
      : existing.causeOrigin;
    if (!existing.domain && cause.domain) {
      existing.domain = cause.domain;
    }
    if (probabilityOrder[cause.probability] < probabilityOrder[existing.probability]) {
      existing.probability = cause.probability;
    }
    existing.estimatedTime = Math.max(existing.estimatedTime, cause.estimatedTime);
  });

  const aggregated = Array.from(grouped.values());
  const idToGroupId = new Map<string, string>();

  aggregated.forEach((cause, index) => {
    const groupId = cause.sourceIds && cause.sourceIds.length > 0 ? cause.sourceIds[0] : `cause-${index}`;
    cause.id = groupId;
    idToGroupId.set(groupId, groupId);
    cause.sourceIds?.forEach(src => {
      if (src) idToGroupId.set(src, groupId);
    });

    const detailList =
      cause.details && cause.details.length > 0
        ? cause.details
        : cause.description
        ? [cause.description]
        : [];

    cause.details = detailList;
    cause.description = detailList[0] || cause.description;
    cause.summary = detailList[0] || cause.summary;
    cause.requiredTools = filterSpecialTools(cause.requiredTools);
    cause.requiredParts = uniqueStrings(cause.requiredParts);
    cause.structuredTestingSteps = normaliseStepNodes(
      cause.structuredTestingSteps && cause.structuredTestingSteps.length > 0
        ? cause.structuredTestingSteps
        : cause.testingSteps
    );
    cause.testingSteps = flattenStepNodes(cause.structuredTestingSteps);
    cause.structuredRepairSteps = normaliseStepNodes(
      cause.structuredRepairSteps && cause.structuredRepairSteps.length > 0
        ? cause.structuredRepairSteps
        : cause.repairSteps
    );
    cause.repairSteps = flattenStepNodes(cause.structuredRepairSteps);
    cause.planNotes = uniqueStrings(cause.planNotes || []);
    cause.sourceSnippets = mergeSourceSnippets(cause.sourceSnippets, []);

    if (cause.failureModes && cause.failureModes.length > 0) {
      cause.failureMode = uniqueStrings(cause.failureModes).join(' / ');
    }
  });

  aggregated.forEach(cause => {
    cause.eliminates = uniqueStrings(
      (cause.eliminates || [])
        .map(sourceId => {
          const target = idToGroupId.get(sourceId);
          return target && target !== cause.id ? target : null;
        })
        .filter(Boolean) as string[]
    );
  });

  aggregated.sort((a, b) => probabilityOrder[a.probability] - probabilityOrder[b.probability]);

  const baselineMap = new Map<string, PossibleCause>();
  aggregated.forEach(cause => baselineMap.set(cause.id, cloneCause(cause)));
  originalCausesRef.current = baselineMap;

  setPossibleCauses(aggregated);
  setError(null);
  setErrorDetails(null);
  setAnalysisFailureCode(null);
  setAnalysisFailureFeedbackContext(null);
  setShowKnowledgeCoverageModal(false);
  setKnowledgeCoverageActionError(null);
  setKnowledgeCoverageRetrySubmitting(false);
  setAnalysisComplete(true);
  setAnalysisStarted(false);
  setAnalysisProgress(100);
  setActiveStageMessage('Analysis complete!');
  setStageStates(prev => {
    const next = { ...prev };
    STAGE_SEQUENCE.forEach(stage => {
      const current = next[stage.id];
      if (!current || current.status !== 'complete') {
        next[stage.id] = {
          status: 'complete',
          message: current?.message || stage.defaultMessage,
          durationMs: current?.durationMs
        };
      }
    });
    return next;
  });
};

const handleAcceptAnalysis = async () => {
  if (!analysisId || !caseData?.id) {
    return;
  }

  try {
    setAcceptingAnalysis(true);
    const response = await tsfadApi.acceptAnalysis(analysisId, {
      caseId: caseData.id,
      possibleCauses,
      clientCaseModel: {
        implementedKitSelections
      }
    });

    const updated = response?.analysis || null;
    if (updated) {
      const receiptCauseCount = Number(response?.saveReceipt?.causeCount);
      if (Number.isFinite(receiptCauseCount) && receiptCauseCount !== possibleCauses.length) {
        throw new Error(
          `Save verification failed: expected ${possibleCauses.length} causes but the server confirmed ${receiptCauseCount}.`
        );
      }
      const updatedForDisplay = {
        ...updated,
        // The submitted on-screen plan is the authoritative save payload. Do not replace it
        // with an immediate object-storage reread that may still expose the previous version.
        possibleCauses
      };
      setAnalysisId(updated.id || null);
      const nextCauses = possibleCauses;
      setPossibleCauses(nextCauses);
      setSelectedCause(prev =>
        prev ? nextCauses.find((cause: PossibleCause) => cause.id === prev.id) || null : null
      );
      setLatestAnalysis(updatedForDisplay);
      const updatedImplementedKitSelections =
        updated?.caseModel && typeof updated.caseModel === 'object'
          ? (updated.caseModel as Record<string, any>).implementedKitSelections
          : null;
      setImplementedKitSelections(
        Array.isArray(updatedImplementedKitSelections)
          ? updatedImplementedKitSelections.filter(entry => entry?.kitId)
          : implementedKitSelections
      );
      setAnalysisStatus(updated.analysisStatus === 'ACCEPTED' ? 'ACCEPTED' : 'COMPLETED');
      setCaseData(prev =>
        prev
          ? {
              ...prev,
              tsfadAnalyses: [
                updatedForDisplay,
                ...(prev.tsfadAnalyses?.filter(analysis => analysis.id !== updated.id) || [])
              ]
            }
          : prev
      );
    } else {
      setAnalysisStatus('ACCEPTED');
    }
    if (draftSaveTimeoutRef.current) {
      window.clearTimeout(draftSaveTimeoutRef.current);
      draftSaveTimeoutRef.current = null;
    }
    clearTSFADDraft(caseData.id);
    setDraftRestoredAt(null);
  } catch (err) {
    console.error('Failed to accept Troubleshooting From a Distance (TSFAD)', err);
    alert((err as any)?.message || 'Failed to save analysis. Please try again.');
  } finally {
    setAcceptingAnalysis(false);
  }
};

const startAnalysis = useCallback(async () => {

  if (analysisStarted || !caseData?.id) {

    return;

  }



  if (draftSaveTimeoutRef.current) {
    window.clearTimeout(draftSaveTimeoutRef.current);
    draftSaveTimeoutRef.current = null;
  }
  clearTSFADDraft(caseData.id);
  draftRestoreAppliedRef.current = false;
  setDraftRestoredAt(null);
  setImplementedKitSelections([]);
  implementedKitsHydratedAnalysisIdRef.current = null;

  closeProgressStream();

  setAnalysisStarted(true);

  setAnalysisComplete(false);

  setError(null);

  setErrorDetails(null);
  setAnalysisFailureCode(null);
  setAnalysisFailureFeedbackContext(null);
  setShowKnowledgeCoverageModal(false);
  setKnowledgeCoverageActionError(null);
  setKnowledgeCoverageRetrySubmitting(false);
  setShowAssignmentBlockedModal(false);
  setAssignmentBlockedDetails('');
  setShowAnalyzeRetryModal(false);
  setAnalyzeRetryFailureCode(null);
  setAnalyzeRetryOccurredAt(null);
  setAnalyzeRetryDetails(null);
  setCitationIntegrityReview(null);
  setShowCitationIntegrityModal(false);

  setAnalysisId(null);

  setModelId(caseData.device?.modelId || caseData.device?.model?.id || null);

  setPossibleCauses([]);

  originalCausesRef.current = new Map();

  setAnalysisStatus('NONE');

  setLatestAnalysis(null);

  setAcceptingAnalysis(false);
  setSavedQuotes([]);
  setSavedQuotesLoaded(false);
  setSavedQuotesLoading(false);
  setSavedQuotesLoadFailed(false);
  setQuoteModalOpen(false);
  setQuoteEditingQuoteId(null);
  setQuoteRevisionOfQuoteId(null);
  setQuoteDeleteId(null);
  setQuoteError(null);
  setQuoteTemplateLayout(null);
  setSelectedQuoteLineIds([]);
  setQuoteCustomerName('');
  setQuoteCustomerSignature('');

  setStageStates(() => ({

    ...createInitialStageState(),

    PROBING: { status: 'active', message: STAGE_MAP.PROBING.defaultMessage }

  }));

  setActiveStageMessage(STAGE_MAP.PROBING.defaultMessage);

  setAnalysisProgress(STAGE_MAP.PROBING.startProgress);



  try {

    const apiReady = await waitForHealth();

    if (!apiReady) {

      handleAnalysisFailure(503, 'Services are not ready. Please try again shortly.');

      return;

    }



    const base = await getBaseUrl();

    const token = getAuthToken();
    const streamUrl = token
      ? `${base}/api/tsfad/progress/${encodeURIComponent(caseData.id)}?token=${encodeURIComponent(token)}`
      : `${base}/api/tsfad/progress/${encodeURIComponent(caseData.id)}`;

    const eventSource = new EventSource(streamUrl);

    progressEventSourceRef.current = eventSource;



    eventSource.onmessage = event => {

      try {

        const payload = JSON.parse(event.data);

        const { phase, message, metadata = {} } = payload || {};

        const eventCaseId = metadata.caseId || metadata.case_id || null;

        if (!eventCaseId || eventCaseId !== caseData.id) {

          return;

        }



        if (phase === 'analysis-stage') {

          const stageId = (metadata.stage || metadata.phase) as AnalysisStageId | undefined;

          const rawStatus = (metadata.status || '').toString().toLowerCase();

          const stageMessage = metadata.message || message;

          const durationMs =

            typeof metadata.durationMs === 'number' && metadata.durationMs >= 0

              ? Number(metadata.durationMs)

              : undefined;



          if (stageId && STAGE_MAP[stageId]) {

            if (rawStatus === 'started') {

              updateStageState(stageId, 'active', stageMessage);

            } else if (rawStatus === 'completed' || rawStatus === 'complete') {

              updateStageState(stageId, 'complete', stageMessage, durationMs);

            } else if (rawStatus === 'error' || rawStatus === 'failed') {

              updateStageState(stageId, 'error', stageMessage, durationMs);

            }

          }

        }



        if (typeof metadata.progress === 'number') {

          setAnalysisProgress(prev => Math.max(prev, Math.min(100, Math.round(metadata.progress))));

        }

      } catch (err) {

        console.warn('Failed to parse progress event:', err);

      }

    };



    eventSource.onerror = () => {

      console.warn('Progress stream error');

      closeProgressStream();

      setActiveStageMessage('Lost connection to analysis progress feed');

    };



    const response = await fetch(`${base}/api/tsfad/analyze`, {

      method: 'POST',

      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify({

        caseId: caseData.id,

        deviceModel: caseData.device?.modelNumber,

        problemDescription,

        symptoms,

        customerInputs: caseData.customerInputs || []

      })

    });



    if (!response.ok) {

      const payload = await response.json().catch(() => null);

      const errorMessage = payload?.error || payload?.message || 'Analysis request failed';

      const details = payload?.details || payload?.detail || null;

      handleAnalysisFailure(response.status, errorMessage, details, payload);

      return;

    }



    const result = await response.json();

    applyAnalysisResult(result);

  } catch (error) {

    console.error('Failed to start analysis:', error);

    handleAnalysisFailure(500, 'Failed to start analysis', (error as any)?.message);

  }

}, [analysisStarted, applyAnalysisResult, caseData, handleAnalysisFailure, problemDescription, symptoms]);



useEffect(() => {

  if (autoStartRequested && caseData && !analysisStarted) {

    startAnalysis();

    setAutoStartRequested(false);

    navigate('.', { replace: true, state: undefined });

  }

}, [autoStartRequested, caseData, analysisStarted, startAnalysis, navigate]);



useEffect(() => {
  const params = new URLSearchParams(location.search);
  if (params.get('rerun')) {
    setAutoStartRequested(true);
    params.delete('rerun');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : ''
      },
      { replace: true }
    );
  }
}, [location.pathname, location.search, navigate]);

useEffect(() => {
  if (!analysisComplete || !quoteAnalysisId) {
    setSavedQuotes([]);
    setSavedQuotesLoaded(false);
    setSavedQuotesLoading(false);
    setSavedQuotesLoadFailed(false);
    return;
  }
}, [analysisComplete, quoteAnalysisId]);

useEffect(() => {
  if (!analysisComplete || !quoteAnalysisId || savedQuotesLoaded || savedQuotesLoading) {
    return;
  }
  loadSavedQuotes();
}, [analysisComplete, quoteAnalysisId, loadSavedQuotes, savedQuotesLoaded, savedQuotesLoading]);



const handleSuggestCause = async (options?: { suggestionText?: string }) => {
  const suggestionText = (options?.suggestionText ?? suggestion).trim();
  if (!suggestionText || !analysisId) return null;

  setSuggestionLoading(true);
  setSuggestionResult(null);
  let resultData: any = null;

  try {
    const base = await getBaseUrl();
    const response = await fetch(`${base}/api/tsfad/evaluate-cause`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        analysisId,
        suggestion: suggestionText,
        deviceModel: caseData?.device?.modelNumber,
        problemDescription,
        currentCauses: possibleCauses
      })
    });

    if (!response.ok) {
      const failure = await response.json().catch(() => null);
      resultData = {
        accepted: false,
        outcome: 'ERROR',
        originalSuggestion: suggestionText,
        reasoning: formatTsfadApiFailure(
          failure,
          'The suggested cause could not be evaluated.'
        ),
        reasoningSummary: formatTsfadApiFailure(
          failure,
          'The suggested cause could not be evaluated.'
        ),
        rejectionDetails: {
          primaryReason: failure?.code || 'EVALUATION_FAILED',
          explanation: formatTsfadApiFailure(
            failure,
            'The suggested cause could not be evaluated.'
          )
        }
      };
      setSuggestionResult(resultData);
      return resultData;
    }

    const result = await response.json();
    if (typeof result?.analysisId === 'string' && result.analysisId.trim()) {
      setAnalysisId(result.analysisId.trim());
    }
    if (result?.analysisRecord && typeof result.analysisRecord === 'object') {
      const nextAnalysisRecord = result.analysisRecord as TSFADAnalysisRecord;
      setLatestAnalysis(nextAnalysisRecord);
      setAnalysisStatus(nextAnalysisRecord.analysisStatus === 'ACCEPTED' ? 'ACCEPTED' : 'COMPLETED');
    }
    const transformedNewCause = result.newCause ? transformCause(result.newCause, possibleCauses.length) : undefined;
    resultData = {
      ...result,
      newCause: transformedNewCause,
      originalSuggestion: suggestionText
    };
    setSuggestionResult(resultData);

    if (result.accepted && transformedNewCause) {
      // Add the new cause to the list
      setPossibleCauses(prev => {
        const updated = [...prev, transformedNewCause];
        const probabilityOrder: Record<string, number> = { HIGH: 1, MEDIUM: 2, LOW: 3 };
        updated.sort((a, b) => probabilityOrder[a.probability] - probabilityOrder[b.probability]);
        return updated;
      });
      storeBaselineCause(transformedNewCause);
      setSuggestion('');
      markAnalysisDirty();
    }
  } catch (error) {
    console.error('Failed to evaluate suggestion:', error);
    resultData = {
      accepted: false,
      outcome: 'ERROR',
      originalSuggestion: suggestionText,
      reasoning: error instanceof Error ? error.message : 'Failed to evaluate suggestion. Please try again.',
      rejectionDetails: {
        primaryReason: 'EVALUATION_FAILED',
        explanation: error instanceof Error ? error.message : 'Failed to evaluate suggestion. Please try again.'
      }
    };
    setSuggestionResult(resultData);
  } finally {
    setSuggestionLoading(false);
  }

  return resultData;
};

const handleAddUnverifiedCause = async () => {
  const suggestionText = String(suggestionResult?.originalSuggestion || suggestion || '').trim();
  const evaluationId = String(suggestionResult?.unverifiedAddToken || '').trim();
  if (!suggestionText || !evaluationId || !analysisId) return null;

  setSuggestionLoading(true);
  try {
    const base = await getBaseUrl();
    const response = await fetch(`${base}/api/tsfad/add-unverified-cause`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        analysisId,
        evaluationId,
        suggestion: suggestionText,
        currentCauses: possibleCauses
      })
    });
    if (!response.ok) {
      const failure = await response.json().catch(() => null);
      const message = formatTsfadApiFailure(
        failure,
        'The unverified cause could not be added.'
      );
      setSuggestionResult((previous: any) => ({
        ...previous,
        accepted: false,
        outcome: 'ERROR',
        reasoning: message,
        reasoningSummary: message,
        rejectionDetails: {
          primaryReason: failure?.code || 'UNVERIFIED_CAUSE_ADD_FAILED',
          explanation: message
        }
      }));
      return null;
    }

    const result = await response.json();
    if (typeof result?.analysisId === 'string' && result.analysisId.trim()) {
      setAnalysisId(result.analysisId.trim());
    }
    if (result?.analysisRecord && typeof result.analysisRecord === 'object') {
      const nextAnalysisRecord = result.analysisRecord as TSFADAnalysisRecord;
      setLatestAnalysis(nextAnalysisRecord);
      setAnalysisStatus(nextAnalysisRecord.analysisStatus === 'ACCEPTED' ? 'ACCEPTED' : 'COMPLETED');
    }
    const transformedNewCause = result.newCause
      ? transformCause(result.newCause, possibleCauses.length)
      : undefined;
    const resultData = {
      ...result,
      newCause: transformedNewCause,
      originalSuggestion: suggestionText
    };
    setSuggestionResult(resultData);

    if (result.accepted && transformedNewCause) {
      setPossibleCauses(prev => [...prev, transformedNewCause]);
      storeBaselineCause(transformedNewCause);
      setSuggestion('');
      markAnalysisDirty();
    }
    return resultData;
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'The unverified cause could not be added.';
    setSuggestionResult((previous: any) => ({
      ...previous,
      accepted: false,
      outcome: 'ERROR',
      reasoning: message,
      reasoningSummary: message,
      rejectionDetails: {
        primaryReason: 'UNVERIFIED_CAUSE_ADD_FAILED',
        explanation: message
      }
    }));
    return null;
  } finally {
    setSuggestionLoading(false);
  }
};

const handleCheckMissedCauses = async () => {
  if (!analysisId) {
    setMissedCausesError('Save or complete the TSFAD analysis before checking for additional causes.');
    setShowMissedCausesModal(true);
    return;
  }

  setShowMissedCausesModal(true);
  setMissedCausesLoading(true);
  setMissedCausesResult(null);
  setMissedCausesError(null);
  setMissedCausesWorkflowError(null);
  setMissedCausesOptimizationComplete(false);
  setSelectedMissedCauseIds([]);
  setMissedCauseBatchResults([]);
  setProcessingMissedCauseId(null);
  try {
    const base = await getBaseUrl();
    const response = await fetch(`${base}/api/tsfad/check-missed-causes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        analysisId,
        deviceModel: caseData?.device?.modelNumber,
        problemDescription,
        currentCauses: possibleCauses
      })
    });
    if (!response.ok) {
      const failure = await response.json().catch(() => null);
      throw new Error(
        formatTsfadApiFailure(failure, 'The additional-cause check could not complete.')
      );
    }
    const result = await response.json();
    setMissedCausesResult({
      outcome: result?.outcome === 'CANDIDATES_FOUND' ? 'CANDIDATES_FOUND' : 'NO_CANDIDATES',
      summary:
        String(result?.summary || '').trim() ||
        (Array.isArray(result?.candidates) && result.candidates.length > 0
          ? 'Additional supported causes were found for review.'
          : 'No additional supported causes were identified.'),
      candidates: Array.isArray(result?.candidates) ? result.candidates : [],
      validationIssues: Array.isArray(result?.validationIssues) ? result.validationIssues : []
    });
  } catch (error) {
    console.error('Failed to check for missed causes:', error);
    setMissedCausesError(
      error instanceof Error ? error.message : 'The additional-cause check could not complete.'
    );
  } finally {
    setMissedCausesLoading(false);
  }
};

const toggleMissedCauseSelection = (candidateId: string) => {
  setSelectedMissedCauseIds(previous =>
    previous.includes(candidateId)
      ? previous.filter(id => id !== candidateId)
      : [...previous, candidateId]
  );
};

const selectAllMissedCauses = () => {
  const completedIds = new Set(
    missedCauseBatchResults
      .filter(item => item.outcome !== 'FAILED')
      .map(item => item.candidateId)
  );
  setSelectedMissedCauseIds(
    (missedCausesResult?.candidates || [])
      .map(candidate => candidate.id)
      .filter(candidateId => !completedIds.has(candidateId))
  );
};

const handleAddSelectedMissedCauses = async () => {
  if (!analysisId || missedCausesProcessing) return;
  const completedIds = new Set(
    missedCauseBatchResults
      .filter(item => item.outcome !== 'FAILED')
      .map(item => item.candidateId)
  );
  const selectedCandidates = (missedCausesResult?.candidates || []).filter(
    candidate => selectedMissedCauseIds.includes(candidate.id) && !completedIds.has(candidate.id)
  );
  if (selectedCandidates.length === 0) return;

  setMissedCausesProcessing(true);
  setMissedCausesError(null);
  setMissedCausesWorkflowError(null);
  setMissedCausesOptimizationComplete(false);
  let workingAnalysisId = analysisId;
  let workingCauses = [...possibleCauses];
  let latestReturnedAnalysis: TSFADAnalysisRecord | null = null;
  let addedAnyCause = false;

  try {
    const base = await getBaseUrl();
    for (const candidate of selectedCandidates) {
      setProcessingMissedCauseId(candidate.id);
      let batchResult: MissedCauseBatchResult;
      try {
        const response = await fetch(`${base}/api/tsfad/evaluate-cause`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            analysisId: workingAnalysisId,
            suggestion: candidate.shortTitle,
            causeOrigin: 'SECOND_PASS',
            suggestedProbability: candidate.probability,
            deviceModel: caseData?.device?.modelNumber,
            problemDescription,
            currentCauses: workingCauses
          })
        });
        const result = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(
            formatTsfadApiFailure(result, 'The cause could not be evaluated and added.')
          );
        }

        if (typeof result?.analysisId === 'string' && result.analysisId.trim()) {
          workingAnalysisId = result.analysisId.trim();
        }
        if (result?.analysisRecord && typeof result.analysisRecord === 'object') {
          latestReturnedAnalysis = result.analysisRecord as TSFADAnalysisRecord;
        }

        if (result?.accepted && result?.newCause) {
          const transformedCause = transformCause(result.newCause, workingCauses.length);
          workingCauses = [...workingCauses, transformedCause];
          storeBaselineCause(transformedCause);
          addedAnyCause = true;
          batchResult = {
            candidateId: candidate.id,
            shortTitle: candidate.shortTitle,
            outcome: 'ADDED',
            message: 'Added to Possible Causes and an action plan was created.'
          };
        } else if (result?.outcome === 'DUPLICATE') {
          batchResult = {
            candidateId: candidate.id,
            shortTitle: candidate.shortTitle,
            outcome: 'DUPLICATE',
            message:
              String(result?.reasoningSummary || result?.reasoning || '').trim() ||
              'Already covered by a cause in the list.'
          };
        } else {
          batchResult = {
            candidateId: candidate.id,
            shortTitle: candidate.shortTitle,
            outcome: 'NOT_ADDED',
            message:
              String(result?.reasoningSummary || result?.reasoning || '').trim() ||
              'This cause was not added.'
          };
        }
      } catch (error) {
        console.error('Failed to add missed-cause candidate:', candidate.shortTitle, error);
        batchResult = {
          candidateId: candidate.id,
          shortTitle: candidate.shortTitle,
          outcome: 'FAILED',
          message:
            error instanceof Error ? error.message : 'The cause could not be added.'
        };
      }
      setMissedCauseBatchResults(previous => [
        ...previous.filter(item => item.candidateId !== candidate.id),
        batchResult
      ]);
    }

    if (workingAnalysisId !== analysisId) {
      setAnalysisId(workingAnalysisId);
    }
    if (addedAnyCause) {
      setMissedCausesOptimizing(true);
      try {
        const optimization = await tsfadApi.optimizePlan(workingAnalysisId);
        if (!Array.isArray(optimization?.possibleCauses)) {
          throw new Error('The optimized cause workflow was not returned.');
        }
        workingCauses = optimization.possibleCauses.map((cause: any, index: number) =>
          transformCause(cause, index)
        );
        workingCauses.forEach(storeBaselineCause);
        if (optimization?.analysisRecord && typeof optimization.analysisRecord === 'object') {
          latestReturnedAnalysis = optimization.analysisRecord as TSFADAnalysisRecord;
        }
        setMissedCausesOptimizationComplete(true);
      } catch (error) {
        console.error('Failed to optimize the completed missed-cause workflow:', error);
        setMissedCausesWorkflowError(
          `The selected causes and action plans were added, but the final workflow optimization failed. ${
            error instanceof Error ? error.message : 'Please retry the workflow optimization.'
          }`
        );
      } finally {
        setMissedCausesOptimizing(false);
      }
      setPossibleCauses(workingCauses);
      markAnalysisDirty();
    }
    if (latestReturnedAnalysis) {
      setLatestAnalysis(latestReturnedAnalysis);
      setAnalysisStatus(
        latestReturnedAnalysis.analysisStatus === 'ACCEPTED' ? 'ACCEPTED' : 'COMPLETED'
      );
    }
  } finally {
    setSelectedMissedCauseIds([]);
    setProcessingMissedCauseId(null);
    setMissedCausesProcessing(false);
  }
};

const submitDocumentationFeedback = async () => {
    if (!feedbackForm.title || !feedbackForm.description) {
      alert('Please provide a title and description for the feedback');
      return;
    }

    const feedbackModelId = modelId || caseData?.device?.modelId || caseData?.device?.model?.id || null;
    if (!feedbackModelId) {
      alert('Model ID not available. Please ensure analysis has completed.');
      return;
    }

    try {
      await tsfadApi.submitDocumentationFeedback({
        modelId: feedbackModelId,
        caseId: caseData?.id,
        feedbackType: feedbackForm.feedbackType,
        severity: feedbackForm.severity,
        title: feedbackForm.title,
        description: feedbackForm.description,
        impact: feedbackForm.impact,
        metadata: {
          ...(feedbackMetadata || {}),
          causeId: feedbackCause?.id || null,
          causeDescription: feedbackCause?.description || feedbackSuggestionDescription || null,
          suggestionDescription: feedbackSuggestionDescription || feedbackCause?.description || null
        }
      });

      alert('Documentation feedback submitted successfully');
      closeFeedbackModal();
  } catch (error: any) {
    console.error('Failed to submit documentation feedback:', error);
    alert(error?.message || 'Failed to submit feedback');
  }
};

const openFeedbackModal = useCallback(({
    cause,
    defaults,
    relatedDescription,
    metadata
  }: {
    cause?: PossibleCause | null;
    defaults?: Partial<DocumentationFeedback>;
    relatedDescription?: string | null;
    metadata?: Record<string, unknown> | null;
  } = {}) => {
    setFeedbackForm({ ...createFeedbackDefaults(), ...(defaults || {}) });
    setFeedbackCause(cause ?? null);
    setFeedbackSuggestionDescription(relatedDescription ?? null);
    setFeedbackMetadata(metadata ?? null);
    setShowFeedbackModal(true);
  }, []);

const openKnowledgeCoverageFeedbackModal = useCallback(() => {
    const draft = buildKnowledgeCoverageFeedbackDraft();

    openFeedbackModal({
      defaults: draft.defaults,
      metadata: draft.metadata
    });
  }, [buildKnowledgeCoverageFeedbackDraft, openFeedbackModal]);

const handleKnowledgeCoverageFeedback = useCallback(() => {
    setShowKnowledgeCoverageModal(false);
    setKnowledgeCoverageActionError(null);
    openKnowledgeCoverageFeedbackModal();
  }, [openKnowledgeCoverageFeedbackModal]);

const handleKnowledgeCoverageEditAndRetry = useCallback(async () => {
    setKnowledgeCoverageActionError(null);
    setKnowledgeCoverageRetrySubmitting(true);
    try {
      await submitKnowledgeCoverageFeedback();
      setShowKnowledgeCoverageModal(false);
    } catch (error: any) {
      console.error('Failed to submit knowledge coverage feedback before retry:', error);
      setKnowledgeCoverageActionError(
        error?.message || 'Failed to log the knowledge coverage issue. Please send feedback before retrying.'
      );
    } finally {
      setKnowledgeCoverageRetrySubmitting(false);
    }
  }, [submitKnowledgeCoverageFeedback]);

const beginProcedureEdit = () => {
    if (!selectedCause) return;
    setIsInactivating(false);
    setInactivationReason('');
    loadCauseDrafts(selectedCause);
    setIsEditingProcedure(true);
  };

const cancelProcedureEdit = () => {
    if (selectedCause) {
      loadCauseDrafts(selectedCause);
    } else {
      loadCauseDrafts(null);
    }
    setProcedureEditNotes('');
    setIsEditingProcedure(false);
  };

const saveProcedureEdits = () => {
    if (!selectedCause) return;

    const note = procedureEditNotes.trim();
    if (!note) {
      alert('Please add a brief note explaining the edit.');
      return;
    }

    const verificationDraft = parseStepDraft(procedureDraft, selectedCause.structuredTestingSteps);
    if (verificationDraft.flattened.length === 0) {
      alert('Please provide at least one procedure step before saving.');
      return;
    }

    const repairDraftData = parseStepDraft(repairDraft, selectedCause.structuredRepairSteps);
    const tools = parseMultilineList(toolsDraft);
    const parts = parseMultilineList(partsDraft);
    const planNotes = parseMultilineList(notesDraft);

    const timestamp = new Date().toISOString();
    const updatedCause: PossibleCause = {
      ...selectedCause,
      testProcedure: verificationDraft.text,
      structuredTestingSteps: verificationDraft.structured,
      testingSteps: verificationDraft.flattened,
      structuredRepairSteps: repairDraftData.structured,
      repairSteps: repairDraftData.flattened,
      requiredTools: tools,
      requiredParts: parts,
      planNotes,
      procedureEditedByTech: true,
      procedureEditNotes: note,
      procedureEditedAt: timestamp,
      procedureRegeneratedAt: undefined,
      actionLog: [
        ...(selectedCause.actionLog || []),
        { timestamp, type: 'PROCEDURE_EDITED', notes: note }
      ]
    };

    setPossibleCauses(prev =>
      prev.map(cause => (cause.id === selectedCause.id ? updatedCause : cause))
    );

    setSelectedCause(null);
    setTestSteps(prev => [
      ...prev,
      {
        id: `step-${prev.length + 1}`,
        causeId: updatedCause.id,
        description: `Procedure edited for: ${updatedCause.shortTitle || updatedCause.description}`,
        completed: true,
        action: 'EDIT_PROCEDURE',
        notes: note,
        timestamp: new Date()
      }
    ]);

    storeBaselineCause(updatedCause);
    loadCauseDrafts(updatedCause);
    setProcedureEditNotes('');
    setIsEditingProcedure(false);
    markAnalysisDirty();
  };

const beginInactivation = () => {
    if (!selectedCause) return;
    setIsEditingProcedure(false);
    setProcedureEditNotes('');
    setIsInactivating(true);
    setInactivationReason('');
  };

const cancelInactivation = () => {
    setIsInactivating(false);
    setInactivationReason('');
  };

const confirmInactivateCause = () => {
    if (!selectedCause) return;
    const reason = inactivationReason.trim();
    if (!reason) {
      alert('Please add a brief reason before inactivating this cause.');
      return;
    }

    const timestamp = new Date().toISOString();
    const historyEntry = { reason, timestamp };
    const updatedCause: PossibleCause = {
      ...selectedCause,
      status: 'INACTIVE',
      inactiveReason: reason,
      inactiveAt: timestamp,
      inactiveHistory: [...(selectedCause.inactiveHistory || []), historyEntry],
      actionLog: [
        ...(selectedCause.actionLog || []),
        { timestamp, type: 'INACTIVATED', notes: reason }
      ]
    };

    setPossibleCauses(prev =>
      prev.map(cause => (cause.id === updatedCause.id ? updatedCause : cause))
    );

    setSelectedCause(updatedCause);

    setTestSteps(prev => [
      ...prev,
      {
        id: `step-${prev.length + 1}`,
        causeId: updatedCause.id,
        description: `Inactivated: ${updatedCause.shortTitle || updatedCause.description}`,
        completed: true,
        action: 'INACTIVATE',
        notes: reason,
        timestamp: new Date()
      }
    ]);

    setIsInactivating(false);
    setInactivationReason('');
    markAnalysisDirty();
  };

const reactivateCause = (cause: PossibleCause) => {
    const timestamp = new Date().toISOString();
    setPossibleCauses(prev =>
      prev.map(existing => {
        if (existing.id !== cause.id) return existing;
        return {
          ...existing,
          status: 'UNTESTED',
          inactiveReason: undefined,
          inactiveAt: undefined,
          actionLog: [
            ...(existing.actionLog || []),
            { timestamp, type: 'REACTIVATED' }
          ]
        };
      })
    );

    setTestSteps(prev => [
      ...prev,
      {
        id: `step-${prev.length + 1}`,
        causeId: cause.id,
        description: `Reactivated: ${cause.shortTitle || cause.description}`,
        completed: true,
        action: 'REACTIVATE',
        timestamp: new Date()
      }
    ]);
    markAnalysisDirty();
  };

const handleReportIssueFromProcedure = () => {
    if (!selectedCause) return;
    const titleText = formatCauseTitle(selectedCause.description, selectedCause.shortTitle);
    openFeedbackModal({
      cause: selectedCause,
      defaults: {
        feedbackType: 'WRONG_PROCEDURE',
        severity: 'MEDIUM',
        title: titleText ? `Procedure issue: ${titleText}` : '',
        description: '',
        impact: ''
      },
      relatedDescription: selectedCause.description || titleText || null
    });
  };

const handleReactivateCurrentCause = () => {
    if (!selectedCause) return;
    reactivateCause(selectedCause);
    cancelInactivation();
    setIsEditingProcedure(false);
    setProcedureEditNotes('');setSelectedCause(null);
  };

const regenerateProcedure = async () => {
    if (!selectedCause || !analysisId) {
      alert('Unable to regenerate without an active cause and analysis.');
      return;
    }
    if (regeneratingCause) return;

    const baselineSnapshot = cloneCause(selectedCause);
    storeBaselineCause(baselineSnapshot);

    setRegeneratingCause(true);
    setIsEditingProcedure(false);
    setProcedureEditNotes('');
    setIsInactivating(false);
    setInactivationReason('');

    try {
      const base = await getBaseUrl();
      const response = await fetch(`${base}/api/tsfad/regenerate-cause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysisId,
          causeId: selectedCause.id,
          cause: baselineSnapshot,
          currentCauses: possibleCauses
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const message =
          errorData?.details ||
          errorData?.error ||
          'Failed to regenerate procedure. Please try again.';
        throw new Error(message);
      }

      const payload = await response.json();
      if (typeof payload?.analysisId === 'string' && payload.analysisId.trim()) {
        setAnalysisId(payload.analysisId.trim());
      }
      if (payload?.analysisRecord && typeof payload.analysisRecord === 'object') {
        const nextAnalysisRecord = payload.analysisRecord as TSFADAnalysisRecord;
        setLatestAnalysis(nextAnalysisRecord);
        setAnalysisStatus(nextAnalysisRecord.analysisStatus === 'ACCEPTED' ? 'ACCEPTED' : 'COMPLETED');
      }
      const regenerated = (payload?.cause || null) as PossibleCause | null;
      if (!regenerated) {
        throw new Error('Regeneration did not return updated procedure details.');
      }

      const timestamp = new Date().toISOString();
      const updatedCause: PossibleCause = {
        ...regenerated,
        procedureEditedByTech: false,
        procedureEditNotes: undefined,
        procedureEditedAt: undefined,
        procedureRegeneratedAt: timestamp,
        actionLog: [
          ...(regenerated.actionLog || []),
          { timestamp, type: 'REGENERATED' }
        ]
      };

      setPossibleCauses(prev =>
        prev.map(cause => (cause.id === updatedCause.id ? updatedCause : cause))
      );
      setSelectedCause(updatedCause);
    setTestSteps(prev => [
      ...prev,
      {
        id: `step-${prev.length + 1}`,
        causeId: updatedCause.id,
        description: `Procedure regenerated for: ${updatedCause.shortTitle || updatedCause.description}`,
        completed: true,
        action: 'REGENERATE',
        timestamp: new Date()
      }
    ]);
    loadCauseDrafts(updatedCause);
    markAnalysisDirty();
  } catch (error: any) {
      console.error('Failed to regenerate procedure:', error);
      alert(error?.message || 'Failed to regenerate procedure. Please try again.');
    } finally {
      setRegeneratingCause(false);
    }
  };

const restoreOriginalProcedure = () => {
    if (!selectedCause) return;

    const baseline = originalCausesRef.current.get(selectedCause.id);
    if (!baseline) {
      alert('Original procedure data is not available to restore.');
      return;
    }

    setRestoringCause(true);
    try {
      const timestamp = new Date().toISOString();
      const baselineClone = cloneCause(baseline);
      const updatedCause: PossibleCause = {
        ...baselineClone,
        id: selectedCause.id,
        status: selectedCause.status,
        techSuggested: selectedCause.techSuggested,
        secondPass: selectedCause.secondPass,
        causeOrigin: selectedCause.causeOrigin,
        inactiveReason: selectedCause.inactiveReason,
        inactiveAt: selectedCause.inactiveAt,
        inactiveHistory: selectedCause.inactiveHistory,
        actionLog: [
          ...(selectedCause.actionLog || []),
          { timestamp, type: 'RESTORED' }
        ]
      };

      setPossibleCauses(prev =>
        prev.map(cause => (cause.id === selectedCause.id ? updatedCause : cause))
      );
      setSelectedCause(updatedCause);
      loadCauseDrafts(updatedCause);
      setIsEditingProcedure(false);
      setProcedureEditNotes('');
      setIsInactivating(false);
      setInactivationReason('');
    setTestSteps(prev => [
      ...prev,
      {
        id: `step-${prev.length + 1}`,
        causeId: updatedCause.id,
        description: `Procedure restored for: ${updatedCause.shortTitle || updatedCause.description}`,
        completed: true,
        action: 'RESTORE',
        timestamp: new Date()
      }
    ]);
    markAnalysisDirty();
  } finally {
    setRestoringCause(false);
  }
};

  const hasActionPlanDetails = useCallback((cause: PossibleCause | null | undefined) => {
    if (!cause) return false;
    if (cause.unverifiedPlaceholder) return true;
    if (Array.isArray(cause.structuredTestingSteps) && cause.structuredTestingSteps.length > 0) return true;
    if (Array.isArray(cause.testingSteps) && cause.testingSteps.length > 0) return true;
    if (typeof cause.testProcedure === 'string' && cause.testProcedure.trim().length > 0) return true;
    if (Array.isArray(cause.structuredRepairSteps) && cause.structuredRepairSteps.length > 0) return true;
    if (Array.isArray(cause.repairSteps) && cause.repairSteps.length > 0) return true;
    if (Array.isArray(cause.requiredTools) && cause.requiredTools.length > 0) return true;
    if (Array.isArray(cause.requiredParts) && cause.requiredParts.length > 0) return true;
    if (Array.isArray(cause.planNotes) && cause.planNotes.length > 0) return true;
    return false;
  }, []);

  const hydrateCauseDetails = useCallback(
    async (cause: PossibleCause): Promise<PossibleCause> => {
      if (hasActionPlanDetails(cause)) return cause;
      if (!analysisId) return cause;

      try {
        setCauseDetailLoadingId(cause.id);
        const payload = await tsfadApi.getCauseDetails(analysisId, cause.id);
        const raw = payload?.cause;
        if (!raw) return cause;

        const idx = possibleCauses.findIndex(item => item.id === cause.id);
        const transformed = transformCause(raw, idx >= 0 ? idx : 0);
        const merged: PossibleCause = {
          ...transformed,
          id: cause.id,
          status: cause.status,
          techSuggested: cause.techSuggested || transformed.techSuggested,
          secondPass: cause.secondPass || transformed.secondPass,
          causeOrigin: cause.causeOrigin || transformed.causeOrigin,
          inactiveReason: cause.inactiveReason || transformed.inactiveReason,
          inactiveAt: cause.inactiveAt || transformed.inactiveAt,
          inactiveHistory: cause.inactiveHistory || transformed.inactiveHistory,
          actionLog: cause.actionLog || transformed.actionLog
        };

        setPossibleCauses(prev => prev.map(item => (item.id === cause.id ? merged : item)));
        return merged;
      } catch (error) {
        console.error('Failed to hydrate cause details', error);
        return cause;
      } finally {
        setCauseDetailLoadingId(null);
      }
    },
    [analysisId, hasActionPlanDetails, possibleCauses, transformCause]
  );

  const performTest = useCallback(async (cause: PossibleCause) => {
    if (causeDetailLoadingId === cause.id) return;
    const resolved = await hydrateCauseDetails(cause);
    setSelectedCause(resolved);
    setShowTestModal(true);
    setTestNotes('');
  }, [causeDetailLoadingId, hydrateCauseDetails]);

  const getRemainingCauses = () => {
    return possibleCauses.filter(c => c.status === 'UNTESTED');
  };

  const getFailedCauses = () => {
    return possibleCauses.filter(c => c.status === 'FAILED');
  };

  const selectedVerificationSteps = React.useMemo<StepNode[]>(() => {
    if (!selectedCause) return [];
    if (selectedCause.structuredTestingSteps && selectedCause.structuredTestingSteps.length > 0) {
      return normaliseStepNodes(selectedCause.structuredTestingSteps);
    }
    if (selectedCause.testingSteps && selectedCause.testingSteps.length > 0) {
      return normaliseStepNodes(selectedCause.testingSteps);
    }
    if (selectedCause.testProcedure) {
      return normaliseStepNodes(selectedCause.testProcedure.split(/[\n]+/));
    }
    return [];
  }, [normaliseStepNodes, selectedCause]);

  const selectedRepairSteps = React.useMemo<StepNode[]>(() => {
    if (!selectedCause) return [];
    if (selectedCause.structuredRepairSteps && selectedCause.structuredRepairSteps.length > 0) {
      return normaliseStepNodes(selectedCause.structuredRepairSteps);
    }
    if (selectedCause.repairSteps && selectedCause.repairSteps.length > 0) {
      return normaliseStepNodes(selectedCause.repairSteps);
    }
    return [];
  }, [normaliseStepNodes, selectedCause]);

  const selectedPlanSteps = React.useMemo<PlanStepOption[]>(() => {
    const verification = flattenStepNodesForSelector(selectedVerificationSteps, 'VERIFICATION');
    const repair = flattenStepNodesForSelector(selectedRepairSteps, 'REPAIR');
    return [...verification, ...repair];
  }, [selectedRepairSteps, selectedVerificationSteps]);

  const selectedPlanStep = React.useMemo(
    () => selectedPlanSteps.find(step => step.key === selectedPlanStepKey) || null,
    [selectedPlanStepKey, selectedPlanSteps]
  );

  const askPlanAssistant = useCallback(
    async (mode: 'quick' | 'custom' = 'custom') => {
      if (!modelId) {
        setPlanAssistantError('Model context is unavailable. Re-run TSFAD analysis and try again.');
        return;
      }

      if (!selectedCause) {
        setPlanAssistantError('No cause is selected.');
        return;
      }

      const customQuestion = planAssistantQuestion.trim();
      const quickQuestion = selectedPlanStep
        ? `How do I carry out ${selectedPlanStep.section.toLowerCase()} step ${selectedPlanStep.number} safely and efficiently?`
        : '';
      const technicianQuestion = mode === 'quick' ? quickQuestion : customQuestion;

      if (!technicianQuestion) {
        setPlanAssistantError(
          mode === 'quick'
            ? 'Select a verification or repair step first.'
            : 'Enter a question first.'
        );
        return;
      }

      try {
        setPlanAssistantLoading(true);
        setPlanAssistantActiveAsk(mode);
        setPlanAssistantError(null);
        const base = await getBaseUrl();
        const token = getAuthToken();
        const condensedHistory = planAssistantMessages.slice(-6).map(message => ({
          role: message.role,
          content: message.content
        }));
        const stepContext = selectedPlanStep
          ? `${selectedPlanStep.section} step ${selectedPlanStep.number}: ${selectedPlanStep.description}`
          : null;
        const formattedQuestion =
          mode === 'custom' && stepContext
            ? [`Focus step: ${stepContext}`, `Technician question: ${technicianQuestion}`].join('\n')
            : technicianQuestion;

        const response = await fetch(`${base}/api/knowledge-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            modelId,
            question: formattedQuestion,
            history: condensedHistory,
            conversationId: planAssistantConversationId,
            includeFundamentals: false,
            includeImages: false,
            responseLanguage: 'EN'
          })
        });

        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(payload?.error || 'Failed to get plan guidance.');
        }

        const now = new Date().toISOString();
        const assistantTimestamp = payload?.timestamp || now;
        setPlanAssistantMessages(prev => [
          ...prev,
          {
            id: `user-${now}`,
            role: 'user',
            content: technicianQuestion,
            timestamp: now
          },
          {
            id: `assistant-${assistantTimestamp}`,
            role: 'assistant',
            content: String(payload?.answer || '').trim(),
            timestamp: assistantTimestamp
          }
        ]);
        setPlanAssistantConversationId(payload?.conversationId || planAssistantConversationId || null);
        if (mode === 'custom') {
          setPlanAssistantQuestion('');
        }
      } catch (err) {
        console.error('TSFAD plan assistant failed', err);
        setPlanAssistantError(err instanceof Error ? err.message : 'Failed to get plan guidance.');
      } finally {
        setPlanAssistantLoading(false);
        setPlanAssistantActiveAsk(null);
      }
    },
    [
      modelId,
      planAssistantConversationId,
      planAssistantMessages,
      planAssistantQuestion,
      selectedCause,
      selectedPlanStep
    ]
  );

  const activeCauses = React.useMemo(
    () => possibleCauses.filter(cause => cause.status !== 'INACTIVE'),
    [possibleCauses]
  );

  const inactiveCauses = React.useMemo(
    () => possibleCauses.filter(cause => cause.status === 'INACTIVE'),
    [possibleCauses]
  );

  const quoteCatalogLookup = React.useMemo(() => {
    const byPartNumber = new Map<string, QuoteCatalogPartItem>();
    const bySku = new Map<string, QuoteCatalogPartItem>();
    quotePartCatalog.forEach(item => {
      const partKey = normaliseLookupToken(String(item?.partNumber || ''));
      const skuKey = normaliseLookupToken(String(item?.sku || ''));
      if (partKey && !byPartNumber.has(partKey)) byPartNumber.set(partKey, item);
      if (skuKey && !bySku.has(skuKey)) bySku.set(skuKey, item);
    });
    return { byPartNumber, bySku };
  }, [quotePartCatalog]);

  const findQuoteCatalogItem = useCallback(
    (rawPartNumber: string): QuoteCatalogPartItem | null => {
      const key = normaliseLookupToken(rawPartNumber);
      if (!key) return null;
      return quoteCatalogLookup.byPartNumber.get(key) || quoteCatalogLookup.bySku.get(key) || null;
    },
    [quoteCatalogLookup]
  );

  const applyPartNumberToQuoteLine = useCallback(
    (lineIndex: number, rawPartNumber: string) => {
      const matched = findQuoteCatalogItem(rawPartNumber);
      const fallbackPartNumber = String(rawPartNumber || '').trim() || null;
      setQuoteLineItems(prev =>
        prev.map((item, itemIndex) => {
          if (itemIndex !== lineIndex) return item;
          if (String(item.lineType || '').toLowerCase() !== 'part') {
            return clearQuoteSlaFields({
              ...item,
              partNumber: null,
              sku: null,
              catalogId: null
            });
          }
          if (!fallbackPartNumber && !matched) {
            return clearQuoteSlaFields({
              ...item,
              partNumber: null,
              sku: null,
              catalogId: null,
              source: 'manual'
            });
          }
          if (!matched) {
            return clearQuoteSlaFields({
              ...item,
              partNumber: fallbackPartNumber,
              sku: null,
              catalogId: null,
              source: 'manual'
            });
          }
          const nextLine: QuoteLineItemDraft = {
            ...item,
            partNumber: matched.partNumber || fallbackPartNumber,
            sku: matched.sku || null,
            catalogId: matched.id || null,
            description: normalizeQuoteFieldText(matched.description || item.description),
            unitValue: Number(matched.unitValue || 0),
            source: 'catalog',
            isFree: false,
            originalUnitValue: null,
            slaCoverageStatus: null,
            slaLabel: null,
            slaDiscountPercent: null,
            slaCoverageReason: null,
            slaMatchedListNames: []
          };
          const coverage = findQuoteSlaPartCoverage(
            rawPartNumber,
            nextLine.partNumber || matched.partNumber || fallbackPartNumber
          );
          return applySlaCoverageToQuotePartLine(nextLine, coverage);
        })
      );
    },
    [applySlaCoverageToQuotePartLine, clearQuoteSlaFields, findQuoteCatalogItem, findQuoteSlaPartCoverage]
  );

  const quoteTotals = React.useMemo(
    () => computeQuoteTotals(quoteLineItems, quoteTemplateLayout?.totalSettings),
    [quoteLineItems, quoteTemplateLayout?.totalSettings]
  );
  const quoteSaveButtonLabel = quoteEditingQuoteId
    ? 'Update Draft'
    : quoteRevisionOfQuoteId
    ? 'Save Revision Draft'
    : 'Save Quote';
  const quoteHeaderBlocks = React.useMemo(() => {
    if (!quoteTemplateLayout) return [];
    const primaryContact =
      caseData?.customer?.contacts?.find?.((entry: any) => entry?.isPrimary) ||
      caseData?.customer?.contacts?.[0] ||
      null;
    const primaryTask =
      caseData?.activeTask ||
      caseData?.displayTask ||
      (Array.isArray(caseData?.tasks) && (caseData?.tasks?.length || 0) > 0
        ? caseData?.tasks?.[0]
        : null);
    const serviceLocationName = String(
      caseData?.device?.location?.name ||
        (caseData?.device as any)?.locationName ||
        (caseData?.device as any)?.customerLocationName ||
        (caseData as any)?.location?.name ||
        primaryContact?.location?.name ||
        ''
    ).trim();

    const context = {
      customer: {
        companyName: caseData?.customer?.companyName || caseData?.customer?.company || '',
        contact: {
          fullName: primaryContact?.fullName || '',
          email: primaryContact?.email || '',
          phone: primaryContact?.phone || ''
        },
        location: {
          name: serviceLocationName
        }
      },
      device: {
        modelNumber: caseData?.device?.modelNumber || caseData?.device?.model?.modelNumber || '',
        serialNumber: caseData?.device?.serialNumber || '',
        manufacturer: caseData?.device?.manufacturer || caseData?.device?.model?.manufacturer || '',
        assetNumber: caseData?.device?.assetNumber || ''
      },
      case: {
        caseNumber: caseData?.caseCode || caseData?.caseNumber || caseData?.id || '',
        priority: caseData?.priority || '',
        status: caseData?.status || ''
      },
      task: {
        taskType:
          primaryTask?.type?.label ||
          String(
            (primaryTask as any)?.taskType ||
              (typeof (primaryTask as any)?.type === 'string' ? (primaryTask as any).type : '')
          ).trim(),
        status: primaryTask?.status || '',
        assignedUserName:
          primaryTask?.assignees?.[0]?.fullName ||
          primaryTask?.assignees?.[0]?.username ||
          primaryTask?.assignedUserName ||
          ''
      },
      quote: {
        title: quoteTitle,
        total: `$${quoteTotals.grandTotal.toFixed(2)}`
      }
    } as Record<string, any>;

    const byId = quoteTemplateLayout.infoBlocks.reduce<Record<string, QuoteTemplateInfoBlock>>((acc, block) => {
      acc[block.id] = block;
      return acc;
    }, {});

    const orderedSlotIds = QUOTE_HEADER_SLOT_IDS.filter(slotId => {
      if (quoteTemplateLayout.headerRows === 1) {
        return slotId.startsWith('h1');
      }
      return true;
    });

  return orderedSlotIds
      .map(slotId => ({
        slotId,
        blockId: quoteTemplateLayout.headerAssignments?.[slotId] || null
      }))
      .filter(({ blockId }) => Boolean(blockId))
      .map(({ slotId, blockId }) => byId[String(blockId)] ? ({ slotId, block: byId[String(blockId)] }) : null)
      .filter((entry): entry is { slotId: (typeof QUOTE_HEADER_SLOT_IDS)[number]; block: QuoteTemplateInfoBlock } =>
        Boolean(entry && entry.block && entry.block.included)
      )
      .map(({ slotId, block }) => ({
        id: block.id,
        label: block.label,
        slotId,
        slotRow: getQuoteHeaderSlotMeta(slotId).row,
        slotCol: getQuoteHeaderSlotMeta(slotId).col,
        textAlign: getQuoteHeaderSlotAlign(slotId),
        fields: block.fields
          .filter(field => field.included)
          .map(field => {
            const sourceGroup = String(field.sourceGroup || '').toLowerCase();
            const sourceObj = context[sourceGroup] || {};
            const value = readPathValue(sourceObj, field.sourceKey);
            return {
              id: field.id,
              label: field.label,
              value: value || 'Not provided'
            };
          })
      }));
  }, [caseData, quoteTemplateLayout, quoteTitle, quoteTotals.grandTotal]);
  useEffect(() => {
    const previous = prevSelectedCauseRef.current;

    if (!selectedCause) {
      setIsEditingProcedure(false);
      setProcedureEditNotes('');
      setIsInactivating(false);
      setInactivationReason('');
      loadCauseDrafts(null);
      prevSelectedCauseRef.current = null;
      return;
    }

    const sameCause = previous?.id === selectedCause.id;
    if (sameCause && (isEditingProcedureRef.current || isInactivatingRef.current)) {
      prevSelectedCauseRef.current = selectedCause;
      return;
    }

    setIsEditingProcedure(false);
    setProcedureEditNotes('');
    setIsInactivating(false);
    setInactivationReason('');
    loadCauseDrafts(selectedCause);
    prevSelectedCauseRef.current = selectedCause;
  }, [selectedCause, loadCauseDrafts]);

  useEffect(() => {
    if (inactiveCauses.length === 0 && inactivePanelOpen) {
      setInactivePanelOpen(false);
    } else if (inactiveCauses.length > 0 && !inactivePanelOpen) {
      setInactivePanelOpen(true);
    }
  }, [inactiveCauses.length, inactivePanelOpen]);

  const explanationVerificationSteps = React.useMemo<StepNode[]>(() => {
    if (!selectedCauseForExplanation) return [];
    if (
      selectedCauseForExplanation.structuredTestingSteps &&
      selectedCauseForExplanation.structuredTestingSteps.length > 0
    ) {
      return normaliseStepNodes(selectedCauseForExplanation.structuredTestingSteps);
    }
    if (
      selectedCauseForExplanation.testingSteps &&
      selectedCauseForExplanation.testingSteps.length > 0
    ) {
      return normaliseStepNodes(selectedCauseForExplanation.testingSteps);
    }
    if (selectedCauseForExplanation.testProcedure) {
      return normaliseStepNodes(selectedCauseForExplanation.testProcedure.split(/[\n]+/));
    }
    return [];
  }, [normaliseStepNodes, selectedCauseForExplanation]);

  const getEstimatedTimeOnSite = () => {
    const untestedCauses = getRemainingCauses();
    if (untestedCauses.length === 0) return 0;

    // Group causes by type to estimate combined testing time
    // This is a simplified calculation - in reality, you'd want more sophisticated grouping
    const totalTime = untestedCauses.reduce((total, cause) => total + cause.estimatedTime, 0);
    
    // Apply efficiency factor (testing multiple related items together saves time)
    const efficiencyFactor = 0.7; // 30% time savings from combined testing
    return Math.round(totalTime * efficiencyFactor);
  };

  type PrioritisedItem = {
    item: string;
    probability: 'HIGH' | 'MEDIUM' | 'LOW';
    weight: number;
  };

  type ParsedResourceItem = {
    description: string;
    partNumber: string | null;
    failurePercent: number | null;
  };

const handleFlagReferenceImageFromProcedure = useCallback(
    ({
      cause,
      image,
      context
    }: {
      cause: PossibleCause;
      image: ReferenceImageHit;
      context?: {
        section?: ReferenceImageBlockSection;
        itemId?: string;
        sourceNodeIds?: string[];
        blockText?: string;
        anchorText?: string;
      } | null;
    }) => {
      if (!cause || !image?.id) return;
      const causeTitle = formatCauseTitle(cause.description, cause.shortTitle);
      const issueSubject = (image.label || image.id || causeTitle || 'reference image').trim();
      const descriptionLines = [
        `Case: ${caseDisplayNumber || caseData?.id || 'Unknown case'}`,
        `Cause: ${causeTitle || 'Unknown cause'}`,
        `Image ID: ${image.id}`,
        `Image title: ${image.label || 'Untitled reference image'}`,
        `Section: ${context?.section || 'N/A'}`,
        `Item ID: ${context?.itemId || 'N/A'}`,
        `Anchor text: ${context?.anchorText || 'N/A'}`
      ];

      openFeedbackModal({
        cause,
        defaults: {
          feedbackType: 'WRONG_REFERENCE_IMAGE',
          severity: 'MEDIUM',
          title: `Reference image mismatch: ${issueSubject.slice(0, 80)}`,
          description: descriptionLines.join('\n'),
          impact: ''
        },
        relatedDescription: cause.description || causeTitle || null,
        metadata: {
          source: 'tsfad-action-plan-image-preview',
          analysisId,
          caseNumber: caseDisplayNumber || null,
          causeId: cause.id,
          causeTitle: causeTitle || null,
          causeDescription: cause.description || null,
          flaggedImage: {
            imageId: image.id,
            label: image.label || null,
            fileUrl: image.fileUrl || null,
            linkedNodeIds: Array.isArray(image.linkedNodeIds) ? image.linkedNodeIds : [],
            linkedSnippetIds: Array.isArray(image.linkedSnippetIds) ? image.linkedSnippetIds : [],
            linkedDomains: Array.isArray(image.linkedDomains) ? image.linkedDomains : [],
            matched: image.matched || null,
            linkRelation: image.linkRelation || null,
            linkScore: image.linkScore ?? null,
            primaryPartNumber: image.primaryPartNumber || null,
            partNumbers: Array.isArray(image.partNumbers) ? image.partNumbers : [],
            components: Array.isArray(image.components) ? image.components : [],
            subassemblies: Array.isArray(image.subassemblies) ? image.subassemblies : [],
            systemPaths: Array.isArray(image.systemPaths) ? image.systemPaths : [],
            viewType: image.viewType || null,
            viewTags: Array.isArray(image.viewTags) ? image.viewTags : []
          },
          actionPlanContext: {
            section: context?.section || null,
            itemId: context?.itemId || null,
            sourceNodeIds: Array.isArray(context?.sourceNodeIds) ? context?.sourceNodeIds : [],
            blockText: context?.blockText || null,
            anchorText: context?.anchorText || null
          }
        }
      });
    },
    [analysisId, caseData?.id, caseDisplayNumber, openFeedbackModal]
  );

  type SlaCoverageStatus = 'COVERED' | 'DISCOUNTED' | 'NOT_COVERED';

  type SlaCoverageResource = {
    item: string;
    sourceType: 'PART' | 'TOOL';
    partNumber?: string | null;
    normalizedKey?: string | null;
    coverageStatus: SlaCoverageStatus;
    coverageReason?: string | null;
    discountPercent?: number | null;
    matchedListIds?: string[];
    matchedListNames?: string[];
    matchedLineTypes?: string[];
  };

  type SlaCoverageData = {
    active?: boolean;
    reason?: string | null;
    template?: {
      id?: string;
      name?: string;
      brandingName?: string;
      tierBrandingName?: string | null;
      slaType?: string;
      status?: string;
    } | null;
    resources?: {
      parts?: SlaCoverageResource[];
      tools?: SlaCoverageResource[];
    } | null;
    summary?: {
      totalParts?: number;
      coveredParts?: number;
      discountedParts?: number;
      notCoveredParts?: number;
      totalTools?: number;
      coveredTools?: number;
      discountedTools?: number;
      notCoveredTools?: number;
    } | null;
  };

  type KitRecommendationType = 'QUOTE_KIT' | 'MAINTENANCE_SIGNAL';

  type KitRecommendationEntry = {
    kitId: string;
    kitNumber?: string;
    sku?: string;
    name?: string;
    deviceModel?: string;
    purpose?: string;
    buildSource?: string;
    recommendationType: KitRecommendationType;
    matchedPartCount: number;
    totalSuggestedParts: number;
    totalKitComponents: number;
    suggestedPartsCoveragePercent: number;
    kitCoveragePercent: number;
    pricingMode?: 'SLA' | 'RAW';
    currencyCode?: string | null;
    matchedPartNumbers?: string[];
    kitPrice?: number | null;
    matchedPartsPriceTotal?: number | null;
    savingsAmount?: number | null;
    rawKitPrice?: number | null;
    rawMatchedPartsPriceTotal?: number | null;
    kitSlaCoverageStatus?: 'COVERED' | 'DISCOUNTED' | 'NOT_COVERED' | null;
    kitSlaDiscountPercent?: number | null;
    matchedPartsCoverageSummary?: {
      covered?: number;
      discounted?: number;
      notCovered?: number;
    } | null;
    rationale?: string | null;
  };

  type KitRecommendationData = {
    active?: boolean;
    reason?: string | null;
    deviceModelCandidates?: string[];
    summary?: {
      totalSuggestedParts?: number;
      totalCandidateKits?: number;
      matchedKitCount?: number;
      recommendationCount?: number;
    } | null;
    recommendations?: KitRecommendationEntry[] | null;
  };

  const sanitiseCatalogText = (value: string) =>
    value
      .replace(/\uFFFD/g, 'Ø')
      .replace(/\s+/g, ' ')
      .trim();

  const parseResourceItem = (rawValue: string): ParsedResourceItem => {
    let text = sanitiseCatalogText(String(rawValue || ''));
    if (!text) return { description: '', partNumber: null, failurePercent: null };

    let failurePercent: number | null = null;
    const failureMatch = text.match(/\(([-+]?\d+(?:\.\d+)?)%\)\s*$/);
    if (failureMatch) {
      failurePercent = Number(failureMatch[1]);
      text = text.slice(0, failureMatch.index).trim();
    }

    let partNumber: string | null = null;
    const trailingPartMatch = text.match(/\(([A-Z0-9][A-Z0-9-/]{4,})\)\s*$/i);
    if (trailingPartMatch && /\d/.test(trailingPartMatch[1])) {
      partNumber = trailingPartMatch[1].toUpperCase();
      text = text.slice(0, trailingPartMatch.index).trim();
    } else {
      const inlinePartMatch = text.match(/\b([A-Z0-9][A-Z0-9-/]{4,})\b/i);
      if (inlinePartMatch && /\d/.test(inlinePartMatch[1])) {
        partNumber = inlinePartMatch[1].toUpperCase();
      }
    }

    return {
      description: text || sanitiseCatalogText(String(rawValue || '')),
      partNumber,
      failurePercent: Number.isFinite(failurePercent) ? failurePercent : null
    };
  };

  const normalisePartsKey = (value: string) => {
    const cleaned = value.trim();
    const partNumbers = Array.from(new Set((cleaned.match(/\b\d{5,}\b/g) || []))).sort();
    if (partNumbers.length > 0) {
      return `parts:${partNumbers.join(',')}`;
    }
    return cleaned
      .toLowerCase()
      .replace(/\(.*?\)/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const analysisSlaCoverage = useMemo<SlaCoverageData | null>(() => {
    const latestCoverage =
      latestAnalysis?.caseModel && typeof latestAnalysis.caseModel === 'object'
        ? (latestAnalysis.caseModel as Record<string, unknown>).slaCoverage
        : null;
    const fallbackCoverage =
      caseData?.tsfadAnalyses?.[0]?.caseModel &&
      typeof caseData.tsfadAnalyses[0].caseModel === 'object'
        ? (caseData.tsfadAnalyses[0].caseModel as Record<string, unknown>).slaCoverage
        : null;
    const candidate = latestCoverage || fallbackCoverage;
    return candidate && typeof candidate === 'object' ? (candidate as SlaCoverageData) : null;
  }, [caseData?.tsfadAnalyses, latestAnalysis]);

  const resourceCoverageLookup = useMemo(() => {
    const buildLookup = (entries: SlaCoverageResource[] | undefined) => {
      const byRaw = new Map<string, SlaCoverageResource>();
      const byPartNumber = new Map<string, SlaCoverageResource>();
      const byNormalized = new Map<string, SlaCoverageResource>();
      (Array.isArray(entries) ? entries : []).forEach(entry => {
        const rawKey = String(entry?.item || '').trim();
        if (rawKey && !byRaw.has(rawKey)) {
          byRaw.set(rawKey, entry);
        }
        const partKey = String(entry?.partNumber || '')
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, '');
        if (partKey && !byPartNumber.has(partKey)) {
          byPartNumber.set(partKey, entry);
        }
        const normalizedKey = String(entry?.normalizedKey || '').trim();
        if (normalizedKey && !byNormalized.has(normalizedKey)) {
          byNormalized.set(normalizedKey, entry);
        }
      });
      return { byRaw, byPartNumber, byNormalized };
    };

    return {
      parts: buildLookup(analysisSlaCoverage?.resources?.parts),
      tools: buildLookup(analysisSlaCoverage?.resources?.tools)
    };
  }, [analysisSlaCoverage]);

  const getResourceCoverage = (item: string, sourceType: 'PART' | 'TOOL'): SlaCoverageResource | null => {
    const lookup = sourceType === 'PART' ? resourceCoverageLookup.parts : resourceCoverageLookup.tools;
    const rawKey = String(item || '').trim();
    if (rawKey && lookup.byRaw.has(rawKey)) {
      return lookup.byRaw.get(rawKey) || null;
    }
    const parsed = parseResourceItem(item);
    const partKey = String(parsed.partNumber || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    if (partKey && lookup.byPartNumber.has(partKey)) {
      return lookup.byPartNumber.get(partKey) || null;
    }
    const normalizedKey = partKey || normalisePartsKey(parsed.description || item);
    if (normalizedKey && lookup.byNormalized.has(normalizedKey)) {
      return lookup.byNormalized.get(normalizedKey) || null;
    }
    return null;
  };

  const getCoverageBadgeLabel = (coverage: SlaCoverageResource | null) => {
    if (!coverage) return null;
    if (coverage.coverageStatus === 'COVERED') {
      return 'Covered';
    }
    if (coverage.coverageStatus === 'DISCOUNTED') {
      const discount = Number(coverage.discountPercent);
      return Number.isFinite(discount) ? `${discount}% discount` : 'Discounted';
    }
    return 'Not covered';
  };

  const formatCurrencyValue = (value?: number | null, currencyCode?: string | null) => {
    if (value === null || value === undefined) return null;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    const resolvedCurrency = String(currencyCode || '').trim().toUpperCase() || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: resolvedCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numeric);
    } catch (_error) {
      return `${resolvedCurrency} ${numeric.toFixed(2)}`;
    }
  };

  const getKitRecommendationActionText = (recommendation: KitRecommendationEntry) => {
    const savingsAmount = Number(recommendation.savingsAmount);
    if (Number.isFinite(savingsAmount)) {
      const absSavings = formatCurrencyValue(Math.abs(savingsAmount), recommendation.currencyCode);
      if (savingsAmount > 0.01) {
        return `If you implement this kit, the quote will be ${absSavings} less.`;
      }
      if (savingsAmount < -0.01) {
        return `If you implement this kit, the quote will add ${absSavings} to the quote.`;
      }
      return 'There is no financial benefit to the customer from quoting the kit.';
    }
    return 'No price comparison is available for this kit yet.';
  };

  const getKitRecommendationSlaText = (recommendation: KitRecommendationEntry) => {
    if (recommendation.pricingMode !== 'SLA') {
      return null;
    }
    const kitStatus = String(recommendation.kitSlaCoverageStatus || 'NOT_COVERED').trim().toUpperCase();
    const summary = recommendation.matchedPartsCoverageSummary || {};
    const coveredParts = Number(summary.covered || 0);
    const discountedParts = Number(summary.discounted || 0);
    const hasMatchedPartSla = coveredParts > 0 || discountedParts > 0;

    if (kitStatus === 'COVERED') {
      return 'SLA: This kit is covered under the agreement.';
    }
    if (kitStatus === 'DISCOUNTED') {
      const discountPercent = Number(recommendation.kitSlaDiscountPercent || 0);
      const discountLabel =
        Number.isFinite(discountPercent) && discountPercent > 0
          ? `${discountPercent}% discount`
          : 'discounted pricing';
      if (hasMatchedPartSla) {
        return `SLA: This kit gets ${discountLabel}, and matched parts also receive individual SLA pricing.`;
      }
      return `SLA: This kit gets ${discountLabel} under the agreement.`;
    }
    if (hasMatchedPartSla) {
      return 'SLA: This kit is not covered, but matched parts receive SLA pricing individually.';
    }
    return 'SLA: This kit is not covered under the agreement.';
  };

  const collectItemsWithPriority = (
    itemsAccessor: (cause: PossibleCause) => string[],
    keyFn: (value: string) => string = value => value
  ): PrioritisedItem[] => {
    const itemRank = new Map<string, PrioritisedItem>();

    activeCauses.forEach(cause => {
      const weight = probabilityOrder[cause.probability] || 4;
      const probabilityLabel = cause.probability;
      itemsAccessor(cause).forEach(item => {
        if (!item) return;
        const key = keyFn(item);
        const existing = itemRank.get(key);
        if (!existing || weight < existing.weight) {
          itemRank.set(key, { item, probability: probabilityLabel, weight });
          return;
        }
        if (weight === existing.weight && item.length < existing.item.length) {
          itemRank.set(key, { ...existing, item });
        }
      });
    });

    return Array.from(itemRank.values()).sort(
      (a, b) => a.weight - b.weight || a.item.localeCompare(b.item)
    );
  };

  const getRequiredTools = () => collectItemsWithPriority(cause => cause.requiredTools || []);

  const slaCoverageSummaryText = useMemo(() => {
    if (!analysisSlaCoverage) return null;
    if (!analysisSlaCoverage.active) {
      return 'No active device SLA was available for this TSFAD analysis.';
    }

    const summary = analysisSlaCoverage.summary || {};
    const templateLabelParts = [
      analysisSlaCoverage.template?.brandingName || analysisSlaCoverage.template?.name || '',
      analysisSlaCoverage.template?.tierBrandingName || '',
      analysisSlaCoverage.template?.slaType || ''
    ]
      .map(value => String(value || '').trim())
      .filter(Boolean);
    const templateLabel = templateLabelParts.length > 0 ? templateLabelParts.join(' | ') : 'Active SLA';

    return `${templateLabel} applied. Parts: ${summary.coveredParts || 0} covered, ${
      summary.discountedParts || 0
    } discounted, ${summary.notCoveredParts || 0} not covered. Tools: ${
      summary.coveredTools || 0
    } covered, ${summary.notCoveredTools || 0} not covered.`;
  }, [analysisSlaCoverage]);

  const analysisKitRecommendations = useMemo<KitRecommendationData | null>(() => {
    const latestRecommendations =
      latestAnalysis?.caseModel && typeof latestAnalysis.caseModel === 'object'
        ? (latestAnalysis.caseModel as Record<string, unknown>).kitRecommendations
        : null;
    const fallbackRecommendations =
      caseData?.tsfadAnalyses?.[0]?.caseModel &&
      typeof caseData.tsfadAnalyses[0].caseModel === 'object'
        ? (caseData.tsfadAnalyses[0].caseModel as Record<string, unknown>).kitRecommendations
        : null;
    const candidate = latestRecommendations || fallbackRecommendations;
    return candidate && typeof candidate === 'object' ? (candidate as KitRecommendationData) : null;
  }, [caseData?.tsfadAnalyses, latestAnalysis]);

  const visibleKitRecommendations = useMemo<KitRecommendationEntry[]>(() => {
    const recommendations = analysisKitRecommendations?.recommendations;
    return Array.isArray(recommendations) ? recommendations.filter(Boolean) : [];
  }, [analysisKitRecommendations]);

  const visibleKitRecommendationMap = useMemo(() => {
    const next = new Map<string, KitRecommendationEntry>();
    visibleKitRecommendations.forEach(recommendation => {
      const key = String(recommendation?.kitId || '').trim();
      if (!key) return;
      next.set(key, recommendation);
    });
    return next;
  }, [visibleKitRecommendations]);

  const resolveKitDisplayLabel = useCallback(
    (
      value:
        | Partial<ImplementedKitSelection>
        | Partial<KitRecommendationEntry>
        | null
        | undefined
    ) => {
      const kitNumber = String(value?.kitNumber || '').trim();
      if (kitNumber) return kitNumber;
      const sku = String(value?.sku || '').trim();
      if (sku) return sku;
      const name = String(value?.name || '').trim();
      if (name) return name;
      return 'Kit';
    },
    []
  );

  const implementedKitSelectionMap = useMemo(() => {
    const next = new Map<string, ImplementedKitSelection>();
    implementedKitSelections.forEach(selection => {
      const key = String(selection?.kitId || '').trim();
      if (!key) return;
      next.set(key, selection);
    });
    return next;
  }, [implementedKitSelections]);

  const implementedKitPartLookup = useMemo(() => {
    const next = new Map<string, ImplementedKitSelection>();
    implementedKitSelections.forEach(selection => {
      selection.matchedPartNumbers.forEach(partNumber => {
        const key = normaliseLookupToken(String(partNumber || ''));
        if (!key || next.has(key)) return;
        next.set(key, selection);
      });
    });
    return next;
  }, [implementedKitSelections]);

  const getImplementedKitSelectionForResource = (item: string) => {
    const parsed = parseResourceItem(item);
    const key = normaliseLookupToken(String(parsed.partNumber || ''));
    if (!key) return null;
    return implementedKitPartLookup.get(key) || null;
  };

  const getRequiredParts = () =>
    collectItemsWithPriority(cause => cause.requiredParts || [], normalisePartsKey)
      .map((entry, index) => ({
        ...entry,
        isImplementedKitComponent: Boolean(getImplementedKitSelectionForResource(entry.item)),
        originalIndex: index
      }))
      .sort(
        (a, b) =>
          Number(a.isImplementedKitComponent) - Number(b.isImplementedKitComponent) ||
          a.originalIndex - b.originalIndex
      )
      .map(({ isImplementedKitComponent, originalIndex, ...entry }) => entry);

  const getOverlappingImplementedKitSelection = useCallback(
    (recommendation: KitRecommendationEntry) => {
      for (const partNumber of Array.isArray(recommendation.matchedPartNumbers) ? recommendation.matchedPartNumbers : []) {
        const key = normaliseLookupToken(String(partNumber || ''));
        if (!key) continue;
        const selection = implementedKitPartLookup.get(key);
        if (selection && selection.kitId !== recommendation.kitId) {
          return selection;
        }
      }
      return null;
    },
    [implementedKitPartLookup]
  );

  const implementKitRecommendation = useCallback((recommendation: KitRecommendationEntry) => {
    const kitId = String(recommendation.kitId || '').trim();
    if (!kitId) return;
    const overlap = getOverlappingImplementedKitSelection(recommendation);
    if (overlap) return;
    setImplementedKitSelections(prev => {
      if (prev.some(entry => String(entry?.kitId || '').trim() === kitId)) {
        return prev;
      }
      return [
        ...prev,
        {
          kitId,
          kitNumber: recommendation.kitNumber || null,
          sku: recommendation.sku || null,
          name: recommendation.name || null,
          matchedPartNumbers: Array.isArray(recommendation.matchedPartNumbers)
            ? recommendation.matchedPartNumbers.map(value => String(value || '').trim()).filter(Boolean)
            : [],
          implementedAt: new Date().toISOString()
        }
      ];
    });
    markAnalysisDirty();
  }, [getOverlappingImplementedKitSelection, markAnalysisDirty]);

  const kitRecommendationSummaryText = useMemo(() => {
    if (!analysisKitRecommendations?.active) return null;
    if (visibleKitRecommendations.length === 0) return null;
    const summary = analysisKitRecommendations.summary || {};
    const recommendationCount = Number(summary.recommendationCount || visibleKitRecommendations.length);
    const matchedKitCount = Number(summary.matchedKitCount || visibleKitRecommendations.length);
    return `${recommendationCount} kit recommendation${
      recommendationCount === 1 ? '' : 's'
    } found across ${matchedKitCount} matching kit${matchedKitCount === 1 ? '' : 's'}.`;
  }, [analysisKitRecommendations, visibleKitRecommendations]);

  const renderPartResourceRow = (item: string, probability: 'HIGH' | 'MEDIUM' | 'LOW', key: string) => {
    const parsed = parseResourceItem(item);
    const coverage = getResourceCoverage(item, 'PART');
    const coverageLabel = getCoverageBadgeLabel(coverage);
    const implementedSelection = getImplementedKitSelectionForResource(item);
    const implementedKitLabel = implementedSelection ? resolveKitDisplayLabel(
      visibleKitRecommendationMap.get(String(implementedSelection.kitId || '').trim()) || implementedSelection
    ) : null;
    return (
      <li key={key} style={styles.resourceRow}>
        {renderPriorityBadge(probability)}
        <div style={styles.resourceContent}>
          <div style={styles.resourceMainLine}>
            <span
              style={
                implementedSelection
                  ? styles.resourceImplementedText
                  : undefined
              }
            >
              <span style={styles.resourcePartNumber}>{parsed.partNumber || 'NO-PN'}</span>
              <span style={styles.resourceDivider}>|</span>
              <span>{parsed.description}</span>
              <span style={styles.resourceDivider}>|</span>
              <span style={styles.resourceFailureInline}>
                {parsed.failurePercent === null ? 'N/A' : `${parsed.failurePercent}%`}
              </span>
            </span>
            {analysisSlaCoverage?.active && coverageLabel ? (
              <span
                style={{
                  ...styles.resourceCoverageBadge,
                  ...(coverage?.coverageStatus === 'COVERED'
                    ? styles.resourceCoverageBadgeCovered
                    : coverage?.coverageStatus === 'DISCOUNTED'
                    ? styles.resourceCoverageBadgeDiscounted
                    : styles.resourceCoverageBadgeNotCovered)
                }}
              >
                {coverageLabel}
              </span>
            ) : null}
          </div>
          {implementedSelection && implementedKitLabel ? (
            <div style={styles.resourceMetaRow}>
              <span style={styles.resourceKitBadge}>KIT {implementedKitLabel}</span>
              <span style={styles.resourceKitNote}>
                Replaced in the quote when this kit is implemented.
              </span>
            </div>
          ) : null}
        </div>
      </li>
    );
  };

  const renderToolResourceRow = (item: string, probability: 'HIGH' | 'MEDIUM' | 'LOW', key: string) => {
    const parsed = parseResourceItem(item);
    const coverage = getResourceCoverage(item, 'TOOL');
    const coverageLabel = getCoverageBadgeLabel(coverage);
    return (
      <li key={key} style={styles.resourceRow}>
        {renderPriorityBadge(probability)}
        <div style={styles.resourceContent}>
          <div style={styles.resourceMainLine}>
            <span style={styles.resourcePartNumber}>{parsed.partNumber || 'NO-PN'}</span>
            <span style={styles.resourceDivider}>|</span>
            <span>{parsed.description}</span>
            {analysisSlaCoverage?.active && coverageLabel ? (
              <span
                style={{
                  ...styles.resourceCoverageBadge,
                  ...(coverage?.coverageStatus === 'COVERED'
                    ? styles.resourceCoverageBadgeCovered
                    : coverage?.coverageStatus === 'DISCOUNTED'
                    ? styles.resourceCoverageBadgeDiscounted
                    : styles.resourceCoverageBadgeNotCovered)
                }}
              >
                {coverageLabel}
              </span>
            ) : null}
          </div>
        </div>
      </li>
    );
  };

  const renderPriorityBadge = (probability: 'HIGH' | 'MEDIUM' | 'LOW') => {
    const color = getProbabilityColor(probability);
    return (
      <span
        style={{
          display: 'inline-block',
          minWidth: '48px',
          padding: '0.1rem 0.45rem',
          marginRight: '0.4rem',
          borderRadius: '999px',
          backgroundColor: `${color}20`,
          color,
          fontWeight: 600,
          fontSize: '0.75rem',
          textAlign: 'center'
        }}
      >
        {probability}
      </span>
    );
  };

  const styles = {
    container: {
      backgroundColor: BRAND_SURFACE,
      color: BRAND_TEXT,
      minHeight: '100vh',
      fontFamily: 'Inter, system-ui, sans-serif'
    },
    header: {
      backgroundColor: BRAND_SURFACE,
      padding: '1rem 2rem',
      borderBottom: `1px solid ${BRAND_BORDER}`
    },
    breadcrumb: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      color: BRAND_MUTED,
      marginBottom: '0.5rem'
    },
    breadcrumbLink: {
      color: BRAND_TEXT,
      textDecoration: 'none',
      cursor: 'pointer'
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    caseInfo: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: BRAND_TEXT
    },
    content: {
      padding: '2.5rem 3rem 4rem',
      maxWidth: '1600px',
      margin: '0 auto'
    },
    topBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem',
      flexWrap: 'wrap' as const,
      marginBottom: '1.5rem'
    },
    backLink: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '2.7rem',
      padding: '0.65rem 1rem',
      cursor: 'pointer',
      color: BRAND_TEXT,
      textDecoration: 'none',
      fontWeight: 700,
      backgroundColor: BRAND_CARD,
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.8rem',
      boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
    },
    saveMeta: {
      color: BRAND_MUTED,
      fontSize: '0.875rem'
    },
    topBarActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      flexWrap: 'wrap' as const,
      justifyContent: 'flex-end'
    },
    heroCard: {
      background: BRAND_CARD,
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '1rem',
      padding: '1.75rem',
      boxShadow: '0 20px 40px -28px rgba(18, 63, 67, 0.22)',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '1.25rem',
      marginBottom: '1.5rem'
    },
    heroHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '1.5rem',
      alignItems: 'flex-start',
      flexWrap: 'wrap' as const
    },
    heroHeadingBlock: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.65rem',
      flex: 1,
      minWidth: 0
    },
    heroTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flexWrap: 'wrap' as const
    },
    heroTitle: {
      fontSize: '1.75rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    heroMetaPill: {
      borderRadius: '9999px',
      padding: '0.25rem 0.7rem',
      backgroundColor: BRAND_ACCENT_SOFT,
      color: BRAND_ACCENT_TEXT,
      border: `1px solid ${BRAND_BORDER}`,
      fontSize: '0.78rem',
      fontWeight: 600
    },
    heroProblem: {
      fontWeight: 600,
      color: BRAND_TEXT,
      maxWidth: '56rem',
      lineHeight: 1.45,
      whiteSpace: 'pre-wrap' as const
    },
    heroMutedChip: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.45rem 0.75rem',
      borderRadius: '9999px',
      backgroundColor: BRAND_SURFACE_SOFT,
      color: BRAND_MUTED,
      border: `1px solid ${BRAND_BORDER}`,
      fontSize: '0.82rem',
      fontWeight: 500,
      lineHeight: 1.35
    },
    heroActions: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.75rem',
      width: '100%',
      maxWidth: '560px',
      minWidth: '320px',
      marginLeft: 'auto',
      alignItems: 'flex-end'
    },
    heroActionButtonCompact: {
      width: '320px',
      minWidth: '220px',
      maxWidth: '320px',
      alignSelf: 'flex-end'
    },
    heroActionButtonPrimary: {
      background: BRAND_TEXT,
      border: `1px solid ${BRAND_TEXT}`,
      color: '#ffffff',
      borderRadius: '0.8rem',
      padding: '0.8rem 1.35rem',
      fontWeight: 700,
      cursor: 'pointer',
      minHeight: '2.85rem',
      boxShadow: '0 10px 20px -16px rgba(18, 63, 67, 0.48)'
    },
    heroActionButtonSecondary: {
      background: BRAND_CARD,
      border: `1px solid ${BRAND_BORDER}`,
      color: BRAND_TEXT,
      borderRadius: '0.8rem',
      padding: '0.8rem 1.35rem',
      fontWeight: 700,
      cursor: 'pointer',
      minHeight: '2.85rem',
      boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
    },
    heroActionNote: {
      margin: 0,
      maxWidth: '560px',
      fontSize: '0.8rem',
      color: BRAND_MUTED,
      lineHeight: 1.35,
      textAlign: 'right' as const
    },
    heroGrid: {
      display: 'grid',
      gap: '1rem',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'
    },
    heroFieldLabel: {
      fontSize: '0.75rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.08em',
      color: BRAND_MUTED,
      fontWeight: 700
    },
    heroFieldValue: {
      color: BRAND_TEXT,
      fontWeight: 600
    },
    heroDeviceIdentifiers: {
      marginTop: '0.45rem',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(110px, max-content))',
      gap: '0.55rem 1.4rem',
      alignItems: 'start'
    },
    heroDeviceIdentifier: {
      display: 'flex',
      flexDirection: 'column' as const,
      minWidth: 0,
      gap: '0.1rem'
    },
    heroDeviceIdentifierLabel: {
      fontSize: '0.68rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.08em',
      color: BRAND_MUTED,
      fontWeight: 700,
      lineHeight: 1.1
    },
    heroDeviceAssetValue: {
      color: BRAND_TEXT,
      fontWeight: 700,
      fontSize: '0.96rem',
      lineHeight: 1.2
    },
    heroDeviceSerialValue: {
      color: BRAND_TEXT,
      fontWeight: 500,
      fontSize: '0.96rem',
      lineHeight: 1.2
    },
    section: {
      backgroundColor: BRAND_CARD,
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '1rem',
      padding: '1.75rem',
      marginBottom: '1.5rem',
      boxShadow: '0 15px 35px -30px rgba(18, 63, 67, 0.2)'
    },
    acceptedNotice: {
      backgroundColor: BRAND_SUCCESS_SOFT,
      border: `1px solid ${BRAND_SUCCESS_BORDER}`,
      borderRadius: '1rem',
      padding: '1rem 1.25rem',
      marginBottom: '1.5rem',
      boxShadow: '0 1px 2px rgba(37, 98, 77, 0.12)'
    },
    acceptedNoticeTitle: {
      fontWeight: 600,
      color: BRAND_SUCCESS_TEXT,
      marginBottom: '0.35rem'
    },
    acceptedNoticeMeta: {
      fontSize: '0.875rem',
      color: BRAND_SUCCESS_TEXT,
      lineHeight: 1.5
    },
    draftRecoveredNotice: {
      marginBottom: '1rem',
      padding: '0.625rem 0.875rem',
      borderRadius: '0.5rem',
      border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
      backgroundColor: BRAND_TEXT_SOFT,
      color: BRAND_TEXT,
      fontSize: '0.875rem',
      lineHeight: 1.5
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: BRAND_TEXT,
      marginBottom: '1rem'
    },
    sectionDescription: {
      margin: '0 0 1rem',
      color: BRAND_MUTED,
      fontSize: '0.9rem',
      lineHeight: 1.5
    },
    acceptedBadge: {
      backgroundColor: BRAND_SUCCESS_SOFT,
      color: BRAND_SUCCESS_TEXT,
      border: `1px solid ${BRAND_SUCCESS_BORDER}`,
      borderRadius: '999px',
      padding: '0.25rem 0.75rem',
      fontSize: '0.75rem',
      fontWeight: 600
    },
    problemBox: {
      backgroundColor: BRAND_SURFACE_SOFT,
      border: `1px solid ${BRAND_BORDER}`,
      padding: '1rem',
      borderRadius: '0.375rem',
      marginBottom: '1rem'
    },
    instructionText: {
      fontSize: '0.875rem',
      color: BRAND_MUTED,
      fontStyle: 'italic',
      marginBottom: '0.5rem'
    },
    symptomList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    symptomItem: {
      padding: '0.5rem',
      marginBottom: '0.25rem',
      backgroundColor: BRAND_TEXT_SOFT,
      border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
      borderRadius: '0.375rem',
      color: BRAND_TEXT,
      fontSize: '0.875rem'
    },
    mediaSectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem',
      marginBottom: '1rem',
      flexWrap: 'wrap' as const
    },
    infoItem: {
      padding: '0.5rem 0',
      borderBottom: `1px solid ${BRAND_BORDER}`,
      fontSize: '0.875rem'
    },
    mediaGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '1rem'
    },
    mediaCard: {
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.9rem',
      padding: '0.9rem',
      backgroundColor: BRAND_SURFACE_SOFT,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.75rem',
      minHeight: '100%'
    },
    mediaPreview: {
      width: '100%',
      height: '160px',
      objectFit: 'cover' as const,
      borderRadius: '0.75rem',
      backgroundColor: BRAND_SURFACE,
      display: 'block'
    },
    mediaAudioPreview: {
      width: '100%'
    },
    mediaFilePreview: {
      width: '100%',
      minHeight: '160px',
      borderRadius: '0.75rem',
      background: `linear-gradient(135deg, ${BRAND_SURFACE_SOFT} 0%, ${BRAND_ACCENT_SOFT} 100%)`,
      color: BRAND_TEXT,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      fontSize: '0.95rem',
      fontWeight: 600,
      textAlign: 'center' as const
    },
    mediaFileName: {
      fontWeight: 600,
      color: BRAND_TEXT,
      wordBreak: 'break-word' as const
    },
    mediaMeta: {
      fontSize: '0.85rem',
      color: BRAND_MUTED
    },
    mediaLink: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '0.8rem',
      padding: '0.7rem 1.05rem',
      backgroundColor: BRAND_CARD,
      border: `1px solid ${BRAND_BORDER}`,
      color: BRAND_TEXT,
      fontSize: '0.9rem',
      fontWeight: 700,
      textDecoration: 'none',
      minHeight: '2.6rem',
      boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
    },
    mediaEmptyState: {
      border: `1px dashed ${BRAND_TEXT_SOFT_BORDER}`,
      borderRadius: '0.9rem',
      padding: '1.25rem',
      backgroundColor: BRAND_SURFACE_SOFT,
      color: BRAND_MUTED,
      fontSize: '0.95rem'
    },
    emptyState: {
      color: BRAND_MUTED,
      fontStyle: 'italic'
    },
    button: {
      padding: '0.7rem 1.2rem',
      borderRadius: '0.8rem',
      border: '1px solid transparent',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.2s',
      minWidth: '160px',
      minHeight: '2.7rem',
      textAlign: 'center' as const
    },
    disabledButton: {
      opacity: 0.55,
      cursor: 'not-allowed'
    },
    primaryButton: {
      backgroundColor: BRAND_TEXT,
      borderColor: BRAND_TEXT,
      color: 'white',
      boxShadow: '0 8px 18px -16px rgba(18, 63, 67, 0.48)'
    },
    secondaryButton: {
      backgroundColor: BRAND_CARD,
      borderColor: BRAND_BORDER,
      color: BRAND_TEXT,
      boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
    },
    outlineButton: {
      backgroundColor: BRAND_CARD,
      color: BRAND_TEXT,
      borderColor: BRAND_BORDER,
      boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
    },
    dangerOutlineButton: {
      backgroundColor: BRAND_DANGER_SOFT,
      color: BRAND_DANGER_TEXT,
      borderColor: BRAND_DANGER_BORDER
    },
    editActionButton: {
      backgroundColor: BRAND_ACCENT_SOFT,
      color: BRAND_ACCENT_TEXT,
      borderColor: BRAND_ACCENT
    },
    reportButton: {
      backgroundColor: BRAND_TEXT_SOFT,
      color: BRAND_TEXT,
      borderColor: BRAND_TEXT_SOFT_BORDER
    },
    regenerateButton: {
      backgroundColor: BRAND_SURFACE_SOFT,
      color: BRAND_TEXT,
      borderColor: BRAND_BORDER
    },
    restoreButton: {
      backgroundColor: BRAND_CARD,
      color: BRAND_TEXT,
      borderColor: BRAND_BORDER,
      boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
    },
    footerCloseButton: {
      backgroundColor: BRAND_CARD,
      borderColor: BRAND_BORDER,
      color: BRAND_TEXT,
      boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
    },
    dangerButton: {
      backgroundColor: BRAND_DANGER_TEXT,
      borderColor: BRAND_DANGER_TEXT,
      color: 'white'
    },
    successButton: {
      backgroundColor: BRAND_SUCCESS_TEXT,
      borderColor: BRAND_SUCCESS_TEXT,
      color: 'white'
    },
    warningButton: {
      backgroundColor: BRAND_ACCENT_SOFT,
      borderColor: BRAND_ACCENT,
      color: BRAND_ACCENT_TEXT
    },
    progressBar: {
      width: '100%',
      height: '8px',
      backgroundColor: BRAND_SURFACE,
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '0.5rem'
    },
    progressFill: {
      height: '100%',
      backgroundColor: BRAND_TEXT,
      transition: 'width 0.5s ease'
    },
    progressMessage: {
      fontSize: '0.875rem',
      color: BRAND_MUTED,
      marginTop: '0.75rem'
    },
    stageList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '0.75rem',
      marginTop: '1rem'
    },
    stageChip: (status: StageStatus) => ({
      borderRadius: '0.75rem',
      border: '1px solid',
      borderColor:
        status === 'complete'
          ? BRAND_SUCCESS_BORDER
          : status === 'active'
          ? BRAND_ACCENT
          : status === 'error'
          ? BRAND_DANGER_BORDER
          : BRAND_BORDER,
      backgroundColor:
        status === 'complete'
          ? BRAND_SUCCESS_SOFT
          : status === 'active'
          ? BRAND_ACCENT_SOFT
          : status === 'error'
          ? BRAND_DANGER_SOFT
          : BRAND_CARD,
      padding: '0.75rem',
      minHeight: '64px',
      display: 'flex',
      flexDirection: 'column' as const,
      justifyContent: 'space-between'
    }),
    stageChipHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    stageChipBadge: (status: StageStatus) => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1.5rem',
      height: '1.5rem',
      borderRadius: '9999px',
      fontSize: '0.85rem',
      fontWeight: 600,
      color:
        status === 'complete'
          ? BRAND_SUCCESS_TEXT
          : status === 'active'
          ? BRAND_ACCENT_TEXT
          : status === 'error'
          ? BRAND_DANGER_TEXT
          : BRAND_MUTED,
      backgroundColor:
        status === 'complete'
          ? BRAND_SUCCESS_SOFT
          : status === 'active'
          ? BRAND_CARD
          : status === 'error'
          ? BRAND_DANGER_SOFT
          : BRAND_SURFACE_SOFT
    }),
    stageChipLabel: {
      fontSize: '0.95rem',
      fontWeight: 600,
      color: BRAND_TEXT
    },
    stageChipDuration: {
      fontSize: '0.75rem',
      color: BRAND_MUTED,
      marginTop: '0.5rem'
    },
    errorBox: {
      backgroundColor: BRAND_DANGER_SOFT,
      border: `1px solid ${BRAND_DANGER_BORDER}`,
      borderRadius: '0.5rem',
      padding: '1.5rem',
      textAlign: 'center' as const
    },
    errorTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: BRAND_DANGER_TEXT,
      marginBottom: '0.5rem'
    },
    errorText: {
      color: BRAND_DANGER_TEXT,
      marginBottom: '1.5rem'
    },
    buttonGroup: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
      flexWrap: 'wrap' as const
    },
    buttonRow: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '0.75rem',
      justifyContent: 'flex-start',
      marginTop: '1.5rem'
    },
    causesList: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '1rem'
    },
    causeEmptyState: {
      padding: '1rem',
      borderRadius: '0.5rem',
      backgroundColor: BRAND_SURFACE_SOFT,
      border: `1px dashed ${BRAND_TEXT_SOFT_BORDER}`,
      color: BRAND_MUTED,
      fontSize: '0.9rem',
      textAlign: 'center' as const
    },
    causeCard: {
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.75rem',
      padding: '1rem',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      transition: 'all 0.2s',
      boxShadow: '0 8px 18px rgba(18, 63, 67, 0.04)'
    },
    causeHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'start',
      marginBottom: '0.5rem'
    },
    causeDescription: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.25rem'
    },
    causeTitle: {
      fontWeight: '600',
      fontSize: '1rem',
      color: BRAND_TEXT
    },
    causeSubtitle: {
      fontSize: '0.875rem',
      color: BRAND_MUTED
    },
    causeDetailList: {
      margin: '0.25rem 0 0 0',
      paddingLeft: '1.25rem',
      color: BRAND_MUTED,
      fontSize: '0.875rem'
    },
    probabilityBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    statusBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    procedureEditedBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '0.375rem',
      fontSize: '0.75rem',
      fontWeight: '500',
      backgroundColor: BRAND_ACCENT_SOFT,
      color: BRAND_ACCENT_TEXT,
      border: `1px solid ${BRAND_ACCENT}`
    },
    procedureRegeneratedBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '0.375rem',
      fontSize: '0.75rem',
      fontWeight: '500',
      backgroundColor: BRAND_TEXT_SOFT,
      color: BRAND_TEXT,
      border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`
    },
    techSuggestedBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '0.375rem',
      fontSize: '0.75rem',
      fontWeight: '500',
      backgroundColor: BRAND_SURFACE_SOFT,
      color: BRAND_TEXT,
      border: `1px solid ${BRAND_BORDER}`
    },
    secondPassBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '0.375rem',
      fontSize: '0.75rem',
      fontWeight: '500',
      backgroundColor: BRAND_ACCENT_SOFT,
      color: BRAND_ACCENT_TEXT,
      border: `1px solid ${BRAND_ACCENT}`
    },
    causeMeta: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'flex-end',
      gap: '0.5rem'
    },
    causeBadges: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap' as const,
      justifyContent: 'flex-end'
    },
    causeActions: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap' as const,
      justifyContent: 'flex-end'
    },
    causeActionButton: {
      padding: '0.45rem 0.8rem',
      fontSize: '0.78rem',
      fontWeight: 700,
      backgroundColor: BRAND_CARD,
      color: BRAND_TEXT,
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.65rem',
      cursor: 'pointer',
      boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
    },
    inactiveHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.75rem'
    },
    inactiveCard: {
      border: `1px solid ${BRAND_ACCENT}`,
      backgroundColor: BRAND_ACCENT_SOFT,
      borderRadius: '0.75rem',
      padding: '1rem',
      marginBottom: '0.75rem'
    },
    inactiveTitle: {
      fontWeight: '600',
      fontSize: '1rem',
      color: BRAND_ACCENT_TEXT
    },
    inactiveMeta: {
      fontSize: '0.8rem',
      color: BRAND_ACCENT_TEXT,
      marginTop: '0.25rem'
    },
    inactiveReason: {
      fontSize: '0.875rem',
      color: BRAND_TEXT,
      marginTop: '0.5rem'
    },
    testingPreview: {
      marginBottom: '0.25rem'
    },
    testingLabel: {
      fontSize: '0.75rem',
      fontWeight: '600',
      color: BRAND_TEXT,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
      marginBottom: '0.25rem'
    },
    testingList: {
      margin: 0,
      paddingLeft: '1.25rem',
      color: BRAND_TEXT,
      fontSize: '0.875rem',
      listStyle: 'none' as const
    },
    stepNumber: {
      fontWeight: 600,
      marginRight: '0.5rem'
    },
    inlineImageTextLink: {
      display: 'inline',
      border: 'none',
      background: 'none',
      padding: 0,
      margin: 0,
      color: BRAND_TEXT,
      cursor: 'pointer',
      textDecoration: 'underline',
      textUnderlineOffset: '0.12em',
      textAlign: 'left' as const,
      font: 'inherit',
      lineHeight: 'inherit'
    },
    referenceImageStrip: {
      marginTop: '0.65rem',
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '0.65rem'
    },
    referenceImageCard: {
      width: '164px',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.65rem',
      padding: 0,
      overflow: 'hidden',
      backgroundColor: BRAND_CARD,
      boxShadow: '0 6px 18px rgba(18, 63, 67, 0.08)',
      cursor: 'pointer',
      textAlign: 'left' as const,
      font: 'inherit',
      color: BRAND_TEXT
    },
    referenceImageThumb: {
      width: '100%',
      height: '112px',
      objectFit: 'contain' as const,
      display: 'block'
    },
    referenceImagePlaceholder: {
      width: '100%',
      height: '112px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: BRAND_SURFACE_SOFT,
      color: BRAND_MUTED,
      fontSize: '0.8rem',
      textAlign: 'center' as const,
      padding: '0.5rem'
    },
    previewBackdrop: {
      position: 'fixed' as const,
      inset: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.55)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
      padding: '1rem'
    },
    previewCard: {
      backgroundColor: BRAND_CARD,
      borderRadius: '8px',
      padding: '0.75rem',
      width: 'min(94vw, 1400px)',
      height: '90vh',
      maxHeight: '940px',
      boxShadow: '0 25px 65px rgba(15, 23, 42, 0.35)',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.65rem',
      overflow: 'hidden'
    },
    previewHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '0.65rem',
      alignItems: 'flex-start'
    },
    previewHeaderMain: {
      display: 'grid',
      gap: '0.25rem',
      minWidth: 0
    },
    previewHeaderActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.45rem',
      flexWrap: 'wrap' as const,
      justifyContent: 'flex-end'
    },
    previewTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.6rem',
      flexWrap: 'wrap' as const
    },
    previewImageLabel: {
      color: BRAND_MUTED,
      fontSize: '0.92rem',
      lineHeight: 1.35
    },
    previewClose: {
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_CARD,
      borderRadius: '0.5rem',
      padding: '0.4rem 0.75rem',
      cursor: 'pointer',
      fontWeight: 600,
      color: BRAND_TEXT
    },
    previewControls: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.45rem',
      flexWrap: 'wrap' as const,
      borderTop: `1px solid ${BRAND_BORDER}`,
      paddingTop: '0.55rem'
    },
    previewControlButton: {
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_CARD,
      borderRadius: '6px',
      padding: '0.32rem 0.58rem',
      cursor: 'pointer',
      fontWeight: 700,
      color: BRAND_TEXT,
      minWidth: '2.35rem'
    },
    previewControlButtonDisabled: {
      opacity: 0.45,
      cursor: 'not-allowed'
    },
    previewZoomSlider: {
      width: 'min(34vw, 420px)',
      maxWidth: '100%'
    },
    previewZoomValue: {
      color: BRAND_MUTED,
      fontSize: '0.9rem',
      minWidth: '3rem'
    },
    previewGalleryCount: {
      color: BRAND_MUTED,
      fontSize: '0.85rem',
      fontWeight: 600
    },
    previewGalleryStrip: {
      display: 'flex',
      gap: '0.45rem',
      overflowX: 'auto' as const,
      paddingBottom: '0.15rem'
    },
    previewGalleryThumbButton: {
      width: '86px',
      height: '60px',
      flex: '0 0 auto',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '6px',
      padding: 0,
      overflow: 'hidden',
      backgroundColor: BRAND_CARD,
      cursor: 'pointer'
    },
    previewGalleryThumbButtonActive: {
      borderColor: BRAND_TEXT,
      boxShadow: '0 0 0 2px rgba(18, 63, 67, 0.18)'
    },
    previewGalleryThumb: {
      width: '100%',
      height: '100%',
      objectFit: 'contain' as const,
      display: 'block'
    },
    previewGalleryThumbPlaceholder: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: BRAND_MUTED,
      backgroundColor: BRAND_SURFACE_SOFT,
      fontSize: '0.72rem'
    },
    previewImageStage: {
      flex: 1,
      minHeight: 0,
      overflow: 'auto' as const,
      backgroundColor: BRAND_SURFACE_SOFT,
      borderRadius: '6px',
      padding: '0.75rem'
    },
    previewImageCanvas: {
      minWidth: '100%',
      minHeight: '100%',
      display: 'block'
    },
    previewImage: {
      width: '100%',
      height: '100%',
      maxWidth: 'none',
      maxHeight: 'none',
      display: 'block',
      objectFit: 'contain' as const,
      backgroundColor: BRAND_SURFACE_SOFT
    },
    previewPlaceholder: {
      width: '100%',
      height: '320px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: BRAND_SURFACE_SOFT,
      color: BRAND_MUTED,
      fontSize: '0.95rem',
      borderRadius: '0.65rem'
    },
    testingMore: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: BRAND_MUTED
    },
    toolsAndParts: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '0.75rem',
      marginTop: '1rem'
    },
    listBox: {
      padding: '0.75rem 1rem',
      borderRadius: '0.375rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE_SOFT
    },
    resourceList: {
      margin: 0,
      padding: 0,
      listStyle: 'none' as const,
      display: 'grid',
      gap: '0.5rem'
    },
    resourceRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.4rem'
    },
    resourceContent: {
      display: 'grid',
      gap: 0,
      minWidth: 0
    },
    resourceMetaRow: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      alignItems: 'center',
      gap: '0.35rem 0.45rem',
      marginTop: '0.2rem'
    },
    resourceMainLine: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      alignItems: 'center',
      gap: '0.15rem 0.35rem',
      fontSize: '0.98rem',
      color: BRAND_TEXT,
      lineHeight: 1.35
    },
    resourceImplementedText: {
      textDecoration: 'line-through',
      textDecorationThickness: '1.5px',
      textDecorationColor: BRAND_MUTED,
      opacity: 0.7
    },
    resourcePartNumber: {
      fontWeight: 700,
      color: BRAND_TEXT
    },
    resourceDivider: {
      margin: '0 0.4rem',
      color: BRAND_MUTED
    },
    resourceFailureInline: {
      fontSize: '0.8rem',
      color: BRAND_ACCENT_TEXT,
      fontWeight: 600
    },
    resourceCoverageBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.12rem 0.5rem',
      borderRadius: '999px',
      fontSize: '0.72rem',
      fontWeight: 700,
      border: '1px solid transparent',
      whiteSpace: 'nowrap' as const
    },
    resourceCoverageBadgeCovered: {
      backgroundColor: BRAND_SUCCESS_SOFT,
      borderColor: BRAND_SUCCESS_BORDER,
      color: BRAND_SUCCESS_TEXT
    },
    resourceCoverageBadgeDiscounted: {
      backgroundColor: BRAND_ACCENT_SOFT,
      borderColor: '#E5C68B',
      color: BRAND_ACCENT_TEXT
    },
    resourceCoverageBadgeNotCovered: {
      backgroundColor: BRAND_DANGER_SOFT,
      borderColor: BRAND_DANGER_BORDER,
      color: BRAND_DANGER_TEXT
    },
    resourceKitBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '0.12rem 0.5rem',
      borderRadius: '999px',
      fontSize: '0.72rem',
      fontWeight: 700,
      backgroundColor: BRAND_TEXT_SOFT,
      border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
      color: BRAND_TEXT,
      whiteSpace: 'nowrap' as const
    },
    resourceKitNote: {
      fontSize: '0.78rem',
      color: BRAND_MUTED,
      lineHeight: 1.4
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1rem',
      marginTop: '1rem'
    },
    summaryCard: {
      backgroundColor: BRAND_SURFACE_SOFT,
      border: `1px solid ${BRAND_BORDER}`,
      padding: '1rem',
      borderRadius: '0.375rem',
      textAlign: 'center' as const
    },
    summaryNumber: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: BRAND_TEXT
    },
    summaryLabel: {
      fontSize: '0.875rem',
      color: BRAND_MUTED
    },
    summaryGuidance: {
      marginTop: '1rem',
      padding: '0.9rem 1rem',
      borderRadius: '0.75rem',
      backgroundColor: BRAND_TEXT_SOFT,
      border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
      color: BRAND_TEXT,
      fontSize: '0.92rem',
      lineHeight: 1.5
    },
    slaCoverageBanner: {
      marginTop: '0.85rem',
      padding: '0.85rem 1rem',
      borderRadius: '0.75rem',
      backgroundColor: BRAND_SURFACE_SOFT,
      border: `1px solid ${BRAND_BORDER}`,
      color: BRAND_TEXT,
      fontSize: '0.9rem',
      lineHeight: 1.5
    },
    slaCoverageBannerTitle: {
      fontSize: '0.8rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase' as const,
      color: BRAND_MUTED,
      marginBottom: '0.2rem'
    },
    kitRecommendationGrid: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.75rem',
      marginTop: '0.85rem'
    },
    kitRecommendationCard: {
      borderRadius: '0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_CARD,
      padding: '0.85rem 0.95rem',
      boxShadow: '0 10px 24px rgba(18, 63, 67, 0.08)'
    },
    kitRecommendationCardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '0.65rem'
    },
    kitRecommendationName: {
      fontSize: '0.96rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    kitRecommendationSubtitle: {
      marginTop: '0.15rem',
      fontSize: '0.78rem',
      color: BRAND_MUTED
    },
    kitRecommendationBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.18rem 0.55rem',
      borderRadius: '999px',
      fontSize: '0.72rem',
      fontWeight: 700,
      border: '1px solid transparent',
      whiteSpace: 'nowrap' as const
    },
    kitRecommendationBadgeQuoteKit: {
      backgroundColor: BRAND_TEXT_SOFT,
      borderColor: BRAND_TEXT_SOFT_BORDER,
      color: BRAND_TEXT
    },
    kitRecommendationBadgeMaintenance: {
      backgroundColor: BRAND_ACCENT_SOFT,
      borderColor: '#E5C68B',
      color: BRAND_ACCENT_TEXT
    },
    kitRecommendationMeta: {
      marginTop: '0.45rem',
      fontSize: '0.82rem',
      color: BRAND_MUTED,
      lineHeight: 1.45
    },
    kitRecommendationPanelGrid: {
      display: 'grid',
      gridTemplateColumns: 'minmax(220px, 0.85fr) minmax(320px, 1.15fr)',
      gap: '0.75rem',
      marginTop: '0.75rem'
    },
    kitRecommendationPanel: {
      borderRadius: '0.7rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE_SOFT,
      padding: '0.85rem 0.9rem'
    },
    kitRecommendationPanelHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '0.75rem'
    },
    kitRecommendationPanelTitle: {
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase' as const,
      color: BRAND_MUTED,
      marginBottom: '0.45rem'
    },
    kitRecommendationPanelValue: {
      fontSize: '1.2rem',
      fontWeight: 700,
      color: BRAND_TEXT,
      lineHeight: 1.2
    },
    kitRecommendationPanelBody: {
      marginTop: '0.45rem',
      fontSize: '0.84rem',
      lineHeight: 1.55,
      color: BRAND_TEXT
    },
    kitRecommendationPrice: {
      marginTop: '0.55rem',
      fontSize: '0.82rem',
      lineHeight: 1.5,
      color: BRAND_MUTED
    },
    kitRecommendationReason: {
      marginTop: '0.55rem',
      fontSize: '0.84rem',
      lineHeight: 1.5,
      color: BRAND_TEXT
    },
    kitRecommendationActionButton: {
      minWidth: '118px',
      minHeight: '2rem',
      padding: '0.35rem 0.7rem',
      fontSize: '0.76rem',
      borderRadius: '0.7rem'
    },
    modal: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.45)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      overflowY: 'auto' as const,
      padding: '3rem 1rem',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: BRAND_CARD,
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.75rem',
      padding: '1.75rem',
      maxWidth: '720px',
      width: '100%',
      boxSizing: 'border-box' as const,
      boxShadow: '0 24px 40px rgba(18, 63, 67, 0.18)'
    },
    modalBody: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '1.5rem'
    },
    modalFooter: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: '0.75rem',
      marginTop: '1.5rem'
    },
    modalHeader: {
      marginBottom: '1rem'
    },
    modalTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: BRAND_TEXT,
      marginBottom: '0.5rem'
    },
    modalEstimate: {
      display: 'inline-block',
      backgroundColor: BRAND_SURFACE_SOFT,
      color: BRAND_TEXT,
      border: `1px solid ${BRAND_BORDER}`,
      fontWeight: 600,
      borderRadius: '999px',
      padding: '0.3rem 0.85rem',
      marginBottom: '1rem',
      fontSize: '0.875rem'
    },
    testProcedure: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '1.25rem'
    },
    textarea: {
      width: '100%',
      padding: '0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.375rem',
      backgroundColor: BRAND_CARD,
      color: BRAND_TEXT,
      minHeight: '140px',
      resize: 'vertical' as const
    },
    readOnlyField: {
      backgroundColor: BRAND_SURFACE_SOFT,
      color: BRAND_MUTED,
      cursor: 'not-allowed'
    },
    testHistory: {
      marginTop: '1rem',
      borderTop: `1px solid ${BRAND_BORDER}`,
      paddingTop: '1rem'
    },
    testStep: {
      padding: '0.75rem',
      backgroundColor: BRAND_SURFACE_SOFT,
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.375rem',
      marginBottom: '0.5rem',
      fontSize: '0.875rem'
    },
    inlineNotice: {
      backgroundColor: BRAND_ACCENT_SOFT,
      color: BRAND_ACCENT_TEXT,
      border: `1px solid ${BRAND_ACCENT}`,
      padding: '0.75rem 1rem',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      marginBottom: '1rem'
    },
    verificationReuseHint: {
      backgroundColor: BRAND_TEXT_SOFT,
      color: BRAND_TEXT,
      border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
      borderRadius: '0.375rem',
      padding: '0.55rem 0.75rem',
      fontSize: '0.82rem',
      marginBottom: '0.85rem'
    },
    planAssistantContainer: {
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE_SOFT,
      borderRadius: '0.5rem',
      padding: '0.75rem'
    },
    planAssistantHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.75rem'
    },
    planAssistantTitle: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: BRAND_TEXT
    },
    planAssistantSub: {
      marginTop: '0.25rem',
      fontSize: '0.78rem',
      color: BRAND_MUTED
    },
    planAssistantBody: {
      marginTop: '0.75rem',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.65rem'
    },
    planAssistantSelect: {
      width: '100%',
      padding: '0.55rem 0.65rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.375rem',
      fontSize: '0.9rem',
      backgroundColor: BRAND_CARD,
      color: BRAND_TEXT
    },
    planAssistantInput: {
      width: '100%',
      padding: '0.6rem 0.7rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.375rem',
      backgroundColor: BRAND_CARD,
      color: BRAND_TEXT,
      fontSize: '0.9rem'
    },
    planAssistantActions: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap' as const
    },
    planAssistantMessages: {
      maxHeight: '380px',
      minHeight: '170px',
      overflowY: 'auto' as const,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.5rem',
      padding: '0.2rem 0.1rem'
    },
    planAssistantMessage: {
      borderRadius: '0.45rem',
      padding: '0.55rem 0.65rem',
      fontSize: '0.86rem',
      lineHeight: 1.55,
      whiteSpace: 'pre-wrap' as const,
      wordBreak: 'break-word' as const
    },
    planAssistantUser: {
      backgroundColor: BRAND_TEXT_SOFT,
      color: BRAND_TEXT
    },
    planAssistantAssistant: {
      backgroundColor: BRAND_CARD,
      color: BRAND_TEXT,
      border: `1px solid ${BRAND_BORDER}`
    },
    planAssistantError: {
      color: BRAND_DANGER_TEXT,
      backgroundColor: BRAND_DANGER_SOFT,
      border: `1px solid ${BRAND_DANGER_BORDER}`,
      borderRadius: '0.375rem',
      padding: '0.45rem 0.6rem',
      fontSize: '0.82rem'
    },
    inactivationPanel: {
      backgroundColor: BRAND_ACCENT_SOFT,
      border: `1px solid ${BRAND_ACCENT}`,
      borderRadius: '0.5rem',
      padding: '1rem',
      marginBottom: '1.5rem'
    },
    secondaryText: {
      color: BRAND_MUTED,
      fontSize: '0.875rem',
      marginBottom: '0.75rem'
    },
    editPanel: {
      backgroundColor: BRAND_SURFACE_SOFT,
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.5rem',
      padding: '1rem',
      marginBottom: '1.5rem',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '1rem'
    },
    editLabel: {
      display: 'block',
      marginBottom: '0.4rem',
      fontWeight: 600,
      color: BRAND_TEXT
    },
    editSection: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.5rem'
    },
    fieldHint: {
      fontSize: '0.75rem',
      color: BRAND_MUTED
    },
    inlineInfo: {
      marginTop: '1rem',
      backgroundColor: BRAND_SURFACE_SOFT,
      color: BRAND_TEXT,
      border: `1px solid ${BRAND_BORDER}`,
      padding: '0.75rem 1rem',
      borderRadius: '0.375rem',
      fontSize: '0.875rem'
    },
    suggestionBox: {
      marginTop: '1.5rem',
      padding: '1rem',
      backgroundColor: BRAND_SURFACE_SOFT,
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.5rem',
      textAlign: 'center' as const
    },
    suggestionResult: {
      marginTop: '1rem',
      padding: '1rem',
      borderRadius: '0.375rem',
      fontSize: '0.875rem'
    },
    acceptedResult: {
      backgroundColor: BRAND_SUCCESS_SOFT,
      color: BRAND_SUCCESS_TEXT
    },
    attentionResult: {
      backgroundColor: BRAND_ACCENT_SOFT,
      color: BRAND_ACCENT_TEXT
    },
    rejectedResult: {
      backgroundColor: BRAND_DANGER_SOFT,
      color: BRAND_DANGER_TEXT
    },
    closeButton: {
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: BRAND_MUTED
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.375rem',
      backgroundColor: BRAND_CARD,
      color: BRAND_TEXT,
      fontSize: '1rem'
    },
    select: {
      padding: '0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.375rem',
      fontSize: '1rem',
      backgroundColor: BRAND_CARD,
      color: BRAND_TEXT
    }
  };

  const getProbabilityColor = (probability: string) => {
    switch (probability) {
      case 'HIGH': return BRAND_DANGER_TEXT;
      case 'MEDIUM': return BRAND_ACCENT_TEXT;
      case 'LOW': return BRAND_SUCCESS_TEXT;
      default: return BRAND_MUTED;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PASSED': return BRAND_SUCCESS_TEXT;
      case 'FAILED': return BRAND_DANGER_TEXT;
      case 'N/A': return BRAND_MUTED;
      case 'INACTIVE': return BRAND_ACCENT_TEXT;
      default: return BRAND_MUTED;
    }
  };

  const getTestStepLabel = (step: TestStep): string => {
    switch (step.action) {
      case 'TEST':
        return step.result || 'TESTED';
      case 'EDIT_PROCEDURE':
        return 'Procedure Edited';
      case 'INACTIVATE':
        return 'Inactivated';
      case 'REACTIVATE':
        return 'Reactivated';
      case 'RESTORE':
        return 'Procedure Restored';
      case 'REGENERATE':
        return 'Procedure Regenerated';
      default:
        return step.action;
    }
  };

  const getTestStepColor = (step: TestStep): string => {
    if (step.action === 'TEST') {
      if (step.result === 'PASS') return BRAND_SUCCESS_TEXT;
      if (step.result === 'FAIL') return BRAND_DANGER_TEXT;
      return BRAND_MUTED;
    }
    if (step.action === 'EDIT_PROCEDURE') return BRAND_TEXT;
    if (step.action === 'INACTIVATE') return BRAND_ACCENT_TEXT;
    if (step.action === 'RESTORE') return BRAND_TEXT;
    if (step.action === 'REGENERATE') return BRAND_TEXT;
    return BRAND_SUCCESS_TEXT;
  };

  const StepList: React.FC<{ nodes: StepNode[] }> = ({ nodes }) => (
    <ol style={styles.testingList}>
      {nodes.map(node => (
        <li key={node.number} style={{ marginBottom: '0.35rem' }}>
          <span style={styles.stepNumber}>{node.number}</span>
          <span>{node.description}</span>
          {node.subSteps && node.subSteps.length > 0 && (
            <div style={{ marginTop: '0.35rem', paddingLeft: '1.25rem' }}>
              <StepList nodes={node.subSteps} />
            </div>
          )}
        </li>
      ))}
    </ol>
  );

  const renderStageBadgeSymbol = (status: StageStatus): string => {
    if (status === 'complete') return '\u2713';
    if (status === 'active') return '...';
    if (status === 'error') return '!';
    return '\u25CF';
  };

  const displayStageStatuses = useMemo<Record<AnalysisStageId, StageStatus>>(() => {
    let furthestReached = -1;
    STAGE_SEQUENCE.forEach(stage => {
      const status = stageStates[stage.id]?.status || 'pending';
      if (status === 'active' || status === 'complete' || status === 'error') {
        furthestReached = Math.max(furthestReached, STAGE_INDEX_MAP[stage.id]);
      }
    });

    const next = {} as Record<AnalysisStageId, StageStatus>;
    STAGE_SEQUENCE.forEach(stage => {
      const status = stageStates[stage.id]?.status || 'pending';
      const stageIndex = STAGE_INDEX_MAP[stage.id];
      if (status === 'error') {
        next[stage.id] = 'error';
      } else if (stageIndex < furthestReached) {
        next[stage.id] = 'complete';
      } else {
        next[stage.id] = status;
      }
    });
    return next;
  }, [stageStates]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading case data...</div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div style={styles.container}>
        <div style={{ padding: '2rem', textAlign: 'center', color: BRAND_DANGER_TEXT }}>Case not found</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.topBar}>
          <button style={styles.backLink} onClick={() => navigate('/tech')}>
            Back to Cases
          </button>
          <div style={styles.topBarActions}>
            <div style={styles.saveMeta}>{saveAnalysisMetaLabel}</div>
            <button
              style={{
                ...styles.heroActionButtonPrimary,
                opacity: saveAnalysisDisabled ? 0.65 : 1,
                cursor: saveAnalysisDisabled ? 'not-allowed' : 'pointer'
              }}
              onClick={handleAcceptAnalysis}
              disabled={saveAnalysisDisabled}
            >
              {acceptingAnalysis ? 'Saving...' : 'Save Analysis'}
            </button>
          </div>
        </div>

        <div style={styles.heroCard}>
          <div style={styles.heroHeader}>
            <div style={styles.heroHeadingBlock}>
              <div style={styles.heroTitleRow}>
                <div style={styles.heroTitle}>Troubleshooting From a Distance (TSFAD)</div>
                {aiModel && <span style={styles.heroMetaPill}>AI: {aiModel}</span>}
              </div>
              <div style={styles.heroProblem}>{caseHeaderProblemSummary}</div>

            </div>

            <div style={styles.heroActions}>
              <button
                style={{
                  ...styles.heroActionButtonPrimary,
                  ...styles.heroActionButtonCompact,
                  opacity: analysisStarted || analysisLocked ? 0.75 : 1,
                  cursor: analysisStarted || analysisLocked ? 'not-allowed' : 'pointer'
                }}
                onClick={startAnalysis}
                disabled={analysisStarted || analysisLocked}
              >
                {heroActionLabel}
              </button>
              <p style={styles.heroActionNote}>
                {analysisLocked
                  ? 'A saved TSFAD plan already exists for this case. Re-run TSFAD to generate a fresh plan when new information is available.'
                  : analysisStarted && !analysisComplete
                  ? 'The analysis is currently running. Progress updates will appear below.'
                  : 'Troubleshooting From a Distance (TSFAD) typically finishes within about 2 to 3 minutes, but please allow up to 5 minutes for complex cases.'}
              </p>
            </div>
          </div>

          <div style={styles.heroGrid}>
            <div>
              <div style={styles.heroFieldLabel}>Case</div>
              <div style={styles.heroFieldValue}>{caseDisplayNumber || 'Unknown case'}</div>
            </div>
            <div>
              <div style={styles.heroFieldLabel}>Customer</div>
              <div style={styles.heroFieldValue}>{caseHeaderCustomerName}</div>
            </div>
            <div>
              <div style={styles.heroFieldLabel}>Device</div>
              <div style={styles.heroFieldValue}>{caseHeaderDeviceModel}</div>
              {(caseHeaderDeviceAssetNumber || caseHeaderDeviceSerialNumber) && (
                <div style={styles.heroDeviceIdentifiers}>
                  {caseHeaderDeviceAssetNumber && (
                    <span style={styles.heroDeviceIdentifier}>
                      <span style={styles.heroDeviceIdentifierLabel}>Asset</span>
                      <span style={styles.heroDeviceAssetValue}>{caseHeaderDeviceAssetNumber}</span>
                    </span>
                  )}
                  {caseHeaderDeviceSerialNumber && (
                    <span style={styles.heroDeviceIdentifier}>
                      <span style={styles.heroDeviceIdentifierLabel}>Serial</span>
                      <span style={styles.heroDeviceSerialValue}>{caseHeaderDeviceSerialNumber}</span>
                    </span>
                  )}
                </div>
              )}
            </div>
            <div>
              <div style={styles.heroFieldLabel}>Location</div>
              <div style={styles.heroFieldValue}>{caseHeaderLocationName}</div>
            </div>
            <div>
              <div style={styles.heroFieldLabel}>Contact</div>
              <div style={styles.heroFieldValue}>{caseHeaderContactName}</div>
            </div>
          </div>
        </div>

        {/* Attached Media Section */}
        <div style={styles.section}>
          <div style={styles.mediaSectionHeader}>
            <div>
              <h3 style={styles.sectionTitle}>Attached Media</h3>
              <p style={styles.sectionDescription}>
                Review any photos, recordings, or supporting files shared with this case before testing.
              </p>
            </div>
            <span style={styles.heroMutedChip}>
              {mediaItems.length} attachment{mediaItems.length === 1 ? '' : 's'}
            </span>
          </div>

          {mediaItems.length > 0 ? (
            <div style={styles.mediaGrid}>
              {mediaItems.map((media, index) => {
                const url = resolveCaseMediaUrl(media);
                const category = resolveMediaCategory(media);
                return (
                  <div key={media.id || `${media.fileName}-${index}`} style={styles.mediaCard}>
                    {category === 'image' && (
                      <img src={url} alt={media.fileName || 'Attachment'} style={styles.mediaPreview} />
                    )}
                    {category === 'video' && (
                      <video src={url} controls style={styles.mediaPreview} />
                    )}
                    {category === 'audio' && (
                      <audio src={url} controls style={styles.mediaAudioPreview} />
                    )}
                    {category === 'file' && (
                      <div style={styles.mediaFilePreview}>File attached</div>
                    )}
                    <div style={styles.mediaFileName}>{media.fileName || media.fileType || 'Attachment'}</div>
                    <div style={styles.mediaMeta}>
                      {(media.mimeType || media.fileType || 'file').toString()} | {formatMediaFileSize(media.fileSize)}
                    </div>
                    <a href={url} target="_blank" rel="noopener noreferrer" style={styles.mediaLink}>
                      Open attachment
                    </a>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.mediaEmptyState}>No media files attached.</div>
          )}
        </div>

        {isAnalysisAccepted && (
          <div style={styles.acceptedNotice}>
            <div style={styles.acceptedNoticeTitle}>Troubleshooting From a Distance (TSFAD) saved</div>
            <div style={styles.acceptedNoticeMeta}>
              {acceptedMetaDescription}
              {acceptedMetaDescription ? '. ' : ''}
              To refresh the plan, restart TSFAD from the case once new information is available.
            </div>
          </div>
        )}

        {/* Start Analysis Button or Error */}
        {false && !analysisStarted && !analysisComplete && (
          <div style={{ display: 'none' }}>
            <p style={{ color: '#6b7280', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
              {analysisLocked
                ? 'A saved TSFAD plan already exists for this case. Use “Re-run TSFAD” from the case list to generate a fresh plan.'
                : 'Troubleshooting From a Distance (TSFAD) typically finishes within about 2 to 3 minutes, but please allow up to 5 minutes for complex cases.'}
            </p>
            <button
              style={{
                ...styles.button,
                ...styles.primaryButton,
                ...(analysisLocked ? styles.disabledButton : {})
              }}
              onClick={startAnalysis}
              disabled={analysisLocked}
            >
              Start Troubleshooting From a Distance (TSFAD)
            </button>
          </div>
        )}

        {/* Error State - User Friendly */}
        {error && error !== 'Knowledge Coverage Issue' && (
          <div style={styles.section}>
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                {error === 'No Documentation Available'
                  ? 'DOC'
                  : error === 'Task Assignment Required'
                  ? 'TASK'
                  : error === 'Knowledge Coverage Issue'
                  ? 'PATH'
                  : 'ERR'}
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: BRAND_TEXT }}>
                {error === 'No Documentation Available'
                  ? 'Documentation Required'
                  : error === 'Task Assignment Required'
                  ? 'Task Assignment Required'
                  : error === 'Knowledge Coverage Issue'
                  ? 'Knowledge Coverage Alert'
                  : 'Analysis Failed'}
              </h2>
              <p style={{ fontSize: '1.125rem', color: BRAND_MUTED, marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
                {error === 'No Documentation Available' 
                  ? `Technical documentation needs to be uploaded for the ${caseData?.device?.modelNumber} before Troubleshooting From a Distance (TSFAD) can be performed.`
                  : errorDetails || 'An error occurred during analysis.'}
              </p>
              
              <div style={styles.buttonRow}>
                {error === 'No Documentation Available' && (
                  <button
                    style={{ ...styles.button, ...styles.primaryButton }}
                    onClick={() => {
                      navigate('/tech', { state: { activeTab: 'documents' } });
                    }}
                  >
                    Go to Documents
                  </button>
                )}
                {error === 'Knowledge Coverage Issue' && (
                  <button
                    style={{ ...styles.button, ...styles.primaryButton }}
                    onClick={openKnowledgeCoverageFeedbackModal}
                  >
                    Send Feedback
                  </button>
                )}
                <button
                  style={{
                    ...styles.button,
                    ...(error === 'No Documentation Available' || error === 'Knowledge Coverage Issue'
                      ? styles.secondaryButton
                      : styles.primaryButton)
                  }}
                  onClick={() => navigate('/tech')}
                >
                  Back to Cases
                </button>
              </div>
              
              {error === 'No Documentation Available' && (
                <p style={{ fontSize: '0.875rem', color: BRAND_MUTED, marginTop: '2rem', fontStyle: 'italic' }}>
                  Tip: You may want to check with HQ for documentation if not readily available.
                </p>
              )}
              {error === 'Knowledge Coverage Issue' && (
                <p style={{ fontSize: '0.875rem', color: BRAND_MUTED, marginTop: '2rem', fontStyle: 'italic' }}>
                  Tip: You can refine the problem description and retry, but feedback helps the knowledge team fix missing path tagging.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Analysis Progress */}
        {analysisStarted && !analysisComplete && !error && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Analyzing Problem...</h3>
            <div style={styles.progressBar}>
              <div 
                style={{ ...styles.progressFill, width: `${analysisProgress}%` }}
              />
            </div>
            <p style={styles.progressMessage}>{activeStageMessage}</p>
            <div style={styles.stageList}>
              {STAGE_SEQUENCE.filter(stage => stage.id !== 'COMPLETE').map(stage => {
                const current = stageStates[stage.id];
                const status: StageStatus = displayStageStatuses[stage.id] || 'pending';
                const displayLabel =
                  status === 'complete'
                    ? stage.completedLabel || stage.label
                    : stage.label;
                const durationText =
                  status === 'complete' && typeof current?.durationMs === 'number'
                    ? formatDuration(current.durationMs)
                    : '';
                return (
                  <div key={stage.id} style={styles.stageChip(status)}>
                    <div style={styles.stageChipHeader}>
                      <span style={styles.stageChipBadge(status)}>
                        {renderStageBadgeSymbol(status)}
                      </span>
                      <span style={styles.stageChipLabel}>{displayLabel}</span>
                    </div>
                    {durationText && <span style={styles.stageChipDuration}>{durationText}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Possible Causes */}
        {analysisComplete && (
          <>
            <div style={styles.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>
                  Possible Causes ({getRemainingCauses().length} remaining)
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    style={{ ...styles.button, ...styles.secondaryButton, padding: '0.5rem 1rem' }}
                    onClick={handleCheckMissedCauses}
                    disabled={missedCausesLoading}
                  >
                    {missedCausesLoading ? 'Checking…' : 'Check for Additional Causes'}
                  </button>
                  <button
                    style={{ ...styles.button, ...styles.successButton, padding: '0.5rem 1rem' }}
                    onClick={() => {
                      setSuggestion('');
                      setSuggestionResult(null);
                      setSuggestionLoading(false);
                      setShowSuggestionModal(true);
                    }}
                  >
                    Suggest a Cause
                  </button>
                </div>
              </div>
              
              <div style={styles.causesList}>
                {activeCauses.length === 0 ? (
                  <div style={styles.causeEmptyState}>
                    No active causes. Reactivate an inactive cause or suggest a new one.
                  </div>
                ) : (
                  activeCauses.map(cause => {
                    const isCompleted = cause.status !== 'UNTESTED';
                    const backgroundColor = isCompleted
                      ? cause.status === 'PASSED'
                        ? BRAND_SUCCESS_SOFT
                        : cause.status === 'FAILED'
                        ? BRAND_DANGER_SOFT
                        : cause.status === 'N/A'
                        ? '#ffffff'
                        : '#ffffff'
                      : '#ffffff';
                    const borderColor = isCompleted
                      ? cause.status === 'PASSED'
                        ? BRAND_SUCCESS_BORDER
                        : cause.status === 'FAILED'
                        ? BRAND_DANGER_BORDER
                        : cause.status === 'N/A'
                        ? BRAND_BORDER
                        : BRAND_BORDER
                      : BRAND_BORDER;
                    const title = formatCauseTitle(cause.description, cause.shortTitle);
                    const detailItems =
                      cause.details && cause.details.length > 0
                        ? cause.details
                        : cause.description
                        ? [cause.description]
                        : [];

                    return (
                      <div
                        key={cause.id}
                        style={{
                          ...styles.causeCard,
                          backgroundColor,
                          borderColor,
                          borderWidth: isCompleted ? '2px' : '1px',
                          opacity: cause.status === 'N/A' ? 0.75 : 1
                        }}
                        onClick={() => performTest(cause)}
                      >
                        <div style={styles.causeHeader}>
                          <div style={styles.causeDescription}>
                            <div style={styles.causeTitle}>{title}</div>
                            {detailItems.length === 1 ? (
                              <div style={styles.causeSubtitle}>{detailItems[0]}</div>
                            ) : (
                              <ul style={styles.causeDetailList}>
                                {detailItems.map((detail, index) => (
                                  <li key={index}>{detail}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div style={styles.causeMeta}>
                            <div style={styles.causeBadges}>
                              {!cause.unverifiedPlaceholder && (
                                <span
                                  style={{
                                    ...styles.probabilityBadge,
                                    backgroundColor: `${getProbabilityColor(cause.probability)}20`,
                                    color: getProbabilityColor(cause.probability)
                                  }}
                                >
                                  {cause.probability} PROBABILITY
                                </span>
                              )}
                              {cause.status !== 'UNTESTED' && (
                                <span
                                  style={{
                                    ...styles.statusBadge,
                                    backgroundColor: `${getStatusColor(cause.status)}20`,
                                    color: getStatusColor(cause.status)
                                  }}
                                >
                                  {cause.status === 'N/A' ? 'NOT APPLICABLE' : cause.status}
                                </span>
                              )}
                              {cause.procedureRegeneratedAt && !cause.procedureEditedByTech && (
                                <span style={styles.procedureRegeneratedBadge}>
                                  Procedure Regenerated
                                </span>
                              )}
                              {cause.procedureEditedByTech && (
                                <span style={styles.procedureEditedBadge}>
                                  Procedure Edited
                                </span>
                              )}
                              {cause.techSuggested && (
                                <span style={styles.techSuggestedBadge}>
                                  Tech Suggested
                                </span>
                              )}
                              {cause.secondPass && !cause.techSuggested && (
                                <span style={styles.secondPassBadge}>
                                  2nd Pass
                                </span>
                              )}
                              {cause.unverifiedPlaceholder && (
                                <span style={styles.procedureRegeneratedBadge}>
                                  Unverified — No Action Plan
                                </span>
                              )}
                              {causeDetailLoadingId === cause.id && (
                                <span style={styles.procedureRegeneratedBadge}>
                                  Loading action plan...
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {inactiveCauses.length > 0 && (
              <div style={styles.section}>
                <div style={styles.inactiveHeader}>
                  <h4 style={{ margin: 0 }}>
                    Inactive Causes ({inactiveCauses.length})
                  </h4>
                  <button
                    style={{ ...styles.button, ...styles.secondaryButton, padding: '0.35rem 0.75rem' }}
                    onClick={() => setInactivePanelOpen(prev => !prev)}
                  >
                    {inactivePanelOpen ? 'Hide' : 'Show'}
                  </button>
                </div>

                {inactivePanelOpen && (
                  <div>
                    {inactiveCauses.map(cause => {
                      const title = formatCauseTitle(cause.description, cause.shortTitle);
                      const history = cause.inactiveHistory || [];
                      const latestEntry = history[history.length - 1];
                      const inactiveTimestamp =
                        cause.inactiveAt || latestEntry?.timestamp ? new Date(cause.inactiveAt || latestEntry?.timestamp || '').toLocaleString() : null;
                      const inactiveReason = cause.inactiveReason || latestEntry?.reason || 'No reason recorded';

                      return (
                        <div key={cause.id} style={styles.inactiveCard}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={styles.inactiveTitle}>{title}</div>
                              {inactiveTimestamp && (
                                <div style={styles.inactiveMeta}>
                                  Inactivated: {inactiveTimestamp}
                                </div>
                              )}
                              <div style={styles.inactiveReason}>
                                Reason: {inactiveReason}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <button
                                style={{ ...styles.button, ...styles.secondaryButton }}
                                onClick={() => performTest(cause)}
                                disabled={causeDetailLoadingId === cause.id}
                              >
                                {causeDetailLoadingId === cause.id ? 'Loading...' : 'View Details'}
                              </button>
                              <button
                                style={{ ...styles.button, ...styles.warningButton }}
                                onClick={() => reactivateCause(cause)}
                              >
                                Reactivate
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Summary Section */}
            <div style={styles.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={styles.sectionTitle}>Test Plan Summary</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <button
                    style={{ ...styles.button, ...styles.secondaryButton }}
                    onClick={openQuoteFromTSFAD}
                    disabled={quoteLoading || !quoteAnalysisId}
                  >
                    {quoteLoading ? 'Preparing Quotation...' : 'Create Quotation'}
                  </button>
                  {analysisComplete &&
                    quoteAnalysisId &&
                    (savedQuotes.length > 0 || savedQuotesLoadFailed) && (
                    <button
                      style={{ ...styles.button, ...styles.secondaryButton }}
                      onClick={loadSavedQuotes}
                      disabled={savedQuotesLoading}
                    >
                      {savedQuotesLoading ? 'Loading Saved Quotes...' : 'Load Saved Quotes'}
                    </button>
                  )}
                </div>
              </div>
              {quoteError && (
                <div style={{ marginBottom: '1rem', color: BRAND_DANGER_TEXT, fontSize: '0.875rem' }}>
                  {quoteError}
                </div>
              )}
              {draftRestoredAt && (
                <div style={styles.draftRecoveredNotice}>
                  Recovered unsaved local TSFAD edits from {new Date(draftRestoredAt).toLocaleString()}.
                </div>
              )}

              <div style={styles.summaryGrid}>
                <div style={styles.summaryCard}>
                  <div style={styles.summaryNumber}>{getEstimatedTimeOnSite()}</div>
                  <div style={styles.summaryLabel}>Estimated Minutes On Site</div>
                </div>
                <div style={styles.summaryCard}>
                  <div style={styles.summaryNumber}>{getRemainingCauses().length}</div>
                  <div style={styles.summaryLabel}>Causes to Test</div>
                </div>
                <div style={styles.summaryCard}>
                  <div style={styles.summaryNumber}>{getFailedCauses().length}</div>
                  <div style={styles.summaryLabel}>Confirmed Issues</div>
                </div>
              </div>
              {savedQuotesLoaded && savedQuotes.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '0.5rem' }}>
                    Saved Quotes ({savedQuotes.length})
                  </h4>
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    {savedQuotes.map((quote, index) => {
                      const quoteStatus = normaliseQuoteStatusValue(quote?.status);
                      const inactiveFromStatus = normaliseQuoteStatusValue(quote?.inactiveFromStatus);
                      const isDraft = quoteStatus === 'DRAFT';
                      const isSentAwaitingResponse = quoteStatus === 'SENT' && !quote.customerDecision;
                      const canReactivate = quoteStatus === 'INACTIVE' && inactiveFromStatus === 'SENT';
                      const quoteDecisionLabel = getQuoteDecisionLabel(quote);
                      const quoteDecisionBadgeStyle = getQuoteDecisionBadgeStyle(quote);
                      const quoteStatusBadgeStyle = getQuoteStatusBadgeStyle(quote);
                      const showDecisionChip =
                        Boolean(quoteDecisionLabel) &&
                        !['ACCEPTED', 'DENIED', 'ADJUST_REQUESTED'].includes(quoteStatus);
                      const actionBusy =
                        Boolean(quoteSendingId) ||
                        quoteLoading ||
                        Boolean(quoteStateChangeId) ||
                        Boolean(quoteDeleteId);
                      return (
                      <div
                        key={quote.quoteId || `${quote.title}-${index}`}
                        style={{
                          border: `1px solid ${BRAND_BORDER}`,
                          backgroundColor: BRAND_SURFACE_SOFT,
                          borderRadius: '0.75rem',
                          padding: '0.625rem 0.75rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '0.75rem'
                        }}
                      >
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <div style={{ fontWeight: 600 }}>{quote.title}</div>
                            <span
                              style={quoteStatusBadgeStyle}
                            >
                              {getQuoteStatusLabel(quote)}
                            </span>
                            {showDecisionChip && (
                              <span style={quoteDecisionBadgeStyle}>
                                {quoteDecisionLabel}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: BRAND_MUTED }}>
                            {quote.savedAt ? new Date(quote.savedAt).toLocaleString() : 'Saved'} | Total $
                            {Number(quote?.totals?.grandTotal || 0).toFixed(2)}
                          </div>
                          {quote.customerDecision && (
                            <div style={{ fontSize: '0.78rem', color: quoteDecisionBadgeStyle.color || BRAND_TEXT, marginTop: '0.25rem' }}>
                              Decision: {quote.customerDecision}
                              {quote.decisionAt ? ` (${new Date(quote.decisionAt).toLocaleString()})` : ''}
                              {quote.decisionBy ? ` by ${quote.decisionBy}` : ''}
                            </div>
                          )}
                          {quoteStatus === 'INACTIVE' && quote?.inactiveReason && (
                            <div style={{ fontSize: '0.78rem', color: BRAND_ACCENT_TEXT, marginTop: '0.25rem' }}>
                              Inactive reason: {quote.inactiveReason}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          {isDraft && (
                            <button
                              style={{
                                ...styles.button,
                                ...styles.primaryButton,
                                minWidth: '110px',
                                padding: '0.45rem 0.75rem'
                              }}
                              onClick={() => sendSavedQuote(quote)}
                              disabled={actionBusy}
                            >
                              {quoteSendingId === quote.quoteId ? 'Sending...' : 'Send'}
                            </button>
                          )}
                          {isSentAwaitingResponse && (
                              <button
                                style={{
                                  ...styles.button,
                                  ...styles.warningButton,
                                  minWidth: '110px',
                                  padding: '0.45rem 0.75rem'
                                }}
                                onClick={() => openSavedQuoteInactivateModal(quote)}
                                disabled={actionBusy}
                              >
                                {quoteStateChangeId === quote.quoteId ? 'Updating...' : 'Inactivate'}
                              </button>
                          )}
                          {isDraft && (
                            <button
                              style={{
                                ...styles.button,
                                ...styles.dangerButton,
                                minWidth: '110px',
                                padding: '0.45rem 0.75rem'
                              }}
                              onClick={() => openSavedQuoteDeleteModal(quote)}
                              disabled={actionBusy}
                            >
                              {quoteDeleteId === quote.quoteId ? 'Deleting...' : 'Delete'}
                            </button>
                          )}
                          {canReactivate && (
                            <button
                              style={{
                                ...styles.button,
                                ...styles.primaryButton,
                                minWidth: '110px',
                                padding: '0.45rem 0.75rem'
                              }}
                              onClick={() => setSavedQuoteActive(quote, true)}
                              disabled={actionBusy}
                            >
                              {quoteStateChangeId === quote.quoteId ? 'Updating...' : 'Activate'}
                            </button>
                          )}
                          <button
                            style={{
                              ...styles.button,
                              ...styles.secondaryButton,
                              minWidth: '110px',
                              padding: '0.45rem 0.75rem'
                            }}
                            onClick={() => openSavedQuote(quote)}
                            disabled={
                              quoteLoading ||
                              quoteSendingId === quote.quoteId ||
                              quoteStateChangeId === quote.quoteId ||
                              quoteDeleteId === quote.quoteId
                            }
                          >
                            Open
                          </button>
                        </div>
                      </div>
                    )})}
                  </div>
                </div>
              )}

              <div style={styles.summaryGuidance}>
                Suggested tools support diagnosis. Suggested parts are repair candidates only after testing has
                confirmed a fault.
              </div>

              {slaCoverageSummaryText ? (
                <div style={styles.slaCoverageBanner}>
                  <div style={styles.slaCoverageBannerTitle}>SLA Coverage Check</div>
                  <div>{slaCoverageSummaryText}</div>
                </div>
              ) : null}

              {kitRecommendationSummaryText ? (
                <div style={styles.slaCoverageBanner}>
                  <div style={styles.slaCoverageBannerTitle}>Kit Match Check</div>
                  <div>{kitRecommendationSummaryText}</div>
                  <div style={styles.kitRecommendationGrid}>
                    {visibleKitRecommendations.map(recommendation => {
                      const badgeStyle =
                        recommendation.recommendationType === 'QUOTE_KIT'
                          ? styles.kitRecommendationBadgeQuoteKit
                          : styles.kitRecommendationBadgeMaintenance;
                      const implementedSelection =
                        implementedKitSelectionMap.get(String(recommendation.kitId || '').trim()) || null;
                      const overlappingSelection = implementedSelection
                        ? null
                        : getOverlappingImplementedKitSelection(recommendation);
                      const primaryLabel =
                        recommendation.kitNumber ||
                        recommendation.sku ||
                        recommendation.name ||
                        'Kit recommendation';
                      const secondaryLabel =
                        recommendation.name && recommendation.name !== primaryLabel
                          ? recommendation.name
                          : recommendation.purpose
                          ? `${String(recommendation.purpose || '')
                              .toLowerCase()
                              .replace(/_/g, ' ')} kit`
                          : null;
                      const matchedPrice = formatCurrencyValue(
                        recommendation.matchedPartsPriceTotal,
                        recommendation.currencyCode
                      );
                      const kitPrice = formatCurrencyValue(
                        recommendation.kitPrice,
                        recommendation.currencyCode
                      );
                      const overlapLabel = overlappingSelection
                        ? resolveKitDisplayLabel(
                            visibleKitRecommendationMap.get(String(overlappingSelection.kitId || '').trim()) ||
                              overlappingSelection
                          )
                        : null;
                      return (
                        <div key={recommendation.kitId} style={styles.kitRecommendationCard}>
                          <div style={styles.kitRecommendationCardHeader}>
                            <div>
                              <div style={styles.kitRecommendationName}>{primaryLabel}</div>
                              {secondaryLabel ? (
                                <div style={styles.kitRecommendationSubtitle}>{secondaryLabel}</div>
                              ) : null}
                            </div>
                            <span style={{ ...styles.kitRecommendationBadge, ...badgeStyle }}>
                              Recommended kit
                            </span>
                          </div>
                          <div style={styles.kitRecommendationPanelGrid}>
                            <div style={styles.kitRecommendationPanel}>
                              <div style={styles.kitRecommendationPanelTitle}>Kit Stats</div>
                              <div style={styles.kitRecommendationPanelValue}>
                                {recommendation.kitCoveragePercent}% implicated
                              </div>
                              <div style={styles.kitRecommendationPanelBody}>
                                {recommendation.matchedPartCount} of {recommendation.totalKitComponents} kit parts
                                {' '}appear in the TSFAD parts result.
                              </div>
                            </div>
                            <div style={styles.kitRecommendationPanel}>
                              <div style={styles.kitRecommendationPanelHeader}>
                                <div style={styles.kitRecommendationPanelTitle}>Recommendation</div>
                                <button
                                  style={{
                                    ...styles.button,
                                    ...styles.kitRecommendationActionButton,
                                    ...(implementedSelection ? styles.successButton : styles.primaryButton),
                                    ...(implementedSelection || overlappingSelection ? styles.disabledButton : null)
                                  }}
                                  type="button"
                                  onClick={() => implementKitRecommendation(recommendation)}
                                  disabled={Boolean(implementedSelection || overlappingSelection)}
                                >
                                  {implementedSelection
                                    ? 'Implemented'
                                    : overlappingSelection
                                    ? 'Unavailable'
                                    : 'Implement Kit'}
                                </button>
                              </div>
                              <div style={styles.kitRecommendationPanelBody}>
                                Consider implementing this kit during Repair.
                              </div>
                              <div style={styles.kitRecommendationReason}>
                                {getKitRecommendationActionText(recommendation)}
                              </div>
                              {getKitRecommendationSlaText(recommendation) ? (
                                <div style={styles.kitRecommendationMeta}>
                                  {getKitRecommendationSlaText(recommendation)}
                                </div>
                              ) : null}
                              {kitPrice && matchedPrice ? (
                                <div style={styles.kitRecommendationPrice}>
                                  {recommendation.pricingMode === 'SLA'
                                    ? `Kit customer price ${kitPrice}. Matched parts customer price ${matchedPrice}.`
                                    : `Kit price ${kitPrice}. Matched parts ${matchedPrice}.`}
                                </div>
                              ) : null}
                              {overlapLabel && !implementedSelection ? (
                                <div style={styles.kitRecommendationMeta}>
                                  Some matched parts are already allocated to implemented kit {overlapLabel}.
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div style={styles.toolsAndParts}>
                <div style={styles.listBox}>
                  <h4 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Suggested Tools:</h4>
                  {getRequiredTools().length > 0 ? (
                    <ul style={styles.resourceList}>
                      {getRequiredTools().map(({ item, probability }, index) => (
                        renderToolResourceRow(item, probability, `tool-${index}`)
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: 0, color: '#6b7280' }}>No tools suggested</p>
                  )}
                </div>
                
                <div style={styles.listBox}>
                  <h4 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Suggested Parts:</h4>
                  {getRequiredParts().length > 0 ? (
                    <ul style={styles.resourceList}>
                      {getRequiredParts().map(({ item, probability }, index) => (
                        renderPartResourceRow(item, probability, `part-${index}`)
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: 0, color: '#6b7280' }}>No parts suggested yet</p>
                  )}
                </div>
              </div>

              {testSteps.length > 0 && (
                <div style={styles.testHistory}>
                  <h4>Test History:</h4>
              {testSteps.map(step => (
                <div key={step.id} style={styles.testStep}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{step.description}</span>
                        <span
                          style={{
                            color: getTestStepColor(step),
                            fontWeight: '500'
                          }}
                        >
                          {getTestStepLabel(step)}
                        </span>
                  </div>
                  {step.notes && (
                    <div style={{ marginTop: '0.25rem', color: BRAND_MUTED }}>
                      Notes: {step.notes}
                    </div>
                  )}
                    {step.timestamp && (
                      <div style={{ marginTop: '0.25rem', color: BRAND_MUTED, fontSize: '0.75rem' }}>
                        {step.timestamp instanceof Date ? step.timestamp.toLocaleString() : new Date(step.timestamp).toLocaleString()}
                      </div>
                    )}
                </div>
              ))}
            </div>
          )}
            </div>
          </>
        )}
      </div>

      <TSFADKnowledgeCoverageModal
        open={showKnowledgeCoverageModal && error === 'Knowledge Coverage Issue'}
        styles={styles}
        title="Knowledge Coverage Alert"
        details={errorDetails || 'TSFAD identified a knowledge coverage issue for this case.'}
        selectedPaths={
          Array.isArray(analysisFailureFeedbackContext?.selectedPaths)
            ? (analysisFailureFeedbackContext?.selectedPaths as unknown[])
                .map(value => formatKnowledgePathLabel(String(value || '')))
                .filter(Boolean)
            : []
        }
        totalRecords={
          typeof analysisFailureFeedbackContext?.totalRecords === 'number'
            ? analysisFailureFeedbackContext.totalRecords
            : null
        }
        filteredNoPath={
          typeof analysisFailureFeedbackContext?.filteredNoPath === 'number'
            ? analysisFailureFeedbackContext.filteredNoPath
            : null
        }
        retrySubmitting={knowledgeCoverageRetrySubmitting}
        actionError={knowledgeCoverageActionError}
        onSendFeedback={handleKnowledgeCoverageFeedback}
        onEditProblem={handleKnowledgeCoverageEditAndRetry}
        onBackToCases={() => {
          setShowKnowledgeCoverageModal(false);
          setKnowledgeCoverageActionError(null);
          navigate('/tech');
        }}
      />

      {showAnalyzeRetryModal && error && error !== 'Knowledge Coverage Issue' && (
        <div style={styles.modal} role="dialog" aria-modal="true" aria-label="TSFAD analysis failed">
          <div style={{ ...styles.modalContent, maxWidth: '620px' }}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>TSFAD Run Failed</h3>
              <p style={{ margin: 0, color: BRAND_MUTED }}>
                Troubleshooting From a Distance (TSFAD) could not complete this run.
                Please try running the analysis again.
              </p>
            </div>
            <div style={{ ...styles.inlineNotice, marginBottom: '0.75rem' }}>
              If this continues to fail, share the failure code and timestamp with your admin so logs can be reviewed.
            </div>
            <div style={{ marginBottom: '0.75rem', color: BRAND_TEXT }}>
              {analyzeRetryDetails || errorDetails || 'An unexpected error occurred during analysis.'}
            </div>
            {Array.isArray(citationIntegrityReview?.issues) && citationIntegrityReview.issues.length > 0 && (
              <div style={{ ...styles.inlineNotice, marginBottom: '0.75rem' }}>
                <strong>Evidence review created</strong>
                <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                  {citationIntegrityReview.issues.map((issue: any, index: number) => (
                    <li key={`${issue?.stage || 'citation'}:${issue?.cause?.id || index}`}>
                      {issue?.cause?.shortTitle || issue?.cause?.description || 'Uncited possible cause'} —{' '}
                      {issue?.feedback?.recorded
                        ? 'sent to the feedback review queue'
                        : `feedback could not be recorded${issue?.feedback?.error ? `: ${issue.feedback.error}` : ''}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div
              style={{
                border: `1px solid ${BRAND_BORDER}`,
                borderRadius: '0.5rem',
                backgroundColor: BRAND_SURFACE_SOFT,
                padding: '0.75rem 0.9rem',
                fontSize: '0.82rem',
                color: BRAND_TEXT
              }}
            >
              <div>
                <strong>Failure code:</strong> {analyzeRetryFailureCode || 'N/A'}
              </div>
              <div style={{ marginTop: '0.35rem' }}>
                <strong>Timestamp:</strong>{' '}
                {analyzeRetryOccurredAt ? formatDateTime(analyzeRetryOccurredAt) : 'N/A'}
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => {
                  setShowAnalyzeRetryModal(false);
                  startAnalysis();
                }}
                disabled={analysisStarted}
              >
                {analysisStarted ? 'Running...' : 'Run TSFAD Again'}
              </button>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setShowAnalyzeRetryModal(false)}
              >
                Close
              </button>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => {
                  setShowAnalyzeRetryModal(false);
                  navigate('/tech');
                }}
              >
                Back to Cases
              </button>
            </div>
          </div>
        </div>
      )}

      {showCitationIntegrityModal && Array.isArray(citationIntegrityReview?.issues) && (
        <div
          style={styles.modal}
          role="dialog"
          aria-modal="true"
          aria-label="TSFAD evidence review required"
        >
          <div style={{ ...styles.modalContent, maxWidth: '640px' }}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>TSFAD Evidence Review Required</h3>
              <p style={{ margin: 0, color: BRAND_MUTED }}>
                TSFAD found evidence-coverage or action-plan integrity issues while preparing
                this troubleshooting workflow. The affected items are shown below.
              </p>
            </div>
            <div style={{ ...styles.inlineNotice, marginBottom: '0.9rem' }}>
              A cause was removed only when the cause itself had no cited nodes. For a cited cause,
              unsupported verification steps or invalid/non-corrective repair steps were omitted;
              the cause itself was retained for technician review.
            </div>
            <div style={{ display: 'grid', gap: '0.65rem' }}>
              {citationIntegrityReview.issues.map((issue: any, index: number) => (
                <div
                  key={`${issue?.stage || 'citation'}:${issue?.cause?.id || index}`}
                  style={{
                    border: `1px solid ${BRAND_BORDER}`,
                    borderRadius: '0.5rem',
                    backgroundColor: BRAND_SURFACE_SOFT,
                    padding: '0.75rem 0.9rem'
                  }}
                >
                  <div style={{ fontWeight: 700, color: BRAND_TEXT }}>
                    {issue?.cause?.shortTitle || issue?.cause?.description || 'Uncited possible cause'}
                  </div>
                  <div style={{ marginTop: '0.3rem', color: BRAND_MUTED, fontSize: '0.86rem' }}>
                    {issue?.cause?.reason === 'WORKFLOW_OPTIMIZATION_NOT_APPLIED'
                      ? 'The complete cited action plans were retained, but TSFAD could not safely apply natural workflow reordering. No cause or repair advice was replaced.'
                      : issue?.stage === 'PLAN_ENRICHMENT'
                      ? issue?.cause?.reason === 'MISSING_CORRECTIVE_KNOWLEDGE'
                        ? 'The cited cause was retained, but its focused evidence did not support a complete corrective sequence for the confirmed cause. Invalid repair advice was omitted and the knowledge gap was recorded.'
                        : issue?.cause?.reason === 'NO_CITED_ACTION_PLAN_STEPS_RETURNED'
                        ? 'No cited verification or repair steps were returned; the cited cause was retained for technician review.'
                        : `${Array.isArray(issue?.details?.removedSteps) ? issue.details.removedSteps.length : 'One or more'} uncited enrichment step(s) were omitted; the cited cause was retained.`
                      : 'The proposed cause did not cite a valid knowledge bundle or node.'}
                  </div>
                  <div style={{ marginTop: '0.3rem', color: BRAND_MUTED, fontSize: '0.82rem' }}>
                    {issue?.feedback?.recorded
                      ? 'Sent to the feedback review queue.'
                      : `Feedback was not recorded${issue?.feedback?.error ? `: ${issue.feedback.error}` : '.'}`}
                  </div>
                </div>
              ))}
            </div>
            <div style={styles.modalFooter}>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => setShowCitationIntegrityModal(false)}
              >
                Continue With Reviewed Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignmentBlockedModal && (
        <div style={styles.modal} role="dialog" aria-modal="true" aria-label="TSFAD run blocked">
          <div style={{ ...styles.modalContent, maxWidth: '560px' }}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{assignmentBlockedTitle || 'TSFAD Run Blocked'}</h3>
              {assignmentBlockedLead ? (
                <p style={{ margin: 0, color: BRAND_MUTED }}>{assignmentBlockedLead}</p>
              ) : null}
            </div>
            {assignmentBlockedDetails && (
              <p style={{ margin: '0 0 1rem', color: BRAND_TEXT }}>{assignmentBlockedDetails}</p>
            )}
            <div style={styles.modalFooter}>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setShowAssignmentBlockedModal(false)}
              >
                Close
              </button>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => {
                  setShowAssignmentBlockedModal(false);
                  navigate('/tech');
                }}
              >
                Back to Cases
              </button>
            </div>
          </div>
        </div>
      )}

      <TSFADQuoteModal
        open={quoteModalOpen}
        styles={styles}
        quoteHasActiveSla={Boolean(quoteSlaCoverage?.active)}
        quoteTemplateName={quoteTemplateName}
        quoteEditingQuoteId={quoteEditingQuoteId}
        quoteRevisionOfQuoteId={quoteRevisionOfQuoteId}
        quoteHeaderBlocks={quoteHeaderBlocks}
        quoteTitle={quoteTitle}
        setQuoteTitle={setQuoteTitle}
        quoteDescription={quoteDescription}
        setQuoteDescription={setQuoteDescription}
        quoteLineItems={quoteLineItems}
        setQuoteLineItems={setQuoteLineItems}
        selectedQuoteLineIds={selectedQuoteLineIds}
        setSelectedQuoteLineIds={setSelectedQuoteLineIds}
        applyPartNumberToQuoteLine={applyPartNumberToQuoteLine}
        quotePartCatalog={quotePartCatalog}
        quoteTotals={quoteTotals}
        quoteUnmatchedParts={quoteUnmatchedParts}
        quoteNotes={quoteNotes}
        setQuoteNotes={setQuoteNotes}
        quoteTemplateLayout={quoteTemplateLayout}
        quoteCustomerName={quoteCustomerName}
        setQuoteCustomerName={setQuoteCustomerName}
        quoteCustomerSignature={quoteCustomerSignature}
        setQuoteCustomerSignature={setQuoteCustomerSignature}
        quoteSaving={quoteSaving}
        quoteSaveButtonLabel={quoteSaveButtonLabel}
        onSave={saveQuoteFromTSFAD}
        onClose={() => {
          setQuoteModalOpen(false);
          setQuoteEditingQuoteId(null);
          setQuoteRevisionOfQuoteId(null);
        }}
      />

      <TSFADQuoteInactivateModal
        open={Boolean(quoteInactivateTarget)}
        styles={styles}
        quoteInactivateTarget={quoteInactivateTarget}
        quoteInactivationReason={quoteInactivationReason}
        quoteInactivationError={quoteInactivationError}
        quoteStateChangeId={quoteStateChangeId}
        onReasonChange={(value) => {
          setQuoteInactivationReason(value);
          if (quoteInactivationError) setQuoteInactivationError(null);
        }}
        onClose={closeSavedQuoteInactivateModal}
        onConfirm={confirmSavedQuoteInactivate}
      />

      <TSFADQuoteDeleteModal
        open={Boolean(quoteDeleteTarget)}
        styles={styles}
        quoteDeleteTarget={quoteDeleteTarget}
        quoteDeleteError={quoteDeleteError}
        quoteDeleteId={quoteDeleteId}
        onClose={closeSavedQuoteDeleteModal}
        onConfirm={confirmSavedQuoteDelete}
      />

      {/* Test Modal */}
      <TSFADTestModal
        open={showTestModal}
        styles={styles}
        selectedCause={selectedCause}
        onClose={() => setShowTestModal(false)}
        isEditingProcedure={isEditingProcedure}
        isInactivating={isInactivating}
        inactivationPanelRef={inactivationPanelRef}
        inactivationTextareaRef={inactivationTextareaRef}
        formatCauseTitle={formatCauseTitle}
        inactivationReason={inactivationReason}
        setInactivationReason={setInactivationReason}
        confirmInactivateCause={confirmInactivateCause}
        cancelInactivation={cancelInactivation}
        procedureDraft={procedureDraft}
        setProcedureDraft={setProcedureDraft}
        repairDraft={repairDraft}
        setRepairDraft={setRepairDraft}
        toolsDraft={toolsDraft}
        setToolsDraft={setToolsDraft}
        partsDraft={partsDraft}
        setPartsDraft={setPartsDraft}
        notesDraft={notesDraft}
        setNotesDraft={setNotesDraft}
        procedureEditNotes={procedureEditNotes}
        setProcedureEditNotes={setProcedureEditNotes}
        saveProcedureEdits={saveProcedureEdits}
        cancelProcedureEdit={cancelProcedureEdit}
        selectedVerificationSteps={selectedVerificationSteps}
        selectedRepairSteps={selectedRepairSteps}
        planAssistantOpen={planAssistantOpen}
        setPlanAssistantOpen={setPlanAssistantOpen}
        selectedPlanStepKey={selectedPlanStepKey}
        setSelectedPlanStepKey={setSelectedPlanStepKey}
        selectedPlanSteps={selectedPlanSteps}
        askPlanAssistant={askPlanAssistant}
        planAssistantLoading={planAssistantLoading}
        selectedPlanStep={selectedPlanStep}
        planAssistantActiveAsk={planAssistantActiveAsk}
        planAssistantError={planAssistantError}
        planAssistantMessages={planAssistantMessages}
        formatPlanAssistantText={formatPlanAssistantText}
        planAssistantQuestion={planAssistantQuestion}
        setPlanAssistantQuestion={setPlanAssistantQuestion}
        setPlanAssistantMessages={setPlanAssistantMessages}
        setPlanAssistantError={setPlanAssistantError}
        setPlanAssistantActiveAsk={setPlanAssistantActiveAsk}
        setPlanAssistantConversationId={setPlanAssistantConversationId}
        parseResourceItem={parseResourceItem}
        testNotes={testNotes}
        setTestNotes={setTestNotes}
        regeneratingCause={regeneratingCause}
        restoringCause={restoringCause}
        beginProcedureEdit={beginProcedureEdit}
        handleReportIssueFromProcedure={handleReportIssueFromProcedure}
        regenerateProcedure={regenerateProcedure}
        restoreOriginalProcedure={restoreOriginalProcedure}
        handleReactivateCurrentCause={handleReactivateCurrentCause}
        beginInactivation={beginInactivation}
        onFlagReferenceImage={handleFlagReferenceImageFromProcedure}
      />

      {/* Suggestion Modal */}
      <TSFADSuggestionModal
        open={showSuggestionModal}
        styles={styles}
        suggestion={suggestion}
        setSuggestion={setSuggestion}
        suggestionLoading={suggestionLoading}
        setSuggestionLoading={setSuggestionLoading}
        suggestionResult={suggestionResult}
        setSuggestionResult={setSuggestionResult}
        setShowSuggestionModal={setShowSuggestionModal}
        handleSuggestCause={handleSuggestCause}
        handleAddUnverifiedCause={handleAddUnverifiedCause}
        openFeedbackModal={openFeedbackModal}
      />

      <TSFADMissedCausesModal
        open={showMissedCausesModal}
        styles={styles}
        loading={missedCausesLoading}
        error={missedCausesError}
        workflowError={missedCausesWorkflowError}
        optimizationComplete={missedCausesOptimizationComplete}
        result={missedCausesResult}
        selectedCandidateIds={selectedMissedCauseIds}
        processing={missedCausesProcessing}
        optimizing={missedCausesOptimizing}
        processingCandidateId={processingMissedCauseId}
        batchResults={missedCauseBatchResults}
        onClose={() => setShowMissedCausesModal(false)}
        onRetry={handleCheckMissedCauses}
        onToggleCandidate={toggleMissedCauseSelection}
        onSelectAll={selectAllMissedCauses}
        onClearSelection={() => setSelectedMissedCauseIds([])}
        onAddSelected={handleAddSelectedMissedCauses}
      />

      <TSFADExplanationModal
        open={showExplanationModal}
        styles={styles}
        selectedCauseForExplanation={selectedCauseForExplanation}
        explanationVerificationSteps={explanationVerificationSteps}
        explanationLoading={explanationLoading}
        explanationResult={explanationResult}
        StepList={StepList}
        onClose={() => {
          setShowExplanationModal(false);
          setSelectedCauseForExplanation(null);
          setExplanationResult(null);
        }}
      />

      <TSFADFeedbackModal
        open={showFeedbackModal}
        styles={styles}
        feedbackForm={feedbackForm}
        setFeedbackForm={setFeedbackForm}
        feedbackCause={feedbackCause}
        feedbackSuggestionDescription={feedbackSuggestionDescription}
        onClose={closeFeedbackModal}
        onSubmit={submitDocumentationFeedback}
      />
    </div>
  );
}




































